import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Form, Spinner } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { CustomModal, CustomButton, LoadingSpinner } from '../components';
import { authAPI, avatarsAPI, plansAPI, adminAPI, projectsAPI, paymentsAPI, usersAPI } from '../services/api';
import './AvatarChatPage.css';
import '../components/Navbar.css';

// Role keyword → avatar relation mapping for group chat avatar detection.
// Grandparent terms are split by gender so "grandpa" only matches a male-gendered avatar
// and "grandma" only matches a female-gendered one.
const ROLE_KEYWORDS = [
  { keywords: ['grandpa', 'grandfather', 'grampa', 'granddad', 'gramps'], relations: ['grandfather', 'grandpa'] },
  { keywords: ['grandma', 'grandmother', 'granny', 'grandmum', 'nana'],   relations: ['grandmother', 'grandma'] },
  { keywords: ['dad', 'father', 'papa'],                                   relations: ['father'] },
  { keywords: ['mom', 'mother', 'mama', 'mum'],                            relations: ['mother'] },
  { keywords: ['uncle'],                                                    relations: ['uncle'] },
  { keywords: ['aunt'],                                                     relations: ['aunt'] },
  { keywords: ['brother'],                                                  relations: ['brother'] },
  { keywords: ['sister'],                                                   relations: ['sister'] },
];

// Given a message string and the list of avatars available in the current group,
// return the avatar the message is most likely addressing, or null if unclear.
// Checks first-name/last-name mentions (case-insensitive), then role keywords
// matched against each avatar's `relation` field.
const detectAddressedAvatar = (messageText, groupAvatars) => {
  if (!messageText || !Array.isArray(groupAvatars) || groupAvatars.length === 0) return null;
  const lower = messageText.toLowerCase();

  // 1. Name match — check if the message contains the avatar's first or last name
  for (const avatar of groupAvatars) {
    const first = (avatar.first_name || '').toLowerCase().trim();
    const last  = (avatar.last_name  || '').toLowerCase().trim();
    if (first && lower.includes(first)) return avatar;
    if (last  && lower.includes(last))  return avatar;
  }

  // 2. Role keyword match — check message words against ROLE_KEYWORDS,
  //    then find an avatar whose `relation` field matches one of the rule's relations.
  const words = lower.split(/\W+/).filter(Boolean);
  for (const rule of ROLE_KEYWORDS) {
    if (rule.keywords.some((kw) => words.includes(kw))) {
      const matched = groupAvatars.find((avatar) => {
        const rel = (avatar.relation || '').toLowerCase().trim();
        return rule.relations.some((r) => rel.includes(r));
      });
      if (matched) return matched;
    }
  }

  return null;
};

// Normalize avatar type name for matching
const normalizeAvatarTypeName = (name) => {
  if (!name || typeof name !== 'string') return '';
  const n = name.toLowerCase().trim();
  if (n.includes('loved') && (n.includes('one') || n.includes('once'))) return 'for loved once';
  if (n.includes('myself') || n === 'for me') return 'for myself';
  return n;
};

const AvatarChatPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [messages, setMessages] = useState([]);
  const [chatSessionKey, setChatSessionKey] = useState(0); // Force re-render key for new chats
  const [showImproveModal, setShowImproveModal] = useState(false);
  const [improveContext, setImproveContext] = useState(null);
  const [improveContextLoading, setImproveContextLoading] = useState(false);
  const [improveConversation, setImproveConversation] = useState([]);
  const [improveAnswer, setImproveAnswer] = useState('');
  const [improveAnswerSending, setImproveAnswerSending] = useState(false);
  const [improveDone, setImproveDone] = useState(false);
  const [improveCoveredDomains, setImproveCoveredDomains] = useState([]);
  const [improveDocsUploading, setImproveDocsUploading] = useState(false);
  const [improveTypingTimedOut, setImproveTypingTimedOut] = useState(false);
  const improveConversationEndRef = useRef(null);
  const improveTypingTimerRef = useRef(null);
  const improveFileInputRef = useRef(null);

  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [showAddMemberModal, setShowAddMemberModal] = useState(false);
  const [inviteFirstName, setInviteFirstName] = useState('');
  const [inviteLastName, setInviteLastName] = useState('');
  const [inviteEmail, setInviteEmail] = useState('');
  // Multi-person invite: action 'send_invite' or 'gift_<plan_pricing_id>' from /api/plans/avatar-plans
  const [invitees, setInvitees] = useState([
    { id: 1, firstName: '', lastName: '', email: '', action: 'send_invite' },
  ]);
  const [inviteWhoPay, setInviteWhoPay] = useState(null); // null = no selection, true = I'll pay, false = Let them pay
  const [inviteGiftPlan, setInviteGiftPlan] = useState(null); // 'monthly' | 'yearly' — only relevant when inviteWhoPay === true
  const [giftSentSuccess, setGiftSentSuccess] = useState(false);
  const [sendingInvite, setSendingInvite] = useState(false);
  /** GET /api/plans/avatar-plans — normalized into invite dropdown (Send invite + gift month/year). */
  const [addMemberAvatarPlanDetails, setAddMemberAvatarPlanDetails] = useState(null);
  const [addMemberPlanLoading, setAddMemberPlanLoading] = useState(false);

  const [showChatProfileMenu, setShowChatProfileMenu] = useState(false);
  const [chatAutoRenew, setChatAutoRenew] = useState(null);
  const [cancellingSubscription, setCancellingSubscription] = useState(false);
  const [showCancelConfirmModal, setShowCancelConfirmModal] = useState(false);
  const [showBillingModal, setShowBillingModal] = useState(false);
  const chatProfileMenuRef = useRef(null);
  const [showNewChatModal, setShowNewChatModal] = useState(false);
  const [familyMemberPlans, setFamilyMemberPlans] = useState([]);
  const [familyMemberPlansLoading, setFamilyMemberPlansLoading] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [avatars, setAvatars] = useState([]);
  const [selectedAvatar, setSelectedAvatar] = useState(null);
  const [avatarMembers, setAvatarMembers] = useState([]);
  const [loadingAvatarMembers, setLoadingAvatarMembers] = useState(false);
  const [inviteeMembers, setInviteeMembers] = useState([]);
  const [loadingInviteeMembers, setLoadingInviteeMembers] = useState(false);
  const [groupMembers, setGroupMembers] = useState([]);
  const [loadingData, setLoadingData] = useState(true);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [dashboardSummary, setDashboardSummary] = useState(null);
  const [plans, setPlans] = useState([]);
  const [loadingPlans, setLoadingPlans] = useState(false);
  const [avatarsChecked, setAvatarsChecked] = useState(false);
  const [projects, setProjects] = useState([]);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [loadingSelectedProject, setLoadingSelectedProject] = useState(false);
  const [projectMessages, setProjectMessages] = useState([]);
  const [showCreateProjectModal, setShowCreateProjectModal] = useState(false);
  const [newChatType, setNewChatType] = useState(null); // 'private'|'open'|'selected'|'family'
  const [newChatMemberStep, setNewChatMemberStep] = useState(false);
  const [newChatPickedMembers, setNewChatPickedMembers] = useState([]);
  const [pendingSessionMemberIds, setPendingSessionMemberIds] = useState([]);
  const [addonConfirmed, setAddonConfirmed] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showAddMembersModal, setShowAddMembersModal] = useState(false);
  const [showPaymentSuccessModal, setShowPaymentSuccessModal] = useState(false);
  const [paymentSuccessData, setPaymentSuccessData] = useState(null);
  const [showPlanExpiredModal, setShowPlanExpiredModal] = useState(false);
  const [expiredPlanId, setExpiredPlanId] = useState(null);
  const [addons, setAddons] = useState([]);
  const [loadingAddons, setLoadingAddons] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [tokenBalance, setTokenBalance] = useState(null);
  const [projectMenuOpenId, setProjectMenuOpenId] = useState(null);
  const [sessionMenuOpenId, setSessionMenuOpenId] = useState(null);
  const [avatarMenuOpenId, setAvatarMenuOpenId] = useState(null);
  const [exportingAvatarId, setExportingAvatarId] = useState(null);
  const [showDeleteProjectModal, setShowDeleteProjectModal] = useState(false);
  const [projectToDelete, setProjectToDelete] = useState(null);
  const [showLeaveProjectModal, setShowLeaveProjectModal] = useState(false);
  const [projectToLeave, setProjectToLeave] = useState(null);
  const [showRenameProjectModal, setShowRenameProjectModal] = useState(false);
  const [projectToRename, setProjectToRename] = useState(null);
  const [renameProjectName, setRenameProjectName] = useState('');
  const [renamingProject, setRenamingProject] = useState(false);
  const [deletingProjectId, setDeletingProjectId] = useState(null);
  const [leavingProjectId, setLeavingProjectId] = useState(null);
  const [projectName, setProjectName] = useState('');
  const [projectDescription, setProjectDescription] = useState('');
  const [creatingProject, setCreatingProject] = useState(false);
  const [inviteEmails, setInviteEmails] = useState('');
  const [suppressInviteSuggestions, setSuppressInviteSuggestions] = useState(false);
  const [sendingInvites, setSendingInvites] = useState(false);
  const [selectedInviteUserId, setSelectedInviteUserId] = useState(null);
  const [addMembersQuery, setAddMembersQuery] = useState('');
  const [selectedAddMembers, setSelectedAddMembers] = useState([]); // array of member objects (from inviteeMembers/avatarMembers)
  const [addingMembers, setAddingMembers] = useState(false);
  const [sidebarMembersExpanded, setSidebarMembersExpanded] = useState(true);
  const [membersSectionExpanded, setMembersSectionExpanded] = useState(true);
  const [myAvatarsList, setMyAvatarsList] = useState([]);
  const [loadingMyAvatars, setLoadingMyAvatars] = useState(false);
  const [avatarsSectionExpanded, setAvatarsSectionExpanded] = useState(true);
  const [sidebarAvatarsExpanded, setSidebarAvatarsExpanded] = useState(true);
  const [avatarPlanDetails, setAvatarPlanDetails] = useState(null);
  const [loadingAvatarPlan, setLoadingAvatarPlan] = useState(false);
  const [chatSessionId, setChatSessionId] = useState(null);
  const [isChatInputDisabled, setIsChatInputDisabled] = useState(false);
  const [showTokenLimitModal, setShowTokenLimitModal] = useState(false);
  const [showWeeklyLimitModal, setShowWeeklyLimitModal] = useState(false);
  const [weeklyResetDate, setWeeklyResetDate] = useState('');
  const [showTopUpConfirmModal, setShowTopUpConfirmModal] = useState(false);
  const [manualSessionId, setManualSessionId] = useState(''); // For manual session ID input like index.html
  const [wsConnection, setWsConnection] = useState(null);
  const [isWsConnected, setIsWsConnected] = useState(false);
  const [wsFailed, setWsFailed] = useState(false); // Track if WebSocket is permanently failing
  const [chatSessions, setChatSessions] = useState([]); // Store chat sessions history
  const [chatSearchQuery, setChatSearchQuery] = useState(''); // Search query for chats
  const [chatSessionsLoading, setChatSessionsLoading] = useState(false); // Loading state for sessions
  const [deletingSessionId, setDeletingSessionId] = useState(null); // Session ID being deleted
  const [showDeleteChatModal, setShowDeleteChatModal] = useState(false);
  const [sessionToDelete, setSessionToDelete] = useState(null); // { session_id, title } for confirmation
  const [editingSessionId, setEditingSessionId] = useState(null); // Session ID being edited
  const [editingSessionTitle, setEditingSessionTitle] = useState(''); // Temporary title while editing
  const [updatingSessionTitle, setUpdatingSessionTitle] = useState(false); // Loading state for update
  const [chatSessionsCursor, setChatSessionsCursor] = useState(null); // Cursor for pagination
  const [hasMoreSessions, setHasMoreSessions] = useState(false); // Whether there are more sessions to load
  const [messagesCursor, setMessagesCursor] = useState(null); // Cursor for message pagination
  const [hasMoreMessages, setHasMoreMessages] = useState(false); // Whether there are more messages to load
  const [showConfigPanel, setShowConfigPanel] = useState(false); // Config panel visibility
  const [configApiUrl, setConfigApiUrl] = useState(process.env.REACT_APP_API_URL || 'https://sayagainservices.sayagain.ai');
  const [configCorsProxy, setConfigCorsProxy] = useState('');
  const [configProjectId, setConfigProjectId] = useState('');
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const chatInputRef = useRef(null);
  const [isUploading, setIsUploading] = useState(false);
  const authCheckedRef = useRef(false);
  const plansFetchedRef = useRef(false);
  const projectsFetchedRef = useRef(false);
  const initialLoadRef = useRef(true); // Track if we're in initial load
  const previousAvatarIdRef = useRef(null); // Track previous avatar to detect actual switches
  const wsConnectionCountRef = useRef(0); // Track WebSocket connection attempts
  const currentWsRef = useRef(null); // Track current WebSocket instance for immediate checks
  const lastConnectionAttemptRef = useRef(0); // Track last connection attempt time
  const lastWebSocketMessageRef = useRef({ content: '', timestamp: 0 }); // Track last WebSocket message to prevent duplicates
  const creatingSessionRef = useRef(false); // Prevent duplicate POST /api/chat when creating first session
  const activeGroupAvatarIdRef = useRef(null); // Track last avatar that responded in group chat
  const ninetyPercentWarningShownRef = useRef(false); // Prevent multiple toasts for 90% limit
  const pendingGroupOptimisticIdRef = useRef(null); // Tracks tempId of optimistic msg in group chat (replaced on echo)

  // Auto-resize chat input textarea based on content
  useEffect(() => {
    const textarea = chatInputRef.current;
    if (textarea) {
      textarea.style.height = 'auto';  // release fixed height so scrollHeight reflects true content
      textarea.style.height = `${Math.max(52, textarea.scrollHeight)}px`;
    }
  }, [inputValue]);

  // Renewal flow states
  const [showRenewalSelectionModal, setShowRenewalSelectionModal] = useState(false);
  const [renewalBillingPeriod, setRenewalBillingPeriod] = useState('yearly');
  const [selectedRenewalPlan, setSelectedRenewalPlan] = useState(null);
  const [avatarPlans, setAvatarPlans] = useState([]); // For structured plans (Choose Your Plan modal)
  const [processingRenewal, setProcessingRenewal] = useState(false);
  const [downloadingBackup, setDownloadingBackup] = useState(false);

  const getProjectsCacheKey = (userId) => `projects_cache_v1:${userId || 'unknown'}`;

  const loadCachedProjects = (userId) => {
    try {
      const raw = localStorage.getItem(getProjectsCacheKey(userId));
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      console.warn('Failed to load cached projects:', e);
      return [];
    }
  };

  const JUST_US_KEY = 'sayagain_just_us_projects';
  const getJustUsProjects = () => { try { return new Set(JSON.parse(localStorage.getItem(JUST_US_KEY) || '[]')); } catch { return new Set(); } };
  const markProjectJustUs = (projectId) => { try { const s = getJustUsProjects(); s.add(String(projectId)); localStorage.setItem(JUST_US_KEY, JSON.stringify([...s])); } catch {} };
  const isJustUsProject = (projectId) => getJustUsProjects().has(String(projectId));

  const saveCachedProjects = (userId, nextProjects) => {
    try {
      if (!userId) return;
      const safe = Array.isArray(nextProjects) ? nextProjects : [];
      localStorage.setItem(getProjectsCacheKey(userId), JSON.stringify(safe));
    } catch (e) {
      console.warn('Failed to save cached projects:', e);
    }
  };

  // On chat page load: for any registered/logged-in user, call history API to load their chat list.
  // If they have a saved session in localStorage, also restore that session and load its messages.
  useEffect(() => {
    const token = localStorage.getItem('access_token');
    const savedSessionId = localStorage.getItem('chat_session_id');
    const isLoggedIn = !!token;

    if (savedSessionId) {
      console.log('📋 Restored session ID from storage:', savedSessionId);
      setChatSessionId(savedSessionId);
    }

    // Restore cached messages only when we have a saved session (avoid flash before API loads)
    if (savedSessionId) {
      const savedMessages = localStorage.getItem('chat_messages');
      if (savedMessages) {
        try {
          const parsedMessages = JSON.parse(savedMessages);
          if (Array.isArray(parsedMessages) && parsedMessages.length > 0) {
            const sanitiseContent = (content) => {
              if (content !== null && content !== undefined && typeof content === 'object') {
                return content.message || content.content || content.text || content.detail || JSON.stringify(content);
              }
              return content || '';
            };
            setMessages(parsedMessages.map(m => ({ ...m, content: sanitiseContent(m.content) })));
          }
        } catch (error) {
          console.error('❌ Failed to parse saved messages:', error);
          localStorage.removeItem('chat_messages');
        }
      }
    }

    // Restore: single chat (session_id) or project chat (project_id)
    const savedProjectId = localStorage.getItem('chat_session_project_id');
    if (savedProjectId) {
      (async () => {
        try {
          console.log('📋 Project chat restore: loading project history (MODE C)');
          saveSessionProjectId(savedProjectId);
          const lastSessionId = await loadProjectMessages(savedProjectId);
          if (lastSessionId && lastSessionId > 0) {
            console.log('🔌 Connecting WebSocket for project chat');
            setTimeout(() => connectWebSocket(lastSessionId), 500);
          }
          const detailed = await fetchProjectDetails(Number(savedProjectId));
          if (detailed) setSelectedProject(detailed);
        } catch (e) {
          console.warn('Project chat restore error:', e);
        }
      })();
    } else if (savedSessionId) {
      (async () => {
        try {
          console.log('📋 Single chat restore: loading session history (MODE B)');
          await fetchChatSessions();
          await loadSessionMessages(savedSessionId);
          console.log('🔌 Connecting WebSocket for single chat');
          setTimeout(() => connectWebSocket(savedSessionId), 500);
        } catch (e) {
          console.warn('Restore history/chat flow error:', e);
          if (savedSessionId) setTimeout(() => connectWebSocket(savedSessionId), 500);
        }
      })();
    }

    setTimeout(() => {
      initialLoadRef.current = false;
    }, 200);
  }, []); // Empty dependency array - runs only on mount

  // Check authentication and fetch user/avatar data on mount
  useEffect(() => {
    if (authCheckedRef.current) return;

    const token = localStorage.getItem('access_token');
    if (!token) {
      authCheckedRef.current = true;
      // No notification for guest redirect
      // toast.info('Please sign in to access Avatar Chat');
      navigate('/signin?redirect=/chat');
    } else {
      authCheckedRef.current = true;
      // Check beta expiry
      try {
        const storedUser = JSON.parse(sessionStorage.getItem('user_data') || '{}');
        if (storedUser.is_beta && storedUser.beta_expired) {
          navigate('/beta-expired', { replace: true });
          return;
        }
      } catch (_) { }
      // Don't set isAuthenticated to true yet - wait until we verify avatars exist
      // This prevents the chat page from rendering before we check for avatars
      fetchUserAndAvatarData();
      // Fetch plans on dashboard load
      if (!plansFetchedRef.current) {
        plansFetchedRef.current = true;
        fetchAllPlans();
        fetchTokenBalance();
      }
    }
  }, [navigate]);

  // Fetch projects and chat sessions when user data is available
  useEffect(() => {
    if (user && !projectsFetchedRef.current && isAuthenticated) {
      projectsFetchedRef.current = true;
      fetchProjects();
      fetchChatSessions(); // Always load single chat sidebar (Mode A); projects load via fetchProjects
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, isAuthenticated]);

  // Check for subscription expiry or cancellation
  useEffect(() => {
    // Wait until avatars have been checked to avoid premature redirects with null selectedAvatar
    if (user && user.subscription && avatarsChecked) {
      const { end_date, status } = user.subscription;
      if (end_date) {
        const endDate = new Date(end_date);
        const today = new Date();
        today.setHours(0, 0, 0, 0); // Normalize to start of day for comparison

        const isExpired = endDate < today;
        const isCancelled = status === 'cancelled' || status === 'canceled';

        if (isExpired || isCancelled) {
          console.log('Subscription not active (Expired:', isExpired, 'Cancelled:', isCancelled, '), redirecting to details');
          toast.error(isCancelled ? 'Your subscription has been cancelled. Please renew to access the chat.' : 'Your subscription has expired. Please renew to access the chat.', { toastId: 'subscription-check-error' });
          const avatarId = selectedAvatar?.id || (avatars && avatars.length > 0 ? avatars[0].id : null);
          if (avatarId) {
            navigate(`/avatar/${avatarId}`);
          } else {
            console.log('No avatar detected for redirect after subscription check failure');
            navigate('/chat');
          }
        }
      }
    }
  }, [user, navigate, selectedAvatar, avatars, avatarsChecked]);

  // Handle token limit reached automatically when usage reflects 100% or more
  // OR when weekly_tokens_remaining hits 0 (new time-based limit)
  useEffect(() => {
    if (tokenBalance && tokenBalance.usage_percentage !== undefined) {
      const usage = Number(tokenBalance.usage_percentage);
      const weeklyRemaining = Number(tokenBalance.weekly_tokens_remaining ?? -1);

      // Check weekly limit (new time-based limit) - if 0, show weekly limit modal
      // Only show for subscribed users; free users see standard upgrade prompts
      if (weeklyRemaining === 0 && !isChatInputDisabled && user?.subscription) {
        console.log('⚠️ Weekly token limit reached (0), disabling chat.');
        handleWeeklyLimitReached();
      } else if (usage >= 100 && !isChatInputDisabled) {
        console.log('⚠️ Token usage reached 100%, disabling chat.');
        handleTokenLimitReached();
      } else if (usage >= 90 && usage < 100 && !ninetyPercentWarningShownRef.current) {
        const avatarName = selectedAvatar?.first_name || 'your loved one';
        const lastReset = tokenBalance?.last_reset_at;
        let resetStr = '';
        if (lastReset) {
          const reset = new Date(lastReset);
          reset.setDate(reset.getDate() + 7);
          const day = reset.toLocaleDateString('en-US', { weekday: 'long' });
          const time = reset.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
          resetStr = `${day} at ${time}`;
        } else {
          const nextWeek = new Date();
          nextWeek.setDate(nextWeek.getDate() + 7);
          const day = nextWeek.toLocaleDateString('en-US', { weekday: 'long' });
          const time = nextWeek.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
          resetStr = `${day} at ${time}`;
        }
        // toast.info(`You have reached 90% of your weekly limit, which resets ${resetStr}.`, { autoClose: false });
        ninetyPercentWarningShownRef.current = true;
      }
    }
  }, [tokenBalance, isChatInputDisabled]);

  // When Invite Family modal opens, fetch avatar plan (dropdown: Send invite + gifts from API)
  useEffect(() => {
    if (showAddMemberModal) {
      fetchAddMemberAvatarPlan();
    }
  }, [showAddMemberModal]);

  // Re-open invite modal if the user was redirected back here after a gift payment
  useEffect(() => {
    try {
      const raw = localStorage.getItem('paypal_checkout_context') || sessionStorage.getItem('paypal_checkout_context');
      if (!raw) return;
      const ctx = JSON.parse(raw);
      if (ctx?.reopen_invite_modal) {
        // Clear the flag so it only fires once
        delete ctx.reopen_invite_modal;
        const updated = JSON.stringify(ctx);
        try { localStorage.setItem('paypal_checkout_context', updated); } catch (_) { }
        try { sessionStorage.setItem('paypal_checkout_context', updated); } catch (_) { }
        setGiftSentSuccess(true);
        setShowAddMemberModal(true);
      }
    } catch (_) { }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-close invite modal after showing gift-sent success message
  useEffect(() => {
    if (!giftSentSuccess) return;
    const timer = setTimeout(() => {
      setShowAddMemberModal(false);
      setGiftSentSuccess(false);
      resetInviteForm();
    }, 6000);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [giftSentSuccess]);

  // When Improve modal opens, load context and first question
  useEffect(() => {
    if (!showImproveModal || !selectedAvatar?.id) return;
    const avatarId = selectedAvatar.id || selectedAvatar.avatar_id;
    if (!avatarId) return;

    const load = async () => {
      const fallback = `I'm still finding my way back to myself. Can you tell me something about what I was like — something you always remembered about me?`;

      setImproveContext(null);
      setImproveDone(false);
      setImproveAnswer('');
      setImproveCoveredDomains([]);
      setImproveConversation([]);
      setImproveContextLoading(true);

      try {
        const res = await avatarsAPI.getImproveContext(avatarId);
        const ctx = res?.data ?? res;
        setImproveContext(ctx);
        const question = ctx?.question || fallback;
        setImproveConversation([{ role: 'avatar', text: question, domain: ctx?.domain || null }]);
        if (ctx?.domain) setImproveCoveredDomains([ctx.domain]);
      } catch (err) {
        console.error('Failed to load improve context:', err);
        setImproveConversation([{ role: 'avatar', text: fallback, domain: null }]);
      } finally {
        setImproveContextLoading(false);
      }
    };

    load();
  }, [showImproveModal, selectedAvatar?.id]);
  
  const refreshImproveContext = async (avatarId) => {
    if (!avatarId) return;
    try {
      const res = await avatarsAPI.getImproveContext(avatarId);
      const ctx = res?.data ?? res;
      if (ctx) {
        setImproveContext(ctx);
      }
    } catch (err) {
      console.warn('Failed to refresh improve context:', err);
    }
  };

  useEffect(() => {
    if (improveConversationEndRef.current) {
      improveConversationEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [improveConversation, improveAnswerSending, improveDone]);

  useEffect(() => {
    if (improveAnswerSending) {
      setImproveTypingTimedOut(false);
      improveTypingTimerRef.current = setTimeout(() => setImproveTypingTimedOut(true), 12000);
    } else {
      setImproveTypingTimedOut(false);
      if (improveTypingTimerRef.current) {
        clearTimeout(improveTypingTimerRef.current);
        improveTypingTimerRef.current = null;
      }
    }
  }, [improveAnswerSending]);

  const handleImproveAnswer = async (e) => {
    e?.preventDefault();
    const ans = (improveAnswer || '').trim();
    if (!ans || improveAnswerSending) return;
    const avatarId = selectedAvatar?.id || selectedAvatar?.avatar_id;
    if (!avatarId) return;

    // Find last avatar question for context
    const lastAvatarMsg = [...improveConversation].reverse().find(m => m.role === 'avatar');
    const currentQuestion = lastAvatarMsg?.text || '';

    // Build full session history (Q&A pairs) to pass to backend
    const sessionHistory = [];
    let pendingQuestion = null;
    for (const msg of improveConversation) {
      if (msg.role === 'avatar' && msg.text !== lastAvatarMsg?.text) {
        pendingQuestion = msg.text;
      } else if (msg.role === 'user' && pendingQuestion) {
        sessionHistory.push({ question: pendingQuestion, answer: msg.text });
        pendingQuestion = null;
      }
    }
    // Include current Q + this answer
    if (currentQuestion) sessionHistory.push({ question: currentQuestion, answer: ans });

    setImproveConversation(prev => [...prev, { role: 'user', text: ans }]);
    setImproveAnswer('');
    setImproveAnswerSending(true);

    try {
      const res = await avatarsAPI.submitImproveAnswer(avatarId, ans, currentQuestion, sessionHistory, improveCoveredDomains);
      const data = res?.data ?? res;

      if (data?.acknowledgment) {
        setImproveConversation(prev => [...prev, { role: 'avatar', text: data.acknowledgment }]);
      }
      if (data?.confidence_score !== undefined) {
        setImproveContext(prev => prev ? { ...prev, confidence_score: data.confidence_score } : prev);
        refreshImproveContext(avatarId);
      }
      if (data?.domain) {
        setImproveCoveredDomains(prev => prev.includes(data.domain) ? prev : [...prev, data.domain]);
      }
      if (data?.question) {
        setTimeout(() => {
          setImproveConversation(prev => [...prev, { role: 'avatar', text: data.question, domain: data.domain || null }]);
        }, 600);
      } else if (data?.done) {
        setTimeout(() => setImproveDone(true), 600);
      }
    } catch (err) {
      console.error('Failed to submit answer:', err);
      setImproveConversation(prev => prev.slice(0, -1)); // remove optimistic user msg
    } finally {
      setImproveAnswerSending(false);
    }
  };

  const handleImproveDocUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const avatarId = selectedAvatar?.id || selectedAvatar?.avatar_id;
    if (!avatarId) return;

    setImproveDocsUploading(true);
    try {
      await avatarsAPI.addAvatarDocuments(avatarId, files);
      toast.success(files.length === 1 ? '1 document uploaded successfully.' : `${files.length} documents uploaded successfully.`);
      try {
        const ctxRes = await avatarsAPI.getImproveContext(avatarId);
        const ctx = ctxRes?.data ?? ctxRes;
        if (ctx) setImproveContext(ctx);
      } catch (_) {}
    } catch (err) {
      toast.error(err?.message || 'Failed to upload documents. Please try again.');
    } finally {
      setImproveDocsUploading(false);
      e.target.value = '';
    }
  };

  // Handle PayPaly return parameters (token or PayerID)
  const paypalProcessedRef = useRef(false);
  const paypalPopupRef = useRef(null);
  const giftPaypalCleanupRef = useRef(null);
  // PayPal capture logic removed - now handled by /payment/addon/success
  useEffect(() => {
    // No-op - capture logic moved to dedicated page
  }, []);

  // Close project menu when clicking outside
  useEffect(() => {
    if (!projectMenuOpenId) return;
    const handleClickOutside = (e) => {
      if (e.target.closest('.project-menu-wrapper')) return;
      setProjectMenuOpenId(null);
    };
    const t = setTimeout(() => document.addEventListener('click', handleClickOutside), 0);
    return () => {
      clearTimeout(t);
      document.removeEventListener('click', handleClickOutside);
    };
  }, [projectMenuOpenId]);

  // Close session (direct chat) menu when clicking outside
  useEffect(() => {
    if (!sessionMenuOpenId) return;
    const handleClickOutside = (e) => {
      if (e.target.closest('.session-menu-wrapper')) return;
      setSessionMenuOpenId(null);
    };
    const t = setTimeout(() => document.addEventListener('click', handleClickOutside), 0);
    return () => {
      clearTimeout(t);
      document.removeEventListener('click', handleClickOutside);
    };
  }, [sessionMenuOpenId]);

  // Close avatar menu when clicking outside
  useEffect(() => {
    if (!avatarMenuOpenId) return;
    const handleClickOutside = (e) => {
      if (e.target.closest('.avatar-menu-wrapper')) return;
      setAvatarMenuOpenId(null);
    };
    const t = setTimeout(() => document.addEventListener('click', handleClickOutside), 0);
    return () => {
      clearTimeout(t);
      document.removeEventListener('click', handleClickOutside);
    };
  }, [avatarMenuOpenId]);

  // Fetch avatar members when an avatar is selected — reads from AvatarMemberRelation + owner
  useEffect(() => {
    const fetchMembers = async () => {
      if (!selectedAvatar?.id) {
        setAvatarMembers([]);
        return;
      }

      try {
        setLoadingAvatarMembers(true);
        const res = await avatarsAPI.getAvatarMembers(selectedAvatar.id);
        const rawMembers = res?.data?.members || res?.members || [];

        const normalized = (Array.isArray(rawMembers) ? rawMembers : [])
          .map((m) => {
            if (!m) return null;
            const email = m.email || '';
            const name = m.name || email || 'Member';
            return {
              id: m.user_id,
              user_id: m.user_id,
              email,
              name,
              initials: (name || email || 'M')
                .split(' ')
                .filter(Boolean)
                .slice(0, 2)
                .map((p) => p[0].toUpperCase())
                .join('') || 'M',
              status: 'accepted',
              is_owner: m.is_owner || false,
            };
          })
          .filter(Boolean);

        setAvatarMembers(normalized);
      } catch (e) {
        setAvatarMembers([]);
      } finally {
        setLoadingAvatarMembers(false);
      }
    };

    fetchMembers();
  }, [selectedAvatar?.id]);

  // Fetch my avatars list
  const fetchMyAvatars = async () => {
    try {
      setLoadingMyAvatars(true);
      const response = await avatarsAPI.getMyDetails();
      console.log('📥 My avatars API response:', response);

      // Handle different response structures
      let avatarsList = [];
      if (Array.isArray(response)) {
        avatarsList = response;
      } else if (response?.data) {
        if (Array.isArray(response.data)) {
          avatarsList = response.data;
        } else if (Array.isArray(response.data.avatars)) {
          avatarsList = response.data.avatars;
        } else if (response.data.avatar && typeof response.data.avatar === 'object') {
          avatarsList = [response.data.avatar];
        }
      } else if (Array.isArray(response?.avatars)) {
        avatarsList = response.avatars;
      } else if (response?.avatar && typeof response.avatar === 'object') {
        avatarsList = [response.avatar];
      }

      console.log(`📥 Extracted ${avatarsList.length} avatars from API response`);
      setMyAvatarsList(avatarsList);

      // Auto-select the first avatar as default if avatars are available
      if (avatarsList.length > 0) {
        const firstAvatar = avatarsList[0];
        console.log('✅ Auto-selecting first avatar as default:', firstAvatar);
        // Always set the first avatar as selected when fetching avatars
        // This ensures the first avatar is always selected by default
        setSelectedAvatar(firstAvatar);

        // Also update the main avatars list if it's empty
        if (avatars.length === 0) {
          setAvatars(avatarsList);
        }
      }
    } catch (error) {
      console.error('❌ Failed to fetch my avatars:', error);
      setMyAvatarsList([]);
    } finally {
      setLoadingMyAvatars(false);
    }
  };

  // Fetch my avatars when component mounts or user is authenticated
  useEffect(() => {
    if (isAuthenticated && user) {
      fetchMyAvatars();
    }
  }, [isAuthenticated, user]);

  // Auto-select the first avatar from myAvatarsList if no avatar is currently selected (backup)
  // Note: Primary selection happens in fetchMyAvatars, but this ensures it works even if state is reset
  useEffect(() => {
    if (myAvatarsList.length > 0 && !selectedAvatar && !loadingMyAvatars) {
      const firstAvatar = myAvatarsList[0];
      console.log('✅ Auto-selecting first avatar (backup):', firstAvatar);
      setSelectedAvatar(firstAvatar);
      // Also update the main avatars list if it's empty
      if (avatars.length === 0) {
        setAvatars(myAvatarsList);
      }
    }
  }, [myAvatarsList, selectedAvatar, loadingMyAvatars, avatars.length]);

  // Fetch all plans for dashboard - try admin API first, fallback to public API for regular users
  const fetchAllPlans = async () => {
    try {
      setLoadingPlans(true);

      // Helper function to extract plans from response
      const extractPlansFromResponse = (response) => {
        let extractedPlans = [];

        if (response && response.data) {
          if (response.data.plans && Array.isArray(response.data.plans)) {
            extractedPlans = response.data.plans;
          } else if (Array.isArray(response.data)) {
            extractedPlans = response.data;
          } else if (response.data.items && Array.isArray(response.data.items)) {
            extractedPlans = response.data.items;
          }
        } else if (Array.isArray(response)) {
          extractedPlans = response;
        } else if (response && response.plans && Array.isArray(response.plans)) {
          extractedPlans = response.plans;
        } else if (response && response.items && Array.isArray(response.items)) {
          extractedPlans = response.items;
        }

        return extractedPlans;
      };

      // Try admin API first (only if user has admin token)
      const adminToken = localStorage.getItem('ADMIN_ACCESS_TOKEN') || localStorage.getItem('admin_access_token');
      if (adminToken) {
        try {
          console.log('Trying admin plans API...');
          const adminResponse = await adminAPI.getAdminPlans(1, 10);
          const extractedPlans = extractPlansFromResponse(adminResponse);

          if (extractedPlans.length > 0) {
            setDashboardSummary(adminResponse);
            setPlans(extractedPlans);
            console.log(`Successfully loaded ${extractedPlans.length} plans from admin API`);
            return; // Success, exit early
          }
        } catch (adminError) {
          // Silently fail admin API - regular users don't have admin access (expected)
          console.log('Admin API not available (expected for regular users), falling back to public API');
          // Don't show error toast - this is expected for regular users
        }
      }

      // Fallback to public plans API (for regular users)
      console.log('Fetching plans from public API...');
      const publicResponse = await plansAPI.getAllPlans();
      const extractedPlans = extractPlansFromResponse(publicResponse);

      if (extractedPlans.length > 0) {
        setPlans(extractedPlans);
        console.log(`Successfully loaded ${extractedPlans.length} plans from public API`);
      } else {
        console.warn('No plans found in public API response');
        // Don't show error - plans might not be available
      }
    } catch (error) {
      console.error('Error fetching plans:', error);
      // Only show error if both admin and public APIs fail
      toast.error('Failed to load plan details. Please try again later.', {
        toastId: 'plans-load-error',
        autoClose: 3000
      });
    } finally {
      setLoadingPlans(false);
    }
  };

  // Fetch avatar plans for renewal modal (structured by type)
  const fetchAvatarPlans = async () => {
    try {
      console.log('Fetching avatar plans for renewal...');
      let response;
      try {
        response = await plansAPI.getAvatarPlans();
      } catch (checkoutErr) {
        console.warn('GET /api/avatar-plans failed, falling back:', checkoutErr);
        response = await plansAPI.getAvatarPlans();
      }

      let avatarTypes = [];
      if (response && response.data && response.data.avatar_types && Array.isArray(response.data.avatar_types)) {
        avatarTypes = response.data.avatar_types;
      } else if (response && response.data && Array.isArray(response.data)) {
        avatarTypes = response.data;
      } else if (Array.isArray(response)) {
        avatarTypes = response;
      }

      // Fallback for "For Loved Once" if needed
      const lovedOne = avatarTypes.find(a => (a.id === 2) || (a.name && String(a.name).toLowerCase().includes('loved')));
      if (lovedOne && (!lovedOne.plans || lovedOne.plans.length === 0)) {
        lovedOne.plans = [
          { id: 2, avatar_type_id: 2, monthly_price_usd: 19.99, yearly_price_usd: null, plan_days: 30 },
          { id: 3, avatar_type_id: 2, monthly_price_usd: null, yearly_price_usd: 150, plan_days: 365 }
        ];
      }

      if (avatarTypes.length > 0) {
        setAvatarPlans(avatarTypes);
        console.log('Loaded avatar plans for renewal:', avatarTypes.length);
      }
    } catch (error) {
      console.error('Error fetching avatar plans:', error);
      toast.error('Failed to load plan details.');
    }
  };

  const handleRenewalPayment = async (plan, billingInterval) => {
    setSelectedRenewalPlan(plan);
    setRenewalBillingPeriod(billingInterval);
    setShowRenewalSelectionModal(false);
    setShowBillingModal(false); // Close billing modal if open

    // Open blank popup NOW — synchronous, inside the user gesture — before any await.
    // Browsers only honour window.open() without blocking when called directly from a click.
    const popupWidth = 520;
    const popupHeight = 700;
    const left = Math.round(window.screenX + (window.outerWidth - popupWidth) / 2);
    const top = Math.round(window.screenY + (window.outerHeight - popupHeight) / 2);
    const upgradePopup = window.open(
      'about:blank',
      'PayPalUpgrade',
      `width=${popupWidth},height=${popupHeight},left=${left},top=${top},scrollbars=yes,resizable=yes`
    );
    const popupBlocked = !upgradePopup || upgradePopup.closed || typeof upgradePopup.closed === 'undefined';

    await processRenewalPayment(plan, billingInterval, upgradePopup, popupBlocked);
  };

  const processRenewalPayment = async (planOverride, intervalOverride, upgradePopup = null, popupBlocked = false) => {
    const plan = planOverride || selectedRenewalPlan;
    const interval = intervalOverride || renewalBillingPeriod;
    if (!plan) return;

    setProcessingRenewal(true);
    try {
      // breakdown: 1) save context 2) create order 3) open popup for approve_url

      // 1. Redirect to a dedicated success page for addons
      const baseUrl = window.location.origin;
      const successUrl = `${baseUrl}/payment/success`;
      const currentUrl = window.location.href.split('?')[0];

      // 2. Save context for the success page to pick up (localStorage is reliable across page loads)
      // PayPal sandbox uses 'token' param in URL for order ID.
      localStorage.setItem('paypal_checkout_context', JSON.stringify({
        avatar_plan_id: plan.id,
        plan_interval: interval,
        auto_renew: true,
        started_from: '/chat',
        context: 'popup'
      }));

      // 3. Create PayPal order
      const createResponse = await paymentsAPI.createPaypalOrder({
        avatar_plan_id: plan.id,
        plan_interval: interval,
        return_url: successUrl,
        cancel_url: currentUrl
      });

      console.log('Renewal Create Order Response:', createResponse);

      // 4. Find approval URL and open popup
      let approveUrl;
      if (createResponse?.approve_url) {
        approveUrl = createResponse.approve_url;
      } else if (createResponse?.data?.approve_url) {
        approveUrl = createResponse.data.approve_url;
      } else if (createResponse?.links && Array.isArray(createResponse.links)) {
        const link = createResponse.links.find(l => l.rel === 'approve');
        if (link) approveUrl = link.href;
      } else if (createResponse?.data?.links && Array.isArray(createResponse.data.links)) {
        const link = createResponse.data.links.find(l => l.rel === 'approve');
        if (link) approveUrl = link.href;
      }

      if (approveUrl) {
        // Try to extract order ID from approveUrl as fallback
        let orderId = createResponse?.order_id || createResponse?.data?.order_id;
        if (!orderId) {
          try {
            orderId = new URL(approveUrl).searchParams.get('token');
          } catch (e) {
            console.warn('Failed to extract token/order_id from approveUrl:', e);
          }
        }

        if (orderId) {
          const contextSnapshot = {
            order_id: orderId,
            avatar_plan_id: plan.id,
            plan_interval: interval,
            auto_renew: true,
            started_from: '/chat',
            context: 'popup'
          };
          localStorage.setItem('paypal_checkout_context', JSON.stringify(contextSnapshot));
          console.log('Saved paypal_checkout_context with orderId:', orderId);
        }

        if (popupBlocked || !upgradePopup || upgradePopup.closed) {
          // Popup was blocked or not provided — fall back to full-page redirect
          console.log('Redirecting to PayPal for renewal (full page fallback):', approveUrl);
          window.location.href = approveUrl;
        } else {
          // Navigate the already-open blank popup to the real PayPal URL
          console.log('Navigating popup to PayPal for renewal:', approveUrl);
          upgradePopup.location.href = approveUrl;

          const upgradeMessageHandler = (event) => {
            // Check origin to ensure security
            if (event.origin !== window.location.origin) return;

            if (event.data?.type === 'PAYPAL_SUCCESS') {
              console.log('Received PayPal success from popup:', event.data);
              window.removeEventListener('message', upgradeMessageHandler);
              clearInterval(upgradePollTimer);

              // Handle success - refresh data
              fetchUserAndAvatarData();
            } else if (event.data?.type === 'PAYPAL_ERROR') {
              console.error('Received PayPal error from popup:', event.data.error);
              window.removeEventListener('message', upgradeMessageHandler);
              clearInterval(upgradePollTimer);
            }
          };

          const upgradePollTimer = setInterval(() => {
            if (upgradePopup.closed) {
              window.removeEventListener('message', upgradeMessageHandler);
              clearInterval(upgradePollTimer);
            }
          }, 800);

          window.addEventListener('message', upgradeMessageHandler);
        }

      } else {
        if (upgradePopup && !upgradePopup.closed) upgradePopup.close();
        console.error('No approval URL found in response:', createResponse);
        toast.error('Failed to initiate payment: No approval link returned.');
      }

    } catch (error) {
      if (upgradePopup && !upgradePopup.closed) upgradePopup.close();
      console.error('Renewal payment failed:', error);
      const msg = error?.response?.msg || error?.response?.error || error?.message || 'Payment processing failed';
      toast.error(msg);
    } finally {
      setProcessingRenewal(false);
    }
  };

  const resetInviteForm = () => {
    setInviteFirstName('');
    setInviteLastName('');
    setInviteEmail('');
    setInviteWhoPay(null);
    setInviteGiftPlan(null);
    setInvitees([{ id: Date.now(), firstName: '', lastName: '', email: '', action: 'send_invite' }]);
  };

  /**
   * GET /api/plans/avatar-plans → { avatar_types } or { pricing_options } for invite dropdown.
   * Matches renewal flow: data.avatar_types | data[] (types) | flat pricing rows[].
   */
  const mapAvatarTypeNodesForInvite = (avatarTypes) => {
    if (!Array.isArray(avatarTypes) || avatarTypes.length === 0) return null;
    const cloned = avatarTypes.map((at) => ({ ...at, plans: Array.isArray(at.plans) ? [...at.plans] : at.plans }));
    const lovedOne = cloned.find(
      (a) =>
        a &&
        (a.id === 2 || (a.name && String(a.name).toLowerCase().includes('loved')))
    );
    if (lovedOne && (!lovedOne.plans || lovedOne.plans.length === 0)) {
      lovedOne.plans = [
        { id: 2, avatar_type_id: 2, monthly_price_usd: 19.99, yearly_price_usd: null, plan_days: 30 },
        { id: 3, avatar_type_id: 2, monthly_price_usd: null, yearly_price_usd: 150, plan_days: 365 },
      ];
    }
    const mappedTypes = cloned.map((at) => {
      let pricing_options = Array.isArray(at.pricing_options) ? [...at.pricing_options] : [];
      if (pricing_options.length === 0 && Array.isArray(at.plans)) {
        at.plans.forEach((pl) => {
          if (pl.monthly_price_usd != null) {
            pricing_options.push({
              interval_name: 'monthly',
              interval: 'monthly',
              plan_id: pl.avatar_plan_id ?? at.avatar_plan_id ?? null,
              plan_pricing_id: pl.id ?? pl.avatar_type_id ?? at.id ?? null,
              price_usd: Number(pl.monthly_price_usd),
            });
          }
          if (pl.yearly_price_usd != null) {
            pricing_options.push({
              interval_name: 'yearly',
              interval: 'yearly',
              plan_id: pl.avatar_plan_id ?? at.avatar_plan_id ?? null,
              plan_pricing_id: pl.id ?? pl.avatar_type_id ?? at.id ?? null,
              price_usd: Number(pl.yearly_price_usd),
            });
          }
        });
      }
      return { ...at, pricing_options };
    });
    const hasAny = mappedTypes.some((at) => Array.isArray(at.pricing_options) && at.pricing_options.length > 0);
    return hasAny ? { avatar_types: mappedTypes } : null;
  };

  const normalizeAvatarPlansResponseForInvite = (response) => {
    if (!response) return null;

    const dt = response?.data;
    if (dt && typeof dt === 'object' && !Array.isArray(dt) && Array.isArray(dt.avatar_types)) {
      const mapped = mapAvatarTypeNodesForInvite(dt.avatar_types);
      if (mapped) return mapped;
    }

    const root = dt !== undefined ? dt : response;
    if (Array.isArray(root) && root.length > 0) {
      const first = root[0];
      const looksLikeFlatPricingRow =
        first &&
        (first.interval != null ||
          first.plan_interval != null ||
          (first.price_usd != null &&
            !Array.isArray(first.plans) &&
            !Array.isArray(first.pricing_options)));
      if (!looksLikeFlatPricingRow) {
        const mapped = mapAvatarTypeNodesForInvite(root);
        if (mapped) return mapped;
      }
      if (looksLikeFlatPricingRow) {
        const toNum = (v) => {
          if (v == null || v === '') return null;
          const n = Number(v);
          return Number.isFinite(n) ? n : null;
        };
        const pricing_options = root
          .map((p) => {
            const interval = String(p.interval || p.plan_interval || '').toLowerCase();
            const plan_pricing_id =
              toNum(p.plan_pricing_id) ??
              toNum(p.pricing_id) ??
              toNum(p.avatar_plan_pricing_id) ??
              toNum(p.id) ??
              toNum(p.avatar_type_id);
            const plan_id = toNum(p.avatar_plan_id) ?? toNum(p.plan_id);
            return {
              interval_name: interval,
              interval,
              plan_id: plan_id ?? null,
              plan_pricing_id: plan_pricing_id ?? null,
              price_usd: p.price_usd != null ? Number(p.price_usd) : null,
              avatar_type_name: p.name || p.type_name || p.avatar_type_name || '',
            };
          })
          .filter((o) => o.plan_pricing_id != null && !Number.isNaN(o.plan_pricing_id));
        if (pricing_options.length > 0) return { pricing_options };
      }
    }

    if (root && typeof root === 'object' && !Array.isArray(root) && Array.isArray(root.avatar_types)) {
      const mapped = mapAvatarTypeNodesForInvite(root.avatar_types);
      if (mapped) return mapped;
    }

    if (root && typeof root === 'object' && !Array.isArray(root) && (root.pricing_options || root.avatar_types)) {
      return root;
    }
    return null;
  };

  const countInvitePricingOptions = (payload) => {
    if (!payload) return 0;
    const opts = getPricingOptionsFromAddMemberPlan(payload);
    return opts.length;
  };

  const fetchAddMemberAvatarPlan = async () => {
    setAddMemberPlanLoading(true);
    try {
      let payload = null;
      try {
        const listRes = await plansAPI.getAvatarPlans();
        payload = normalizeAvatarPlansResponseForInvite(listRes);
      } catch (e) {
        console.warn('GET /api/plans/avatar-plans for invite modal failed:', e);
      }
      setAddMemberAvatarPlanDetails(payload);
    } catch (e) {
      console.warn('Fetch plans for invite modal failed:', e);
      setAddMemberAvatarPlanDetails(null);
    } finally {
      setAddMemberPlanLoading(false);
    }
  };

  const getPricingOptionsFromAddMemberPlan = (planDataOverride) => {
    const details = planDataOverride ?? addMemberAvatarPlanDetails;
    if (!details) return [];
    const avatarTypes = details.avatar_types || [];
    if (avatarTypes.length > 0) {
      const all = [];
      avatarTypes.forEach((at) => {
        const opts = at.pricing_options || [];
        opts.forEach((opt) => {
          all.push({
            ...opt,
            avatar_type_id: at.avatar_type_id ?? at.id,
            avatar_type_name: at.name ?? at.type_name ?? '',
          });
        });
      });
      return all;
    }
    return details.pricing_options || [];
  };

  const intervalHintsForInvite = (opt) => {
    const parts = [
      opt?.interval_name,
      opt?.interval,
      opt?.plan_interval,
      opt?.billing_interval,
      opt?.billing_period,
      opt?.billing_cycle,
      opt?.period,
    ];
    return parts
      .filter((p) => p != null && p !== '')
      .map((p) => String(p).toLowerCase().replace(/_/g, ' '))
      .join(' ');
  };

  const classifyInvitePlanInterval = (opt) => {
    const h = intervalHintsForInvite(opt);
    const pd = Number(opt?.plan_days);
    if (Number.isFinite(pd)) {
      if (pd >= 28 && pd <= 35) return 'monthly';
      if (pd >= 360 && pd <= 375) return 'yearly';
    }
    if (h.includes('free') && h.includes('trial')) return null;
    if (/\bmonth\b|monthly|per month|^1m\b/.test(h) || h.includes('month')) return 'monthly';
    if (/\byear\b|yearly|annual|\b12m\b|365|per year/.test(h) || h.includes('year') || h.includes('annual')) {
      return 'yearly';
    }
    return null;
  };

  const invitePlanPricingIdFromOpt = (opt) => {
    if (!opt) return null;
    const toNum = (v) => {
      if (v == null || v === '') return null;
      const n = Number(v);
      return Number.isFinite(n) ? n : null;
    };
    return toNum(opt.plan_pricing_id) ?? toNum(opt.pricing_id) ?? toNum(opt.id);
  };

  const isInvitePaidGiftPricingOpt = (opt) => {
    const pid = invitePlanPricingIdFromOpt(opt);
    if (pid == null) return false;
    const h = intervalHintsForInvite(opt);
    if (h.includes('free') && h.includes('trial')) return false;
    if (String(opt?.interval_name || '').toLowerCase() === 'free_trial') return false;
    return true;
  };

  const isInviteGiftAction = (action) =>
    action != null && action !== 'send_invite' && String(action).startsWith('gift_');

  /**
   * Dropdown: Send invite + one option per paid pricing row from GET /api/plans/avatar-plans.
   */
  const getAddMemberInvitePlanOptions = (planDataOverride) => {
    const options = [];
    const pricingOptions = getPricingOptionsFromAddMemberPlan(planDataOverride);
    const sendInviteOpt =
      pricingOptions.find(
        (opt) =>
          String(opt?.interval_name || '').toLowerCase() === 'free_trial' || Number(opt?.plan_id) === 1
      ) || pricingOptions[0];
    options.push({
      value: 'send_invite',
      label: 'Send invite',
      plan_id: 1,
      plan_pricing_id: sendInviteOpt ? invitePlanPricingIdFromOpt(sendInviteOpt) : null,
      interval: null,
    });

    const seenPid = new Set();
    const paidRows = pricingOptions.filter((opt) => {
      const pid = invitePlanPricingIdFromOpt(opt);
      if (pid == null || seenPid.has(pid)) return false;
      if (!isInvitePaidGiftPricingOpt(opt)) return false;
      seenPid.add(pid);
      return true;
    });

    paidRows.sort((a, b) => {
      const ta = a.avatar_type_id ?? 0;
      const tb = b.avatar_type_id ?? 0;
      if (ta !== tb) return ta - tb;
      const ca = classifyInvitePlanInterval(a) === 'monthly' ? 0 : 1;
      const cb = classifyInvitePlanInterval(b) === 'monthly' ? 0 : 1;
      if (ca !== cb) return ca - cb;
      return invitePlanPricingIdFromOpt(a) - invitePlanPricingIdFromOpt(b);
    });

    paidRows.forEach((opt) => {
      const pid = invitePlanPricingIdFromOpt(opt);
      const kind = classifyInvitePlanInterval(opt);
      const intervalLabel =
        kind === 'monthly' ? '1 month' : kind === 'yearly' ? '1 year' : 'plan';
      const price = Number(opt.price_usd);
      const priceBit = Number.isFinite(price) && price > 0 ? ` — $${price.toFixed(2)}` : '';
      const typeBit = opt.avatar_type_name ? `${opt.avatar_type_name} — ` : '';
      options.push({
        value: `gift_${pid}`,
        label: `${typeBit}Gift ${intervalLabel}${priceBit}`,
        plan_id: opt.plan_id != null ? Number(opt.plan_id) : 1,
        plan_pricing_id: pid,
        price_usd: Number.isFinite(price) ? price : null,
        interval: kind,
      });
    });

    return options;
  };

  /**
   * PayPal line items + total from resolved gift rows.
   * Uses the same per-row prices as the checkout summary (dropdown opts first), then catalog — so total_usd matches PayPal.
   */
  const buildInviteGiftPaypalFromPlan = (planData, giftRows, dropdownOpts) => {
    const pricingOptions = getPricingOptionsFromAddMemberPlan(planData);

    const unitForGiftRow = (g) => {
      const pid = Number(g.plan_pricing_id);
      const sel = dropdownOpts.find((o) => o.value === g.action);
      let u = Number(sel?.price_usd);
      if (Number.isFinite(u)) return u;
      const po = pricingOptions.find((o) => invitePlanPricingIdFromOpt(o) === pid);
      u = po ? Number(po.price_usd) : NaN;
      return u;
    };

    const intervalForGiftRow = (g) => {
      const pid = Number(g.plan_pricing_id);
      const po = pricingOptions.find((o) => invitePlanPricingIdFromOpt(o) === pid);
      return (
        g.plan_interval || (po ? classifyInvitePlanInterval(po) : null) || 'monthly'
      );
    };

    let dropdownSum = 0;
    giftRows.forEach((g) => {
      const u = unitForGiftRow(g);
      if (Number.isFinite(u)) dropdownSum += u;
    });
    dropdownSum = Math.round(dropdownSum * 100) / 100;

    const byPid = new Map();
    giftRows.forEach((g) => {
      const pid = Number(g.plan_pricing_id);
      if (!Number.isFinite(pid)) return;
      const u = unitForGiftRow(g);
      const interval = intervalForGiftRow(g);
      if (!byPid.has(pid)) {
        byPid.set(pid, { quantity: 0, unit_amount_usd: u, interval });
      } else {
        const meta = byPid.get(pid);
        meta.quantity += 1;
        if (!Number.isFinite(meta.unit_amount_usd) && Number.isFinite(u)) {
          meta.unit_amount_usd = u;
        }
        return;
      }
      byPid.get(pid).quantity += 1;
    });

    const groupedLines = [];
    for (const [pidKey, meta] of byPid) {
      const pid = Number(pidKey);
      const u = Number(meta.unit_amount_usd);
      if (!Number.isFinite(u)) continue;
      groupedLines.push({
        interval: meta.interval || 'monthly',
        quantity: meta.quantity,
        unit_amount_usd: u,
        plan_pricing_id: pid,
      });
    }
    let groupedTotal = groupedLines.reduce(
      (s, li) => s + Number(li.quantity) * Number(li.unit_amount_usd),
      0
    );
    groupedTotal = Math.round(groupedTotal * 100) / 100;

    let gift_line_items = groupedLines;
    let combinedGiftUsd = groupedTotal;

    if (dropdownSum > 0 && Math.abs(groupedTotal - dropdownSum) > 0.02) {
      gift_line_items = giftRows
        .map((g) => {
          const pid = Number(g.plan_pricing_id);
          if (!Number.isFinite(pid)) return null;
          const u = unitForGiftRow(g);
          if (!Number.isFinite(u)) return null;
          return {
            interval: intervalForGiftRow(g),
            quantity: 1,
            unit_amount_usd: u,
            plan_pricing_id: pid,
          };
        })
        .filter(Boolean);
      combinedGiftUsd = Math.round(
        gift_line_items.reduce(
          (s, li) => s + Number(li.quantity) * Number(li.unit_amount_usd),
          0
        ) * 100
      ) / 100;
    }

    return { gift_line_items, combinedGiftUsd };
  };

  // If plan options change, reset row actions that no longer exist in the dropdown (e.g. old gift_* ids) — avoids resolveRow returning null.
  useEffect(() => {
    if (!showAddMemberModal || addMemberPlanLoading || !addMemberAvatarPlanDetails) return;
    const allowed = new Set(
      getAddMemberInvitePlanOptions(addMemberAvatarPlanDetails).map((o) => o.value)
    );
    setInvitees((prev) => {
      let changed = false;
      const next = prev.map((i) => {
        if (allowed.has(i.action)) return i;
        changed = true;
        return { ...i, action: 'send_invite' };
      });
      return changed ? next : prev;
    });
  }, [showAddMemberModal, addMemberAvatarPlanDetails, addMemberPlanLoading]);

  const setInviteePlanAction = (inviteeId, actionValue) => {
    const opts = getAddMemberInvitePlanOptions();
    const selected = opts.find((o) => o.value === actionValue);
    setInvitees((prev) =>
      prev.map((i) => {
        if (i.id !== inviteeId) return i;
        return {
          ...i,
          action: actionValue,
          plan_id: selected?.plan_id ?? 1,
          plan_pricing_id: selected?.plan_pricing_id ?? null,
        };
      })
    );
  };

  /** POST /api/avatars/{id}/invite-emails — Send invite rows only (I'll pay / family flow). */
  const buildChatInviteEmailRecipientsFromRows = (sendRows, dropdownOpts, planData) => {
    const pricingOptions = getPricingOptionsFromAddMemberPlan(planData);
    return sendRows.map((row) => {
      const action = row.action || 'send_invite';
      const sel = dropdownOpts.find((o) => o.value === action);
      let plan_id = sel?.plan_id ?? 1;
      let plan_pricing_id = sel?.plan_pricing_id;
      if (action === 'send_invite' && (plan_pricing_id === null || plan_pricing_id === undefined)) {
        const fallback =
          pricingOptions.find((opt) => opt.interval_name === 'free_trial') || pricingOptions[0];
        plan_pricing_id = fallback?.plan_pricing_id ?? null;
      }
      const name = [row.firstName, row.lastName].filter(Boolean).join(' ').trim();
      const rec = {
        email: row.email.trim(),
        plan_id: Number(plan_id) || 1,
      };
      if (plan_pricing_id != null && plan_pricing_id !== '') {
        rec.plan_pricing_id = Number(plan_pricing_id);
      }
      if (name) rec.name = name;
      return rec;
    });
  };

  const sumGiftInvitePrices = (giftRowsResolved, dropdownOpts) => {
    let sum = 0;
    for (const r of giftRowsResolved) {
      const opt = dropdownOpts.find((o) => o.value === r.action);
      const p = Number(opt?.price_usd);
      if (Number.isFinite(p)) sum += p;
    }
    return sum;
  };

  /** Live totals for "I'll pay" + per-row gift/month/year (one PayPal checkout, combined total). */
  const inviteCheckoutPreview = useMemo(() => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const base = {
      validWithEmailCount: 0,
      giftCount: 0,
      sendInviteCount: 0,
      giftLines: [],
      giftTotalUsd: 0,
      monthlySubtotalUsd: 0,
      yearlySubtotalUsd: 0,
      paypalGiftOk: true,
      blockReason: null,
      paypalStepCount: 0,
      hasMixedGiftIntervals: false,
    };
    if (!addMemberAvatarPlanDetails || inviteWhoPay !== true) {
      return base;
    }
    const dropdownOpts = getAddMemberInvitePlanOptions(addMemberAvatarPlanDetails);
    const validInvitees = invitees.filter((i) => {
      const e = (i.email || '').trim();
      return e && emailRegex.test(e);
    });
    const giftLines = [];
    let giftTotalUsd = 0;
    let monthlySubtotalUsd = 0;
    let yearlySubtotalUsd = 0;
    let giftCount = 0;
    let sendInviteCount = 0;
    const monthlyPricingIds = [];
    const yearlyPricingIds = [];
    for (const row of validInvitees) {
      const action = row.action || 'send_invite';
      if (action === 'send_invite') {
        sendInviteCount++;
        continue;
      }
      if (isInviteGiftAction(action)) {
        const opt = dropdownOpts.find((o) => o.value === action);
        const p = Number(opt?.price_usd);
        if (Number.isFinite(p)) giftTotalUsd += p;
        const iv = opt?.interval;
        if (iv === 'monthly' && Number.isFinite(p)) monthlySubtotalUsd += p;
        if (iv === 'yearly' && Number.isFinite(p)) yearlySubtotalUsd += p;
        giftCount++;
        giftLines.push({
          email: row.email.trim(),
          optLabel: opt?.label || 'Gift',
          priceUsd: p,
        });
        const pid =
          opt?.plan_pricing_id != null && opt.plan_pricing_id !== ''
            ? Number(opt.plan_pricing_id)
            : NaN;
        if (iv === 'monthly') monthlyPricingIds.push(pid);
        else if (iv === 'yearly') yearlyPricingIds.push(pid);
      }
    }
    let blockReason = null;
    if (giftCount > 0) {
      if (giftLines.some((l) => !Number.isFinite(l.priceUsd))) {
        blockReason = 'A gift row is missing a price. Wait for plans to load or pick another option.';
      }
    }
    const intervalKinds = (() => {
      const s = new Set();
      if (monthlyPricingIds.length > 0) s.add('monthly');
      if (yearlyPricingIds.length > 0) s.add('yearly');
      return s.size;
    })();
    /** 1 = single PayPal checkout (combined total via total_usd). */
    const paypalStepCount = 1;
    const hasMixedGiftIntervals = intervalKinds > 1;
    const paypalGiftOk = giftCount === 0 || (!blockReason && giftTotalUsd > 0);
    return {
      validWithEmailCount: validInvitees.length,
      giftCount,
      sendInviteCount,
      giftLines,
      giftTotalUsd,
      monthlySubtotalUsd,
      yearlySubtotalUsd,
      paypalGiftOk,
      blockReason,
      paypalStepCount,
      hasMixedGiftIntervals,
    };
  }, [invitees, addMemberAvatarPlanDetails, inviteWhoPay]);

  const handleSendInvitation = async () => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (inviteWhoPay === null) {
      toast.error('Please select who will cover their subscription.');
      return;
    }

    let planData = addMemberAvatarPlanDetails;
    if (!planData || countInvitePricingOptions(planData) === 0) {
      try {
        const listRes = await plansAPI.getAvatarPlans();
        const fromList = normalizeAvatarPlansResponseForInvite(listRes);
        if (fromList && countInvitePricingOptions(fromList) > 0) {
          planData = fromList;
          setAddMemberAvatarPlanDetails(fromList);
        }
      } catch (_) { /* ignore */ }
    }
    if (!planData || countInvitePricingOptions(planData) === 0) {
      toast.error('Plan details are not available. Please try again later.');
      return;
    }

    const dropdownOpts = getAddMemberInvitePlanOptions(planData);

    const validInvitees = invitees.filter((i) => {
      const e = (i.email || '').trim();
      return e && emailRegex.test(e);
    });
    if (validInvitees.length === 0) {
      toast.error('Please enter at least one valid email address.');
      return;
    }
    const invalidEmails = invitees.filter((i) => {
      const e = (i.email || '').trim();
      return e && !emailRegex.test(e);
    });
    if (invalidEmails.length > 0) {
      toast.error('One or more email addresses are invalid.');
      return;
    }

    const resolveRow = (row) => {
      const pricingOptions = getPricingOptionsFromAddMemberPlan(planData);
      let action = row.action || 'send_invite';
      let sel = dropdownOpts.find((o) => o.value === action);
      if (!sel) {
        action = 'send_invite';
        sel = dropdownOpts.find((o) => o.value === 'send_invite');
      }
      if (!sel && dropdownOpts.length > 0) {
        sel = dropdownOpts[0];
        action = sel.value;
      }
      if (!sel) return null;

      let plan_id = sel.plan_id;
      let plan_pricing_id =
        sel.plan_pricing_id !== undefined && sel.plan_pricing_id !== null
          ? sel.plan_pricing_id
          : null;

      if (action === 'send_invite' && (plan_pricing_id === null || plan_pricing_id === undefined)) {
        const fallback =
          pricingOptions.find((opt) => String(opt?.interval_name || '').toLowerCase() === 'free_trial') ||
          pricingOptions[0];
        plan_pricing_id = fallback ? invitePlanPricingIdFromOpt(fallback) : null;
      }

      if (isInviteGiftAction(action)) {
        const pricingN = Number(plan_pricing_id);
        if (!Number.isFinite(pricingN)) {
          return null;
        }
        plan_pricing_id = pricingN;
        const fromApi = pricingOptions.find((o) => invitePlanPricingIdFromOpt(o) === pricingN);
        let resolvedPlanId = Number(plan_id);
        if (!Number.isFinite(resolvedPlanId) && fromApi != null) {
          resolvedPlanId = Number(fromApi.plan_id);
        }
        if (!Number.isFinite(resolvedPlanId)) {
          resolvedPlanId = Number(sel.plan_id);
        }
        if (!Number.isFinite(resolvedPlanId)) {
          resolvedPlanId = 1;
        }
        plan_id = resolvedPlanId;
      }

      let interval = null;
      if (isInviteGiftAction(action)) {
        interval = sel.interval || (() => {
          const po = pricingOptions.find((o) => invitePlanPricingIdFromOpt(o) === Number(plan_pricing_id));
          return po ? classifyInvitePlanInterval(po) : null;
        })();
      }
      return {
        email: row.email.trim(),
        firstName: row.firstName,
        lastName: row.lastName,
        action,
        plan_id,
        plan_pricing_id,
        plan_interval: interval,
      };
    };

    const resolved = validInvitees.map(resolveRow).filter(Boolean);
    if (resolved.length !== validInvitees.length) {
      toast.error('One or more rows have an invalid plan. Check the plan dropdown for each person.');
      return;
    }

    const giftRows = resolved.filter((r) => isInviteGiftAction(r.action));
    const sendRows = resolved.filter((r) => r.action === 'send_invite');

    const avatarIdForInvites = selectedAvatar?.id || selectedAvatar?.avatar_id;

    setSendingInvite(true);
    try {
      if (inviteWhoPay === false) {
        for (const row of resolved) {
          await projectsAPI.sendInvitation({
            invite_email: row.email,
            plan_pricing_id: row.plan_id,
            plan_id: row.plan_pricing_id,
            i_will_pay: 0,
          });
        }
        setShowAddMemberModal(false);
        resetInviteForm();
        return;
      }

      // I'll pay — Send invite rows: POST /api/avatars/{id}/invite-emails (no PayPal)
      if (giftRows.length === 0) {
        if (!avatarIdForInvites) {
          toast.error('Select an avatar before sending invitations.');
          return;
        }
        if (sendRows.length === 0) {
          toast.error('Add at least one recipient with a valid email.');
          return;
        }
        const recipients = buildChatInviteEmailRecipientsFromRows(sendRows, dropdownOpts, planData);
        const response = await avatarsAPI.sendInviteEmails(Number(avatarIdForInvites), {
          selected_email_recipients: recipients,
        });
        const isSuccess =
          response?.status === true ||
          response?.status_code === 200 ||
          response?.status_code === 201;
        if (!isSuccess) throw new Error('Failed to send invitations');
        setShowAddMemberModal(false);
        resetInviteForm();
        return;
      }

      // I'll pay + gift month/year: invite-emails for Send invite rows first, then PayPal; after capture, bulk send-invitation
      if (!avatarIdForInvites) {
        toast.error('Select an avatar before sending invitations.');
        return;
      }

      // Open blank popup NOW — synchronous, inside the user gesture — before any await.
      // Browsers only honour window.open() without blocking when called directly from a click.
      const popupWidth = 500;
      const popupHeight = 700;
      const left = Math.round(window.screenX + (window.outerWidth - popupWidth) / 2);
      const top = Math.round(window.screenY + (window.outerHeight - popupHeight) / 2);
      const giftPopup = window.open(
        'about:blank',
        'PayPalGift',
        `width=${popupWidth},height=${popupHeight},left=${left},top=${top},scrollbars=yes,resizable=yes`
      );
      const popupBlocked = !giftPopup || giftPopup.closed || typeof giftPopup.closed === 'undefined';

      if (sendRows.length > 0) {
        const recipients = buildChatInviteEmailRecipientsFromRows(sendRows, dropdownOpts, planData);
        const response = await avatarsAPI.sendInviteEmails(Number(avatarIdForInvites), {
          selected_email_recipients: recipients,
        });
        const isSuccess =
          response?.status === true ||
          response?.status_code === 200 ||
          response?.status_code === 201;
        if (!isSuccess) throw new Error('Failed to send invite emails');
      }

      const giftByPid = new Map();
      giftRows.forEach((g) => {
        const pid = Number(g.plan_pricing_id);
        if (!Number.isFinite(pid)) return;
        if (!giftByPid.has(pid)) giftByPid.set(pid, []);
        giftByPid.get(pid).push(g);
      });
      const giftInviteParts = [...giftByPid.entries()].map(([ppid, rows]) => ({
        invite_emails: rows.map((r) => r.email),
        plan_id: rows[0]?.plan_id ?? 1,
        plan_pricing_id: Number(ppid),
        i_will_pay: 1,
      }));
      const primaryGiftRow = giftRows[0];
      const planPricingId = primaryGiftRow.plan_pricing_id;
      const plan_interval = primaryGiftRow.plan_interval || 'monthly';

      const fromPlan = buildInviteGiftPaypalFromPlan(planData, giftRows, dropdownOpts);
      let { gift_line_items, combinedGiftUsd } = fromPlan;
      const fallbackSum = Math.round(sumGiftInvitePrices(giftRows, dropdownOpts) * 100) / 100;
      if (!Number.isFinite(combinedGiftUsd) || combinedGiftUsd <= 0) {
        combinedGiftUsd = fallbackSum;
      }

      /** One PayPal “bundle” line: charge exactly total_usd (monthly×n + yearly×m from plan prices). */
      const paypalQuantity = combinedGiftUsd > 0 ? 1 : giftRows.length;

      const pendingGiftPayload = { gift_invite_parts: giftInviteParts };

      const prelimCtx = {
        order_id: null,
        avatar_plan_id: planPricingId,
        plan_interval,
        auto_renew: false,
        quantity: paypalQuantity,
        total_usd: Number.isFinite(combinedGiftUsd) && combinedGiftUsd > 0 ? combinedGiftUsd : undefined,
        bundle_total_usd:
          Number.isFinite(combinedGiftUsd) && combinedGiftUsd > 0 ? combinedGiftUsd : undefined,
        gift_line_items: gift_line_items.length > 0 ? gift_line_items : undefined,
        started_from: '/chat',
        context: 'popup',
        reopen_invite_modal: true,
        invite_modal_checkout: true,
        pending_gift_invite_bulk: pendingGiftPayload,
      };
      try {
        sessionStorage.setItem('paypal_checkout_context', JSON.stringify(prelimCtx));
      } catch (_) { }
      try {
        localStorage.setItem('paypal_checkout_context', JSON.stringify(prelimCtx));
      } catch (_) { }

      if (!Number.isFinite(combinedGiftUsd) || combinedGiftUsd <= 0) {
        if (!popupBlocked) giftPopup.close();
        toast.error('Could not determine gift total from plan prices. Wait for plans to finish loading, then try again.');
        return;
      }


      const createResponse = await paymentsAPI.createPaypalOrder({
        avatar_plan_id: planPricingId,
        plan_interval,
        quantity: paypalQuantity,
        total_usd: combinedGiftUsd > 0 ? combinedGiftUsd : undefined,
        gift_line_items: gift_line_items.length > 0 ? gift_line_items : undefined,
      });
      let approveUrl;
      if (createResponse?.approve_url) {
        approveUrl = createResponse.approve_url;
      } else if (createResponse?.data?.approve_url) {
        approveUrl = createResponse.data.approve_url;
      } else if (createResponse?.data?.links && Array.isArray(createResponse.data.links)) {
        const link = createResponse.data.links.find((l) => l.rel === 'approve');
        if (link) approveUrl = link.href;
      }

      if (!approveUrl) {
        toast.error('Failed to start payment. No approval link returned.');
        return;
      }

      const orderId =
        createResponse?.data?.order_id ||
        createResponse?.order_id ||
        (() => {
          try {
            return new URL(approveUrl).searchParams.get('token');
          } catch (_) {
            return null;
          }
        })();

      const ctx = {
        order_id: orderId,
        avatar_plan_id: planPricingId,
        plan_interval,
        auto_renew: false,
        quantity: paypalQuantity,
        total_usd: Number.isFinite(combinedGiftUsd) && combinedGiftUsd > 0 ? combinedGiftUsd : undefined,
        bundle_total_usd:
          Number.isFinite(combinedGiftUsd) && combinedGiftUsd > 0 ? combinedGiftUsd : undefined,
        gift_line_items: gift_line_items.length > 0 ? gift_line_items : undefined,
        started_from: '/chat',
        context: 'popup',
        reopen_invite_modal: true,
        invite_modal_checkout: true,
        pending_gift_invite_bulk: pendingGiftPayload,
      };
      try {
        sessionStorage.setItem('paypal_checkout_context', JSON.stringify(ctx));
      } catch (_) { }
      try {
        localStorage.setItem('paypal_checkout_context', JSON.stringify(ctx));
      } catch (_) { }

      try {
        localStorage.setItem(
          `paypal_post_capture_invite_${orderId}`,
          JSON.stringify(pendingGiftPayload)
        );
      } catch (_) { }

      if (popupBlocked || giftPopup.closed) {
        const fullPageCtx = { ...ctx, context: 'full-page' };
        try {
          sessionStorage.setItem('paypal_checkout_context', JSON.stringify(fullPageCtx));
        } catch (_) { }
        try {
          localStorage.setItem('paypal_checkout_context', JSON.stringify(fullPageCtx));
        } catch (_) { }
        window.location.href = approveUrl;
        return;
      }

      // Navigate the already-open blank popup to the real PayPal URL
      giftPopup.location.href = approveUrl;

      paypalPopupRef.current = giftPopup;

      const messageHandler = (event) => {
        if (event.data?.type === 'PAYPAL_SUCCESS' && event.data?.context !== 'addon') {
          if (giftPaypalCleanupRef.current) giftPaypalCleanupRef.current();
          paypalPopupRef.current = null;
          resetInviteForm();
          setGiftSentSuccess(true);
          setShowAddMemberModal(true);
        } else if (event.data?.type === 'PAYPAL_ERROR') {
          if (giftPaypalCleanupRef.current) giftPaypalCleanupRef.current();
          paypalPopupRef.current = null;
          toast.error('Payment failed. Please try again.');
        }
      };
      const pollTimer = setInterval(() => {
        if (giftPopup.closed) {
          if (giftPaypalCleanupRef.current) giftPaypalCleanupRef.current();
          paypalPopupRef.current = null;
        }
      }, 600);
      giftPaypalCleanupRef.current = () => {
        clearInterval(pollTimer);
        window.removeEventListener('message', messageHandler);
        giftPaypalCleanupRef.current = null;
      };
      window.addEventListener('message', messageHandler);
    } catch (error) {
      const msg =
        error?.response?.msg || error?.response?.error || error?.message || 'Failed to send invitation.';
      toast.error(msg);
    } finally {
      setSendingInvite(false);
    }
  };

  const fetchAddons = async () => {
    setLoadingAddons(true);
    try {
      const response = await plansAPI.getAddons();
      console.log('Addons API response:', response);

      // Handle both { data: { addons, avatar_plans } } and { addons, avatar_plans } formats
      const resultData = response?.data || response;

      let extractedAddons = [];
      if (Array.isArray(resultData)) {
        extractedAddons = resultData;
      } else if (resultData?.addons && Array.isArray(resultData.addons)) {
        extractedAddons = resultData.addons;
      } else if (resultData?.items && Array.isArray(resultData.items)) {
        extractedAddons = resultData.items;
      }

      // Also extract avatar configurations if present
      const rawAvatarPlans = resultData?.avatar_plans || [];
      if (Array.isArray(rawAvatarPlans) && rawAvatarPlans.length > 0) {
        const plansByType = {};
        rawAvatarPlans.forEach(plan => {
          const typeName = plan.avatar_type_name || 'Generic';
          if (!plansByType[typeName]) {
            plansByType[typeName] = {
              id: plan.avatar_type_id || Math.random(),
              name: typeName,
              plans: []
            };
          }

          const normalizedPlan = { ...plan };
          const planInterval = (plan.interval || '').toLowerCase();
          const planPrice = Number(plan.price_usd || plan.price || 0);

          if (planInterval === 'yearly' || planInterval === 'year') {
            normalizedPlan.yearly_price_usd = planPrice;
            normalizedPlan.monthly_price_usd = null;
          } else {
            normalizedPlan.monthly_price_usd = planPrice;
            normalizedPlan.yearly_price_usd = null;
          }

          plansByType[typeName].plans.push(normalizedPlan);
        });

        console.log('Grouped avatar plans from addons API:', plansByType);
        setAvatarPlans(Object.values(plansByType));
      }

      setAddons(extractedAddons);
    } catch (error) {
      console.error('Error fetching addons:', error);
      toast.error('Failed to load addons');
    } finally {
      setLoadingAddons(false);
    }
  };

  const handleDownloadBackup = async () => {
    setDownloadingBackup(true);
    try {
      const blob = await usersAPI.exportBackup();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'sayagain_backup.zip';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      // toast.success('Backup download started');
    } catch (error) {
      console.error('Backup download failed:', error);
      toast.error(error.message || 'Failed to download backup');
    } finally {
      setDownloadingBackup(false);
    }
  };

  const getMemberInitials = (member) => {
    const name = (member?.name || '').trim();
    if (name) {
      const parts = name.split(/\s+/).filter(Boolean);
      const first = parts[0]?.[0] || '';
      const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] || '') : '';
      return `${first}${last}`.toUpperCase() || 'M';
    }
    const email = (member?.email || '').trim();
    return (email ? email.substring(0, 2) : 'M').toUpperCase();
  };

  // ============================================================================
  // WebSocket Chat Functions
  // ============================================================================
  //
  // How to use WebSocket connection:
  // 1. createChatSession(message) - Creates chat session and gets session_id
  // 2. connectWebSocket(sessionId) - Connects to WebSocket with session_id
  // 3. sendWebSocketMessage(message) - Sends message via WebSocket
  // 4. Messages are automatically received and displayed via onmessage handler
  //
  // Debug commands (in browser console):
  // - debugWebSocket() - Show current WebSocket status
  // - testWebSocketMessage("Hello") - Send test message
  //
  // ============================================================================

  // Check if user hit chat limit and send warning email (backend sends only when count matches threshold)
  // Pass forceSend=true to bypass count check and send email (only works when backend DEBUG=true)
  const checkChatLimitWarning = (forceSend = false) => {
    const token = localStorage.getItem('access_token');
    if (!token) return;
    const apiUrl = (configApiUrl || process.env.REACT_APP_API_URL || 'https://sayagainservices.sayagain.ai').replace(/\/$/, '');
    const url = forceSend ? `${apiUrl}/api/notifications/chat-limit-warning?force_send=true` : `${apiUrl}/api/notifications/chat-limit-warning`;
    fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
    })
      .then((r) => r.json().catch(() => null))
      .then((d) => {
        if (process.env.NODE_ENV === 'development' && d) {
          console.log('Chat limit check:', d?.data || d);
        }
      })
      .catch(() => {
        // silent — a failed limit-warning notification should never surface to the user
      });
  };

  const handleTokenLimitReached = () => {
    setIsTyping(false);
    setIsChatInputDisabled(true);
    setShowBillingModal(true);
    if (!addons || addons.length === 0) {
      fetchAddons();
    }
  };

  const handleWeeklyLimitReached = () => {
    setIsTyping(false);
    setIsChatInputDisabled(true);

    // Calculate reset date (7 days from last_reset_at, or next week if not set)
    const lastReset = tokenBalance?.last_reset_at;
    let resetDateObj;
    if (lastReset) {
      resetDateObj = new Date(lastReset);
      resetDateObj.setDate(resetDateObj.getDate() + 7);
    } else {
      resetDateObj = new Date();
      resetDateObj.setDate(resetDateObj.getDate() + 7);
    }
    const day = resetDateObj.toLocaleDateString('en-US', { weekday: 'long' });
    const time = resetDateObj.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
    setWeeklyResetDate(`${day} at ${time}`);
    setShowWeeklyLimitModal(true);
  };

  // Chat API functions
  const createChatSession = async (message) => {
    try {
      const token = localStorage.getItem('access_token');
      if (!token) {
        throw new Error('No authentication token found');
      }

      const userId = user?.id ?? user?.user_id ?? (() => {
        try {
          const stored = sessionStorage.getItem('user_data');
          if (stored) {
            const parsed = JSON.parse(stored);
            return parsed?.id ?? parsed?.user_id;
          }
        } catch (_) { }
        return null;
      })();
      if (userId == null || userId === '') {
        throw new Error('User not loaded. Please refresh the page and try again.');
      }

      const apiUrl = (configApiUrl || process.env.REACT_APP_API_URL || 'https://sayagainservices.sayagain.ai').replace(/\/$/, '');
      const body = {
        title: (message || '').trim() || 'New chat',
        user_id: userId
      };

      // Add avatar_id if avatar is selected
      const avatarId = selectedAvatar?.id || selectedAvatar?.avatar_id;
      if (avatarId) {
        body.avatar_id = Number(avatarId) || avatarId;
      }

      // Only when user has clicked on a project (group chat): send project_id and start chat
      if (selectedProject && selectedProject.id != null && selectedProject.id !== '') {
        const projectId = selectedProject.id;
        body.project_id = Number(projectId) || projectId;
        // Pass member IDs for just_us/selected chats so backend seeds project_session_members.
        // Clear after use so subsequent messages in the same session don't re-send them.
        if (pendingSessionMemberIds.length > 0) {
          body.member_ids = pendingSessionMemberIds;
          setPendingSessionMemberIds([]);
        }
      }

      const response = await fetch(`${apiUrl}/api/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(body),
      });

      let data;
      try {
        data = await response.json();
      } catch (_) {
        data = {};
      }

      const tokenBalanceFromApi = data.data?.token_balance ?? data.token_balance ?? null;

      // Update token balance from first-message response (POST /api/chat returns it; WebSocket only from 2nd message)
      if (tokenBalanceFromApi && typeof tokenBalanceFromApi === 'object') {
        setTokenBalance({
          base_tokens: tokenBalanceFromApi.base_tokens ?? 0,
          extra_tokens: tokenBalanceFromApi.extra_tokens ?? 0,
          used_tokens: tokenBalanceFromApi.used_tokens ?? 0,
          remaining_tokens: tokenBalanceFromApi.remaining_tokens ?? 0,
          total_tokens: tokenBalanceFromApi.total_tokens ?? ((tokenBalanceFromApi.base_tokens ?? 0) + (tokenBalanceFromApi.extra_tokens ?? 0)),
          usage_percentage: tokenBalanceFromApi.usage_percentage ?? 0,
          remaining_percentage: tokenBalanceFromApi.remaining_percentage ?? 0,
          total_percentage: tokenBalanceFromApi.total_percentage ?? 100,
          weekly_tokens_remaining: tokenBalanceFromApi.weekly_tokens_remaining ?? -1,
          monthly_tokens_remaining: tokenBalanceFromApi.monthly_tokens_remaining ?? -1
        });
      }

      // Backend-enforced quota check
      const msgText = String(data?.msg || data?.message || data?.error || '').toLowerCase();
      const errorCode = (data?.error_code || '').toUpperCase();

      // Check for token limit OR weekly limit (from new time-based limits)
      const isTokenLimit = msgText.includes('token limit reached');
      const isWeeklyLimit = msgText.includes('weekly') || msgText.includes('comfort') || errorCode === 'WEEKLY_LIMIT_REACHED';
      const isMonthlyLimit = msgText.includes('monthly') || errorCode === 'MONTHLY_LIMIT_REACHED';

      if ((data?.status === false || response.status === 402) && isWeeklyLimit) {
        handleWeeklyLimitReached();
        throw new Error('Weekly limit reached');
      }

      if ((data?.status === false || response.status === 402) && (isTokenLimit || isMonthlyLimit)) {
        handleTokenLimitReached();
        throw new Error('Token limit reached');
      }

      if (!response.ok) {
        const msg = data.message || data.error || data.msg || (data.detail && (Array.isArray(data.detail) ? data.detail.map(d => d.msg || d.message || JSON.stringify(d)).join(', ') : String(data.detail))) || `HTTP ${response.status}`;
        console.error('Chat API error:', response.status, data);
        const msgLower = String(msg).toLowerCase();
        if (msgLower.includes('weekly') || msgLower.includes('comfort')) {
          handleWeeklyLimitReached();
        } else if (msgLower.includes('token limit reached') || msgLower.includes('monthly')) {
          handleTokenLimitReached();
        }
        throw new Error(msg);
      }

      // Accept multiple response shapes: data.data.session_id, data.session_id, or data.data (if number)
      // Note: POST /api/chat returns only one initial AI message (msg). Full conversation history
      // is loaded via GET /api/chat/history?session_id=X when opening/selecting a session.
      const sessionId = data.data?.session_id ?? data.session_id ?? (typeof data.data === 'number' || typeof data.data === 'string' ? data.data : null);
      const senderType = data.data?.sender_type ?? data.sender_type ?? null;
      const initialMessage = data.msg ?? data.message ?? data.data?.msg ?? data.data?.initial_message ?? null;

      if (sessionId != null && sessionId !== '') {
        console.log('✅ Chat session created with ID:', sessionId);
        setChatSessionId(sessionId);
        // Only save session_id for single chat; project chat uses project_id only (avoids 400 when restore)
        if (selectedProject?.id) {
          saveSessionId(null); // Clear session_id - project chat uses project_id for history API
          saveSessionProjectId(selectedProject.id);
        } else {
          saveSessionId(sessionId);
        }
        const tokenBalanceFromApi = data.data?.token_balance ?? data.token_balance ?? null;

        return {
          sessionId: String(sessionId),
          senderType: senderType,
          initialMessage: initialMessage,
          tokenBalance: tokenBalanceFromApi
        };
      }

      console.warn('Chat API success but no session_id in response:', data);
      throw new Error('Invalid response format - no session_id');
    } catch (error) {
      console.error('Failed to create chat session:', error);
      throw error;
    }
  };

  // ============================================================================
  // Chat History API Functions
  // ============================================================================
  //
  // MODE A: Session list (single chat sidebar)
  //    GET /api/chat/history?limit=50
  //
  // MODE B: Single chat message history
  //    GET /api/chat/history?session_id=40&limit=100
  //    GET /api/chat/history?session_id=40&limit=100&cursor=...
  //
  // MODE C: Project chat history
  //    GET /api/chat/history?project_id=5&limit=100
  //    GET /api/chat/history?project_id=5&limit=100&cursor=...
  //    Response: { project_id, last_session_id, messages, next_cursor }
  //
  // ============================================================================

  // Fetch chat sessions from history API (session list)
  // When projectId is provided, fetches sessions for that project so group chat history can be loaded
  const fetchChatSessions = async (append = false, projectId = null) => {
    try {
      console.log('🔄 fetchChatSessions called, append:', append, 'projectId:', projectId);

      if (!append) {
        setChatSessionsLoading(true);
        setChatSessionsCursor(null);
      }

      const token = localStorage.getItem('access_token');
      if (!token) {
        console.warn('❌ No authentication token found for fetching chat sessions');
        return;
      }

      // Build API URL: GET /api/chat/history?limit=50 or ?limit=50&project_id=X for project
      let path = '/api/chat/history?limit=50';
      if (projectId != null && projectId !== '') {
        path += '&project_id=' + encodeURIComponent(projectId);
      } else {
        const avatarId = selectedAvatar?.id || selectedAvatar?.avatar_id;
        if (avatarId) {
          path += '&avatar_id=' + encodeURIComponent(avatarId);
        }
      }
      if (append && chatSessionsCursor) {
        path += '&cursor=' + encodeURIComponent(chatSessionsCursor);
      }

      const apiUrl = (configApiUrl || process.env.REACT_APP_API_URL || 'https://sayagainservices.sayagain.ai').replace(/\/$/, '');
      let finalUrl = `${apiUrl}${path}`;

      // Handle CORS proxy for file:// protocol
      if (configCorsProxy && configCorsProxy !== apiUrl) {
        finalUrl = configCorsProxy.replace(/\/$/, '') + '/' + finalUrl;
      }

      console.log('🌐 Fetching chat sessions from:', finalUrl.replace(token, '***'));

      const response = await fetch(finalUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        if (response.status === 404) {
          console.log('📋 No chat sessions found (404) — treating as empty');
          return;
        }
        const errorData = await response.json().catch(() => ({}));
        console.error('❌ Failed to load chat sessions:', {
          status: response.status,
          statusText: response.statusText,
          error: errorData
        });
        toast.error(`Failed to load chat sessions: ${response.status} ${response.statusText}`);
        return;
      }

      const data = await response.json();
      console.log('📋 Chat sessions API response:', data);

      // Extract sessions and cursor from response
      // Response format: { data: { sessions: [...], next_cursor: "..." } } or { sessions: [...], next_cursor: "..." }
      const sessions = (data.data && data.data.sessions) ? data.data.sessions : (data.sessions || []);
      const nextCursor = (data.data && data.data.next_cursor) ? data.data.next_cursor : (data.next_cursor || null);

      console.log('📋 Extracted sessions:', sessions.length, 'sessions');
      console.log('📋 Next cursor:', nextCursor);

      if (!Array.isArray(sessions)) {
        console.error('❌ Sessions is not an array:', sessions);
        toast.error('Invalid response format from chat history API');
        return;
      }

      if (!projectId) {
        if (append) {
          const existingIds = new Set(chatSessions.map(s => s.session_id));
          const newSessions = sessions.filter(s => !existingIds.has(s.session_id));
          setChatSessions(prev => [...prev, ...newSessions]);
          console.log(`✅ Appended ${newSessions.length} new sessions`);
        } else {
          setChatSessions(sessions);
          console.log(`✅ Set ${sessions.length} chat sessions`);
        }
        setChatSessionsCursor(nextCursor);
        setHasMoreSessions(!!nextCursor);
      }

      console.log(`✅ Loaded ${sessions.length} chat sessions (${append ? 'appended' : 'fresh'}${projectId ? ' for project' : ''})`);
      if (!append) return sessions;
    } catch (error) {
      console.error('❌ Error fetching chat sessions:', error);
      console.error('Error details:', {
        message: error.message,
        stack: error.stack,
        name: error.name
      });
      toast.error(`Failed to load chat sessions: ${error.message || 'Unknown error'}`);
    } finally {
      setChatSessionsLoading(false);
    }
    return undefined;
  };

  // Load message history for a specific session
  const loadSessionMessages = async (sessionId, append = false) => {
    try {
      if (!append) {
        setMessagesLoading(true);
        setMessages([]); // Clear current messages when loading a new session
      }

      const token = localStorage.getItem('access_token');
      if (!token) {
        console.warn('No authentication token found for loading session messages');
        return;
      }

      // Build API URL: GET /api/chat/history?session_id=40&limit=100
      let path = `/api/chat/history?session_id=${sessionId}&limit=100`;
      if (append && messagesCursor) {
        path += '&cursor=' + encodeURIComponent(messagesCursor);
      }

      const apiUrl = (configApiUrl || process.env.REACT_APP_API_URL || 'https://sayagainservices.sayagain.ai').replace(/\/$/, '');
      let finalUrl = `${apiUrl}${path}`;

      // Handle CORS proxy for file:// protocol
      if (configCorsProxy && configCorsProxy !== apiUrl) {
        finalUrl = configCorsProxy.replace(/\/$/, '') + '/' + finalUrl;
      }

      const response = await fetch(finalUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        if (response.status === 404) {
          console.log('📋 No message history found (404) — treating as empty chat');
          return;
        }
        const errorData = await response.json().catch(() => ({}));
        console.error('Failed to load session messages:', errorData);
        const msg = errorData?.message || errorData?.error || errorData?.msg;
        if (response.status === 400 && msg && String(msg).toLowerCase().includes('project_id')) {
          // Session is group chat - clear stale session_id, use project chat instead
          saveSessionId(null);
          setChatSessionId(null);
          toast.error('This is a project chat. Select it from Group chats.');
        } else {
          toast.error(msg || 'Failed to load message history');
        }
        return;
      }

      const data = await response.json();

      // Extract messages and cursor from response
      // Response format: { data: { messages: [...], next_cursor: "..." } } or { messages: [...], next_cursor: "..." }
      let messagesData = (data.data && data.data.messages) ? data.data.messages : (data.messages || []);
      const nextCursor = (data.data && data.data.next_cursor) ? data.data.next_cursor : (data.next_cursor || null);

      // Ensure single-chat history is shown in true chronological order.
      // We sort primarily by created_at ascending so older messages come first.
      // If two messages share the same timestamp (or an invalid timestamp), we ensure
      // that USER messages appear before AVATAR messages so that questions show above
      // the corresponding AI replies.
      if (!append && Array.isArray(messagesData) && messagesData.length > 1) {
        const getTs = (m) => {
          if (!m || !m.created_at) return 0;
          const t = new Date(m.created_at).getTime();
          return Number.isNaN(t) ? 0 : t;
        };
        messagesData = [...messagesData].sort((a, b) => {
          const ta = getTs(a);
          const tb = getTs(b);
          if (ta !== tb) return ta - tb;

          const sa = (a.sender_type || '').toString().toLowerCase();
          const sb = (b.sender_type || '').toString().toLowerCase();

          // For identical timestamps, put user message before avatar reply.
          if (sa === 'user' && sb === 'avatar') return -1;
          if (sa === 'avatar' && sb === 'user') return 1;

          // Stable-ish fallback: compare numeric ids if present.
          const ida = parseInt(a.id ?? a.message_id ?? '0', 10) || 0;
          const idb = parseInt(b.id ?? b.message_id ?? '0', 10) || 0;
          return ida - idb;
        });
      }

      // Convert API messages to our format
      const formattedMessages = messagesData.map(msg => {
        let rawContent = msg.message || msg.content || '';
        if (rawContent !== null && rawContent !== undefined && typeof rawContent === 'object') {
          rawContent = rawContent.message || rawContent.content || rawContent.text || rawContent.detail || JSON.stringify(rawContent);
        }
        return {
          id: msg.id || msg.message_id || Date.now() + Math.random(),
          type: msg.sender_type === 'user' ? 'user' : 'avatar',
          content: rawContent,
          time: msg.created_at ? new Date(msg.created_at).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : new Date().toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }),
          senderType: msg.sender_type === 'user' ? 'User' : (msg.sender_type || 'Avatar'),
          senderName: msg.sender_name || null,
          senderUserId: msg.sender_user_id || null,
        };
      });

      if (append) {
        // Avoid duplicates when appending
        const existingIds = new Set(messages.map(m => m.id));
        const newMessages = formattedMessages.filter(m => !existingIds.has(m.id));
        setMessages(prev => [...prev, ...newMessages]);
      } else {
        setMessages(formattedMessages);
      }

      setMessagesCursor(nextCursor);
      setHasMoreMessages(!!nextCursor);

      console.log(`✅ Loaded ${formattedMessages.length} messages for session ${sessionId} (${append ? 'appended' : 'fresh'})`);

      // Auto-scroll to bottom when loading messages
      setTimeout(() => {
        scrollToBottom();
      }, 100);

    } catch (error) {
      console.error('Error loading session messages:', error);
      toast.error('Failed to load message history');
    } finally {
      setMessagesLoading(false);
    }
  };

  // Load project chat message history (MODE C: project_id only)
  // Returns last_session_id for WebSocket reconnect
  const loadProjectMessages = async (projectId, append = false) => {
    try {
      if (!append) {
        setMessagesLoading(true);
        setMessages([]);
      }

      const token = localStorage.getItem('access_token');
      if (!token) {
        console.warn('No authentication token found for loading project messages');
        return null;
      }

      let path = `/api/chat/history?project_id=${projectId}&limit=100`;
      if (append && messagesCursor) {
        path += '&cursor=' + encodeURIComponent(messagesCursor);
      }

      const apiUrl = (configApiUrl || process.env.REACT_APP_API_URL || 'https://sayagainservices.sayagain.ai').replace(/\/$/, '');
      let finalUrl = `${apiUrl}${path}`;
      if (configCorsProxy && configCorsProxy !== apiUrl) {
        finalUrl = configCorsProxy.replace(/\/$/, '') + '/' + finalUrl;
      }

      const response = await fetch(finalUrl, {
        method: 'GET',
        headers: { 'Authorization': `Bearer ${token}` },
      });

      if (!response.ok) {
        if (response.status === 404) {
          console.log('📋 No project message history found (404) — treating as empty chat');
          return null;
        }
        const errorData = await response.json().catch(() => ({}));
        console.error('Failed to load project messages:', errorData);
        toast.error('Failed to load project chat history');
        return null;
      }

      const data = await response.json();
      let messagesData = (data.data && data.data.messages) ? data.data.messages : (data.messages || []);
      const nextCursor = (data.data && data.data.next_cursor) ? data.data.next_cursor : (data.next_cursor || null);
      const lastSessionId = (data.data && data.data.last_session_id) ?? data.last_session_id ?? 0;

      // Ensure project chat history is always in strict chronological order for all viewers.
      // We sort primarily by created_at ascending so older messages come first.
      // If two messages share the same timestamp (or an invalid timestamp), we ensure
      // that USER messages appear before AVATAR messages so that questions show above
      // the corresponding AI replies.
      if (!append && Array.isArray(messagesData) && messagesData.length > 1) {
        const getTs = (m) => {
          if (!m || !m.created_at) return 0;
          const t = new Date(m.created_at).getTime();
          return Number.isNaN(t) ? 0 : t;
        };
        messagesData = [...messagesData].sort((a, b) => {
          const ta = getTs(a);
          const tb = getTs(b);
          if (ta !== tb) return ta - tb;

          const sa = (a.sender_type || '').toString().toLowerCase();
          const sb = (b.sender_type || '').toString().toLowerCase();

          // For identical timestamps, put user message before avatar reply.
          if (sa === 'user' && sb === 'avatar') return -1;
          if (sa === 'avatar' && sb === 'user') return 1;

          // Stable-ish fallback: compare numeric ids if present.
          const ida = parseInt(a.id ?? a.message_id ?? '0', 10) || 0;
          const idb = parseInt(b.id ?? b.message_id ?? '0', 10) || 0;
          return ida - idb;
        });
      }

      // Convert API messages to our format
      const formattedMessages = messagesData.map(msg => {
        let rawContent = msg.message || msg.content || '';
        if (rawContent !== null && rawContent !== undefined && typeof rawContent === 'object') {
          rawContent = rawContent.message || rawContent.content || rawContent.text || rawContent.detail || JSON.stringify(rawContent);
        }
        return {
          id: msg.id || msg.message_id || Date.now() + Math.random(),
          type: msg.sender_type === 'user' ? 'user' : 'avatar',
          content: rawContent,
          time: msg.created_at ? new Date(msg.created_at).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : new Date().toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }),
          senderType: msg.sender_type === 'user' ? 'User' : (msg.sender_type || 'Avatar'),
          senderName: msg.sender_name || null,
          senderUserId: msg.sender_user_id || null,
        };
      });

      if (append) {
        const existingIds = new Set(messages.map(m => m.id));
        const newMessages = formattedMessages.filter(m => !existingIds.has(m.id));
        setMessages(prev => [...prev, ...newMessages]);
      } else {
        setMessages(formattedMessages);
      }

      setMessagesCursor(nextCursor);
      setHasMoreMessages(!!nextCursor);

      if (lastSessionId && lastSessionId > 0) {
        setChatSessionId(lastSessionId);
        saveSessionId(lastSessionId);
      }

      console.log(`✅ Loaded ${formattedMessages.length} project messages (last_session_id: ${lastSessionId})`);

      setTimeout(() => scrollToBottom(), 100);
      return lastSessionId;
    } catch (error) {
      console.error('Error loading project messages:', error);
      toast.error('Failed to load project chat history');
      return null;
    } finally {
      setMessagesLoading(false);
    }
  };

  // Load more messages for current session (pagination)
  const loadMoreMessages = () => {
    if (!hasMoreMessages || messagesLoading) return;
    const projectId = selectedProject?.id || localStorage.getItem('chat_session_project_id');
    if (projectId) {
      loadProjectMessages(projectId, true);
    } else if (chatSessionId) {
      loadSessionMessages(chatSessionId, true);
    }
  };

  // Filter sessions based on search query
  const getFilteredSessions = () => {
    if (!chatSearchQuery.trim()) return chatSessions;

    const query = chatSearchQuery.toLowerCase().trim();
    return chatSessions.filter(session =>
      (session.title || '').toLowerCase().includes(query)
    );
  };

  // Delete a chat session (history) - DELETE /api/chat/session with body { session_id, user_id }
  const deleteChatSession = async (sessionId, e) => {
    if (e) e.stopPropagation();
    if (!sessionId || deletingSessionId) return;
    const token = localStorage.getItem('access_token');
    if (!token) {
      toast.error('Please sign in to delete chat history');
      return;
    }
    const userId = user?.id ?? user?.user_id ?? (() => {
      try {
        const stored = sessionStorage.getItem('user_data');
        if (stored) {
          const parsed = JSON.parse(stored);
          return parsed?.id ?? parsed?.user_id;
        }
      } catch (_) { }
      return null;
    })();
    if (userId == null || userId === '') {
      toast.error('User not loaded. Please refresh and try again.');
      return;
    }
    setDeletingSessionId(sessionId);
    const apiUrl = (configApiUrl || process.env.REACT_APP_API_URL || 'https://sayagainservices.sayagain.ai').replace(/\/$/, '');
    try {
      const response = await fetch(`${apiUrl}/api/chat/session`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          session_id: Number(sessionId) || sessionId,
          user_id: Number(userId) || userId,
        }),
      });
      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.msg || errData.message || errData.error || `Delete failed: ${response.status}`);
      }
      setChatSessions(prev => prev.filter(s => s.session_id !== sessionId));
      if (String(chatSessionId) === String(sessionId)) {
        setChatSessionId(null);
        setMessages([]);
        localStorage.removeItem('chat_session_id');
        localStorage.removeItem('chat_messages');
      }
    } catch (err) {
      console.error('Delete chat session error:', err);
      toast.error(err.message || 'Failed to delete chat');
    } finally {
      setDeletingSessionId(null);
    }
  };

  // Update chat session title - PUT /api/chat/single/title
  const updateChatSessionTitle = async (sessionId, newTitle) => {
    if (!sessionId || !newTitle?.trim()) {
      toast.error('Please enter a valid chat name');
      return;
    }

    const token = localStorage.getItem('access_token');
    if (!token) {
      toast.error('Please sign in to update chat name');
      return;
    }

    const avatarId = selectedAvatar?.id;
    if (!avatarId) {
      toast.error('Avatar not selected');
      return;
    }

    setUpdatingSessionTitle(true);
    const apiUrl = (configApiUrl || process.env.REACT_APP_API_URL || 'https://sayagainservices.sayagain.ai').replace(/\/$/, '');

    try {
      const response = await fetch(`${apiUrl}/api/chat/single/title`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          avatar_id: Number(avatarId),
          session_id: Number(sessionId),
          title: newTitle.trim(),
        }),
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.msg || errData.message || errData.error || `Update failed: ${response.status}`);
      }

      // Update the session in the list
      setChatSessions(prev =>
        prev.map(s =>
          s.session_id === sessionId
            ? { ...s, title: newTitle.trim() }
            : s
        )
      );

      setEditingSessionId(null);
      setEditingSessionTitle('');
    } catch (err) {
      console.error('Update chat session title error:', err);
      toast.error(err.message || 'Failed to update chat name');
    } finally {
      setUpdatingSessionTitle(false);
    }
  };

  // Open session by ID
  const openSessionById = (sessionId) => {
    if (!sessionId || sessionId < 1) {
      toast.error('Please enter a valid session ID');
      return;
    }

    // Check if session already exists
    const existingSession = chatSessions.find(s => s.session_id === sessionId);
    const title = existingSession ? (existingSession.title || 'Untitled') : ('Chat #' + sessionId);
    const isGroup = existingSession ? !!existingSession.is_group_chat : false;

    // If not in sessions list, add it
    if (!existingSession) {
      const newSession = {
        session_id: sessionId,
        title: title,
        is_group_chat: isGroup,
        updated_at: new Date().toISOString()
      };
      setChatSessions(prev => [newSession, ...prev]);
    }

    // Set as current session and load messages
    setChatSessionId(sessionId);
    setChatSessionKey(prev => prev + 1);
    saveSessionId(sessionId);

    // Clear project ID state when switching from group to single chat
    let newProjectId = undefined;
    if (!isGroup) {
      saveSessionProjectId(null);
      setSelectedProject(null);
      newProjectId = null;
    } else if (existingSession?.project_id) {
      saveSessionProjectId(existingSession.project_id);
      newProjectId = existingSession.project_id;
    }

    loadSessionMessages(sessionId); // Load message history
    connectWebSocket(sessionId, newProjectId);

  };

  const connectWebSocket = (sessionId, explicitProjectId = undefined) => {
    if (!sessionId) {
      console.error('No session ID provided for WebSocket connection');
      return;
    }

    // Rate limiting: prevent connection attempts more frequent than every 2 seconds
    const now = Date.now();
    const timeSinceLastAttempt = now - lastConnectionAttemptRef.current;
    if (timeSinceLastAttempt < 2000) {
      console.warn(`⚠️ Connection attempt too soon (${timeSinceLastAttempt}ms since last attempt). Delaying...`);
      setTimeout(() => connectWebSocket(sessionId), 2000 - timeSinceLastAttempt);
      return;
    }
    lastConnectionAttemptRef.current = now;

    const token = localStorage.getItem('access_token');
    if (!token) {
      console.error('❌ No authentication token found for WebSocket connection');
      return;
    }

    // Validate token format and expiration
    try {
      const tokenParts = token.split('.');
      if (tokenParts.length !== 3) {
        console.error('❌ Invalid JWT token format');
        return;
      }

      const payload = JSON.parse(atob(tokenParts[1]));
      const currentTime = Math.floor(Date.now() / 1000);
      console.log('🔐 Token validation:', {
        user_id: payload.sub,
        email: payload.email,
        expires_at: new Date(payload.exp * 1000).toISOString(),
        current_time: new Date(currentTime * 1000).toISOString(),
        is_expired: payload.exp < currentTime,
        time_remaining: payload.exp - currentTime
      });

      if (payload.exp < currentTime) {
        console.error('❌ JWT token has expired');
        toast.error('Session expired. Please login again.');
        return;
      }
    } catch (error) {
      console.error('❌ Error validating JWT token:', error);
      return;
    }

    const apiUrl = (configApiUrl || process.env.REACT_APP_API_URL || 'https://sayagainservices.sayagain.ai').replace(/\/$/, '');
    let wsUrl = apiUrl.replace(/^http/, 'ws') + '/ws/chat/' + sessionId + '?token=' + encodeURIComponent(token);

    // Add avatar_id if avatar is selected
    const avatarId = selectedAvatar?.id || selectedAvatar?.avatar_id;
    if (avatarId) {
      wsUrl += '&avatar_id=' + encodeURIComponent(String(avatarId));
    }

    let projectId = explicitProjectId !== undefined ? explicitProjectId : localStorage.getItem('chat_session_project_id');
    // Only fallback to selectedProject if we didn't explicitly pass null and localStorage is empty
    if (projectId === undefined || projectId === null) {
      projectId = explicitProjectId === undefined ? selectedProject?.id : null;
    }

    // Just Us chats are human-only — skip WebSocket entirely (no avatar backend)
    if (projectId && isJustUsProject(projectId)) {
      console.log('🚫 Just Us project — skipping WebSocket connection entirely');
      return;
    }

    if (projectId) {
      wsUrl += '&project_id=' + encodeURIComponent(String(projectId));
    }

    // Prevent multiple simultaneous connection attempts for the SAME session
    if (wsConnection && (wsConnection.readyState === WebSocket.CONNECTING || wsConnection.readyState === WebSocket.OPEN)) {
      // Create a URL comparison that ignores the token parameter safely, or just compare exact URLs
      if (wsConnection.url === wsUrl) {
        console.log('WebSocket already connecting or connected to this target, skipping new connection attempt');
        return;
      }
      console.log('WebSocket is connected to a different target, closing existing connection');
      wsConnection.close();
      setWsConnection(null);
      setIsWsConnected(false);
    }

    // Close existing connection if any
    if (wsConnection && wsConnection.readyState !== WebSocket.CLOSED) {
      console.log('Closing existing WebSocket connection before creating new one');
      wsConnection.close();
      setWsConnection(null);
      setIsWsConnected(false);
    }

    wsConnectionCountRef.current += 1;
    const connectionId = wsConnectionCountRef.current;



    console.log(`🔌 WebSocket Connection #${connectionId} - Connecting to:`, wsUrl.replace(token, '***'));
    console.log(`🔌 Connection #${connectionId} - Session ID:`, sessionId, projectId ? `Project: ${projectId}` : '(single chat)');

    let connectionTimeout; // Declare outside try block for cleanup in catch

    try {
      const ws = new WebSocket(wsUrl);

      // Add connection timeout after WebSocket is created
      connectionTimeout = setTimeout(() => {
        if (ws.readyState === WebSocket.CONNECTING) {
          console.warn('⚠️ WebSocket connection timeout - closing connection attempt');
          ws.close();
        }
      }, 10000); // 10 second timeout
      currentWsRef.current = ws; // Store in ref immediately for immediate checks
      setWsConnection(ws);

      ws.onopen = () => {
        clearTimeout(connectionTimeout); // Clear the connection timeout
        console.log('✅ WebSocket connected successfully to:', ws.url.replace(/token=[^&]*/, 'token=***'));
        console.log('🔧 Connection details:', {
          connectionId,
          sessionId,
          readyState: ws.readyState,
          protocol: ws.protocol,
          extensions: ws.extensions
        });
        console.log('🔧 Setting isWsConnected to true');
        setIsWsConnected(true);
        setWsConnection(ws); // Set connection here when it's actually open
        currentWsRef.current = ws; // Ensure ref is updated
      };

      ws.onmessage = (event) => {
        console.log('📨 WebSocket message received:', event.data);

        try {
          let messageContent = '';
          let senderType = null;

          // Try to parse as JSON first
          if (typeof event.data === 'string') {
            try {
              const parsed = JSON.parse(event.data);
              console.log('📋 Parsed WebSocket data:', parsed);

              // Heartbeat: reply to server ping with pong (keep connection alive behind proxies)
              if (parsed && parsed.type === 'ping') {
                try {
                  ws.send(JSON.stringify({ type: 'pong', ts: parsed.ts }));
                } catch (e) {
                  console.warn('Failed to send pong:', e);
                }
                return;
              }
              if (parsed && parsed.type === 'pong') {
                return; // ignore server pong (e.g. response to our ping)
              }

              // Extract message content - check multiple possible fields
              messageContent = parsed.message || parsed.content || parsed.text || parsed.msg || parsed.response;

              // If the extracted value is a non-string object, drill into it for the text
              if (messageContent !== null && messageContent !== undefined && typeof messageContent === 'object') {
                messageContent = messageContent.message || messageContent.content || messageContent.text || messageContent.detail || JSON.stringify(messageContent);
              }

              // Extract senderType from response
              senderType = parsed.sender_type || parsed.senderType || parsed.type || null;
              console.log('👤 Sender type from WebSocket:', senderType);

              // Update token balance from WebSocket response if present
              if (parsed.token_balance && typeof parsed.token_balance === 'object') {
                const balanceUserId = parsed.token_balance_user_id;
                const currentUserId = user?.id || user?.user_id;

                // Only update if no balance user ID is provided (backward compatibility)
                // or if it matches the current logged-in user.
                if (!balanceUserId || String(balanceUserId) === String(currentUserId)) {
                  const tokenBalanceData = parsed.token_balance;
                  const baseTokens = tokenBalanceData.base_tokens || 0;
                  const extraTokens = tokenBalanceData.extra_tokens || 0;
                  const usedTokens = tokenBalanceData.used_tokens || 0;
                  // Prioritize remaining_tokens from response, calculate if not provided
                  const remainingTokens = tokenBalanceData.remaining_tokens !== undefined
                    ? tokenBalanceData.remaining_tokens
                    : Math.max(0, baseTokens - usedTokens);

                  setTokenBalance({
                    base_tokens: baseTokens,
                    extra_tokens: extraTokens,
                    used_tokens: usedTokens,
                    remaining_tokens: remainingTokens,
                    total_tokens: baseTokens + extraTokens,
                    usage_percentage: tokenBalanceData.usage_percentage || 0,
                    weekly_tokens_remaining: tokenBalanceData.weekly_tokens_remaining ?? -1,
                    monthly_tokens_remaining: tokenBalanceData.monthly_tokens_remaining ?? -1
                  });
                  console.log('💰 Token balance updated from WebSocket:', {
                    remaining_tokens: remainingTokens,
                    base_tokens: baseTokens,
                    used_tokens: usedTokens
                  });
                } else {
                  console.log(`⏭️ Skipped token balance update for user ${balanceUserId} (current user: ${currentUserId})`);
                }
              }

              if (!messageContent && parsed.error) {
                const errStr = typeof parsed.error === 'string'
                  ? parsed.error
                  : (parsed.error?.detail || parsed.error?.message || JSON.stringify(parsed.error));
                messageContent = `Error: ${errStr}`;
                toast.error(errStr, { toastId: 'ws-error' });
              }

              // If still no content, use the raw data
              if (!messageContent) {
                messageContent = typeof parsed === 'string' ? parsed : JSON.stringify(parsed);
              }
            } catch (parseError) {
              // If JSON parsing fails, treat as plain text
              console.log('🔤 Treating WebSocket data as plain text');
              messageContent = event.data;
            }
          } else {
            // If data is not a string, convert to string
            messageContent = String(event.data);
          }

          console.log('💬 Final message content:', messageContent);

          if (messageContent && messageContent.trim()) {
            const trimmedContent = messageContent.trim();
            const now = Date.now();
            const parsed = (typeof event.data === 'string' && event.data) ? (() => { try { return JSON.parse(event.data); } catch { return {}; } })() : {};

            // sender_type from backend: "user" | "avatar" (case-insensitive)
            const st = (parsed.sender_type || parsed.senderType || '').toString().toLowerCase();
            const isUserMessage = st === 'user';
            const msgType = isUserMessage ? 'user' : 'avatar';
            const messageId = parsed.id ?? null;

            // Project chat should allow duplicate USER messages (same content is valid).
            // Single chat must keep existing dedupe behavior.
            const isProjectChatContext = !!localStorage.getItem('chat_session_project_id');
            const bypassContentDedupe = isProjectChatContext && isUserMessage;

            // Avoid duplicate: same content + same type within 3 seconds
            if (!bypassContentDedupe) {
              const lastMsg = lastWebSocketMessageRef.current;
              if (lastMsg.content === trimmedContent && (now - lastMsg.timestamp) < 3000) {
                setIsTyping(false);
                return;
              }
              lastWebSocketMessageRef.current = { content: trimmedContent, timestamp: now };
            }

            setMessages(prev => {
              // Always dedupe by backend message id when present (prevents true double-delivery).
              if (messageId != null && prev.some(m => String(m.id) === String(messageId))) {
                return prev;
              }

              if (!bypassContentDedupe) {
                const recent = prev.slice(-5);
                if (recent.some(m => m.type === msgType && m.content === trimmedContent)) {
                  return prev;
                }
              }
              const newMsg = {
                id: messageId || now + Math.random(),
                type: msgType,
                content: trimmedContent,
                time: new Date().toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }),
                senderType: senderType || (isUserMessage ? 'User' : 'Avatar'),
                senderName: parsed.sender_name || null,
                senderUserId: parsed.sender_user_id || null
              };
              if (!isUserMessage && parsed.avatar?.avatar_id) {
                activeGroupAvatarIdRef.current = parsed.avatar.avatar_id;
              }

              // For group chat user echo: replace the optimistic placeholder rather than append.
              const pendingId = pendingGroupOptimisticIdRef.current;
              if (isUserMessage && isProjectChatContext && pendingId) {
                pendingGroupOptimisticIdRef.current = null;
                return prev.map(m => m.id === pendingId ? newMsg : m);
              }

              return [...prev, newMsg];
            });
            setIsTyping(false);

            // Fetch token balance to trigger 90%/100% limit popups without needing a refresh
            // Specifically fetch it after an avatar message as those consume tokens
            if (!isUserMessage) {
              fetchTokenBalance();
            }
          }
        } catch (error) {
          console.error('❌ Error processing WebSocket message:', error);
          // Fallback: show raw message
          const now = Date.now();
          const fallbackContent = `Received: ${event.data}`;

          // Check if this is a duplicate of the last WebSocket message
          const lastMsg = lastWebSocketMessageRef.current;
          const isDuplicate =
            lastMsg.content === fallbackContent &&
            (now - lastMsg.timestamp) < 3000;

          if (isDuplicate) {
            console.log('⚠️ Duplicate fallback message detected, skipping');
            setIsTyping(false);
            return;
          }

          // Update the last message ref
          lastWebSocketMessageRef.current = {
            content: fallbackContent,
            timestamp: now
          };

          const avatarMessage = {
            id: now + Math.random(),
            type: 'avatar',
            content: fallbackContent,
            time: new Date().toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
          };

          // Check for duplicates in messages array
          setMessages(prev => {
            const recentMessages = prev.slice(-5);
            const existsInMessages = recentMessages.some(msg =>
              msg.type === 'avatar' &&
              msg.content === fallbackContent
            );

            if (existsInMessages) {
              console.log('⚠️ Fallback message already exists in messages array, skipping');
              return prev;
            }

            return [...prev, avatarMessage];
          });

          setIsTyping(false);
        }
      };

      ws.onerror = (error) => {
        clearTimeout(connectionTimeout);
        console.error('❌ WebSocket error:', {
          error,
          connectionId,
          sessionId,
          url: ws.url.replace(/token=[^&]*/, 'token=***'),
          readyState: ws.readyState,
          timestamp: new Date().toISOString()
        });
        setIsWsConnected(false);
      };

      ws.onclose = (event) => {
        clearTimeout(connectionTimeout);
        console.log('🔌 WebSocket closed:', {
          code: event.code,
          reason: event.reason || 'No reason provided',
          wasClean: event.wasClean,
          url: ws.url.replace(/token=[^&]*/, 'token=***'),
          connectionId,
          sessionId,
          timestamp: new Date().toISOString()
        });

        // Log specific issues based on close code
        if (event.code === 1005) {
          console.warn('⚠️ WebSocket closed with code 1005 (No Status Received) - This usually indicates server-side issues or authentication problems');
        } else if (event.code === 1006) {
          console.warn('⚠️ WebSocket closed with code 1006 (Abnormal Closure) - Connection lost unexpectedly');
        } else if (event.code === 1008) {
          console.warn('⚠️ WebSocket closed with code 1008 (Policy Violation) - Check authentication or server policy');
        } else if (event.code === 1011) {
          console.warn('⚠️ WebSocket closed with code 1011 (Internal Server Error) - Server error occurred');
        } else if (event.code === 1000) {
          console.log('✅ WebSocket closed normally (code 1000)');
        }

        setIsWsConnected(false);
        setWsConnection(null);
        currentWsRef.current = null;
      };

    } catch (error) {
      clearTimeout(connectionTimeout);
      console.error('❌ Failed to create WebSocket connection:', error);
      setIsWsConnected(false);
      setWsConnection(null);
      currentWsRef.current = null;
    }
  };

  const sendWebSocketMessage = (message) => {
    const wsToUse = wsConnection || currentWsRef.current;
    if (!wsToUse) {
      console.error('❌ No WebSocket connection available');
      return false;
    }

    if (wsToUse.readyState !== WebSocket.OPEN) {
      console.error('❌ WebSocket not in OPEN state. Current state:', wsToUse.readyState);
      console.log('WebSocket states: CONNECTING=0, OPEN=1, CLOSING=2, CLOSED=3');
      console.log('isWsConnected state:', isWsConnected);
      return false;
    }

    if (!message || !message.trim()) {
      console.error('❌ Cannot send empty message');
      return false;
    }

    try {
      // In group chat, detect which avatar the message is addressing and use its ID.
      // Fall back to selectedAvatar for single-avatar chats or when no match is found.
      let avatarId = selectedAvatar?.id || selectedAvatar?.avatar_id;
      if (selectedProject) {
        const groupAvatars = avatars && avatars.length > 0 ? avatars : (selectedAvatar ? [selectedAvatar] : []);
        const addressed = detectAddressedAvatar(message, groupAvatars);
        if (addressed) {
          avatarId = addressed.id || addressed.avatar_id;
        } else if (activeGroupAvatarIdRef.current) {
          avatarId = activeGroupAvatarIdRef.current;
        }
      }

      const payloadObj = {
        message: message.trim(),
        session_id: chatSessionId
      };

      if (avatarId) {
        payloadObj.avatar_id = Number(avatarId) || avatarId;
      }

      const payload = JSON.stringify(payloadObj);
      console.log('📤 Sending WebSocket message:', payload);
      wsToUse.send(payload);
      console.log('✅ WebSocket message sent successfully');
      return true;
    } catch (error) {
      console.error('❌ Error sending WebSocket message:', error);
      return false;
    }
  };

  // Helper function to get WebSocket connection status
  const getWebSocketStatus = () => {
    if (!wsConnection) return 'Not connected';
    switch (wsConnection.readyState) {
      case WebSocket.CONNECTING: return 'Connecting';
      case WebSocket.OPEN: return 'Connected';
      case WebSocket.CLOSING: return 'Closing';
      case WebSocket.CLOSED: return 'Closed';
      default: return 'Unknown';
    }
  };

  // Debug function to log WebSocket status
  const debugWebSocketStatus = () => {
    console.log('🔍 WebSocket Debug Info:', {
      connection: wsConnection ? 'exists' : 'null',
      currentWsRef: currentWsRef.current ? 'exists' : 'null',
      readyState: wsConnection ? wsConnection.readyState : 'N/A',
      refReadyState: currentWsRef.current ? currentWsRef.current.readyState : 'N/A',
      status: getWebSocketStatus(),
      isWsConnected,
      chatSessionId,
      messagesCount: messages.length,
      apiUrl: process.env.REACT_APP_API_URL || 'https://sayagainservices.sayagain.ai',
      hasToken: !!localStorage.getItem('access_token'),
      // Persistence info
      savedSessionId: localStorage.getItem('chat_session_id'),
      savedMessagesCount: (() => {
        try {
          const saved = localStorage.getItem('chat_messages');
          return saved ? JSON.parse(saved).length : 0;
        } catch { return 0; }
      })()
    });

    // Also log to browser console for easier debugging
    if (typeof window !== 'undefined' && window.console) {
      console.group('🚀 WebSocket & Chat Status Report');
      console.log('WebSocket Status:', getWebSocketStatus());
      console.log('WebSocket Connected:', isWsConnected);
      console.log('Session ID:', chatSessionId);
      console.log('Current Messages:', messages.length);
      console.log('API URL:', process.env.REACT_APP_API_URL || 'https://sayagainservices.sayagain.ai');
      console.log('--- Persistence ---');
      console.log('Saved Session ID:', localStorage.getItem('chat_session_id'));
      console.log('Saved Messages:', (() => {
        try {
          const saved = localStorage.getItem('chat_messages');
          return saved ? JSON.parse(saved).length : 0;
        } catch { return 0; }
      })());
      console.groupEnd();
    }
  };

  // Save session ID to localStorage
  const saveSessionId = (sessionId) => {
    if (sessionId) {
      localStorage.setItem('chat_session_id', sessionId);
      console.log('💾 Saved chat session ID:', sessionId);
    } else {
      localStorage.removeItem('chat_session_id');
      // Do not auto-clear project ID here to prevent loss of context on refresh/error
      // saveSessionProjectId(null); 
      console.log('🗑️ Cleared chat session ID');
    }
  };

  // Save project ID for current chat session (group/project chat)
  const saveSessionProjectId = (projectId) => {
    if (projectId) {
      localStorage.setItem('chat_session_project_id', String(projectId));
    } else {
      localStorage.removeItem('chat_session_project_id');
    }
  };

  // Save messages to localStorage
  const saveMessages = (messages) => {
    try {
      localStorage.setItem('chat_messages', JSON.stringify(messages));
      console.log('💾 Saved chat messages:', messages.length);
    } catch (error) {
      console.error('❌ Failed to save messages to localStorage:', error);
    }
  };

  // Function to clear session ID (like index.html clear chat)
  const clearSession = () => {
    console.log('🗑️ Clearing session ID and disconnecting WebSocket');
    setChatSessionId(null);
    setManualSessionId('');
    setIsWsConnected(false);
    if (currentWsRef.current) {
      currentWsRef.current.close();
      currentWsRef.current = null;
    }
    setWsConnection(null);

    // Reset last WebSocket message ref to prevent duplicate detection issues
    lastWebSocketMessageRef.current = { content: '', timestamp: 0 };

    localStorage.removeItem('chat_session_id');
    saveSessionProjectId(null);

  };

  // Function to clear chat and reset session
  const clearChat = () => {
    console.log('🗑️ Clearing chat messages and resetting session');
    setMessages([]);
    setChatSessionId(null);
    setManualSessionId('');
    setIsWsConnected(false);
    if (currentWsRef.current) {
      currentWsRef.current.close();
      currentWsRef.current = null;
    }
    setWsConnection(null);

    // Reset last WebSocket message ref to prevent duplicate detection issues
    lastWebSocketMessageRef.current = { content: '', timestamp: 0 };

    // Clear from localStorage
    localStorage.removeItem('chat_session_id');
    localStorage.removeItem('chat_messages');

  };

  // Global debug and utility functions for browser console (like index.html)
  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.debugWebSocket = debugWebSocketStatus;
      window.testWebSocketMessage = (message) => {
        console.log('🧪 Testing WebSocket message send:', message);
        return sendWebSocketMessage(message);
      };
      window.checkChatLimitWarning = checkChatLimitWarning;
      window.testChatLimitEmail = () => {
        console.log('🧪 Sending chat limit warning email (force_send=true, requires backend DEBUG=true)');
        checkChatLimitWarning(true);
      };
      window.clearChat = clearChat;
      window.clearSession = clearSession;
      window.setSessionId = (sessionId) => {
        console.log('🔧 Manually setting session ID:', sessionId);
        const id = parseInt(sessionId, 10);
        if (id > 0) {
          setChatSessionId(id);
          saveSessionId(id);
          connectWebSocket(id);
          return true;
        }
        return false;
      };
      window.checkSavedMessages = () => {
        const saved = localStorage.getItem('chat_messages');
        console.log('💾 Saved messages in localStorage:', saved);
        if (saved) {
          try {
            const parsed = JSON.parse(saved);
            console.log('📋 Parsed saved messages:', parsed);
            console.log('📊 Message count:', parsed.length);
          } catch (e) {
            console.error('❌ Error parsing saved messages:', e);
          }
        }
        return saved;
      };
      window.forceRestoreMessages = () => {
        const saved = localStorage.getItem('chat_messages');
        if (saved) {
          try {
            const parsed = JSON.parse(saved);
            console.log('🔄 Force restoring messages:', parsed.length);
            setMessages(parsed);
            return true;
          } catch (e) {
            console.error('❌ Error restoring messages:', e);
            return false;
          }
        }
        console.log('📭 No saved messages to restore');
        return false;
      };
      window.checkAvatarState = () => {
        console.log('🎭 Avatar State:', {
          selectedAvatar: selectedAvatar?.id,
          previousAvatarIdRef: previousAvatarIdRef.current,
          initialLoad: initialLoadRef.current,
          currentMessages: messages.length,
          chatSessionId: chatSessionId
        });
      };
      window.forceReconnect = () => {
        console.log('🔌 Force reconnecting WebSocket...');
        if (chatSessionId) {
          connectWebSocket(chatSessionId);
        } else {
          console.log('❌ No session ID available for reconnection');
        }
      };
    }

    return () => {
      if (typeof window !== 'undefined') {
        delete window.debugWebSocket;
        delete window.testWebSocketMessage;
        delete window.clearChat;
        delete window.clearSession;
        delete window.setSessionId;
      }
    };
  }, []);

  // Cleanup WebSocket on component unmount
  useEffect(() => {
    return () => {
      if (currentWsRef.current && currentWsRef.current.readyState !== WebSocket.CLOSED) {
        console.log('🧹 Cleaning up WebSocket connection on component unmount');
        currentWsRef.current.close();
        currentWsRef.current = null;
      }
    };
  }, []);


  // Handle Addon Payment (PayPal)
  const handleAddonPayment = async (addon) => {
    if (!addon || !addon.id) {
      toast.error('Invalid upgrade plan selected');
      return;
    }

    // Open blank popup NOW — synchronous, inside the user gesture — before any await.
    // Browsers only honour window.open() without blocking when called directly from a click.
    const popupWidth = 520;
    const popupHeight = 700;
    const left = Math.round(window.screenX + (window.outerWidth - popupWidth) / 2);
    const top = Math.round(window.screenY + (window.outerHeight - popupHeight) / 2);
    const upgradePopup = window.open(
      'about:blank',
      'PayPalUpgrade',
      `width=${popupWidth},height=${popupHeight},left=${left},top=${top},scrollbars=yes,resizable=yes`
    );
    const popupBlocked = !upgradePopup || upgradePopup.closed || typeof upgradePopup.closed === 'undefined';

    try {
      // Removed toast as per user request
      // toast.info(`Initiating PayPal payment for ${addon.name || 'upgrade'}...`, { autoClose: 2000 });

      // Store the addon ID for the capture step
      localStorage.setItem('pending_addon_plan_id', addon.id);

      // Redirect to a dedicated success page for addons
      const baseUrl = window.location.origin;
      const successUrl = `${baseUrl}/payment/addon/success`;
      const currentUrl = window.location.href.split('?')[0];

      const response = await plansAPI.createAddonPayPalOrder({
        addon_plan_id: addon.id,
        return_url: successUrl,
        cancel_url: currentUrl
      });
      console.log('PayPal create order response:', response);

      // Check for approval URL (standard PayPal flow)
      // Expecting output like { id: "ORD-...", links: [ { href: "...", rel: "approve" } ] }
      // Or simplified { approve_url: "..." }

      let approveUrl = null;

      if (response?.approve_url) {
        approveUrl = response.approve_url;
      } else if (response?.data?.approve_url) {
        approveUrl = response.data.approve_url;
      } else if (response?.links && Array.isArray(response.links)) {
        const link = response.links.find(l => l.rel === 'approve');
        if (link) approveUrl = link.href;
      } else if (response?.data?.links && Array.isArray(response.data.links)) {
        const link = response.data.links.find(l => l.rel === 'approve');
        if (link) approveUrl = link.href;
      }

      if (approveUrl) {
        setShowBillingModal(false);

        if (popupBlocked || upgradePopup.closed) {
          // Popup was blocked even for about:blank — fall back to full-page redirect
          window.location.href = approveUrl;
          return;
        }

        // Navigate the already-open blank popup to the real PayPal URL
        upgradePopup.location.href = approveUrl;

        const upgradeMessageHandler = (event) => {
          if (event.data?.type === 'PAYPAL_SUCCESS' && event.data?.context === 'addon') {
            window.removeEventListener('message', upgradeMessageHandler);
            clearInterval(upgradePollTimer);
            setAddonConfirmed(true);
            setTimeout(() => setAddonConfirmed(false), 5000);
            fetchUserAndAvatarData();
          } else if (event.data?.type === 'PAYPAL_ERROR' && event.data?.context === 'addon') {
            window.removeEventListener('message', upgradeMessageHandler);
            clearInterval(upgradePollTimer);
            toast.error('Payment failed. Please try again.');
          }
        };
        const upgradePollTimer = setInterval(() => {
          if (upgradePopup.closed) {
            window.removeEventListener('message', upgradeMessageHandler);
            clearInterval(upgradePollTimer);
          }
        }, 600);
        window.addEventListener('message', upgradeMessageHandler);

      } else {
        if (!popupBlocked) upgradePopup.close();
        const msg = (typeof response?.msg === 'string' ? response.msg : null) ||
          (typeof response?.message === 'string' ? response.message : null) ||
          'No approval URL found';
        console.error('No approval URL found in PayPal response:', response);
        toast.error(`Failed to initiate payment: ${msg}`);
      }
    } catch (error) {
      if (!popupBlocked) upgradePopup.close();
      console.error('Payment error:', error);
      const msg = error.message || error.msg || 'Payment initialization failed';
      toast.error(msg);
    }
  };



  // Fetch projects (real API) - uses /api/projects/my-projects
  const fetchProjects = async () => {
    try {
      setLoadingProjects(true);

      // Wait for user data if not available yet
      let currentUser = user;
      if (!currentUser) {
        // Try to get from sessionStorage
        const storedUserData = sessionStorage.getItem('user_data');
        if (storedUserData) {
          try {
            currentUser = JSON.parse(storedUserData);
          } catch (e) {
            console.warn('Could not parse user data from sessionStorage');
          }
        }
      }

      const userIdForCache = currentUser?.id || currentUser?.user_id;

      // Call the new /api/projects/my-projects endpoint
      // This returns all projects where user is creator OR member
      const response = await projectsAPI.getMyProjects();

      // Handle different response shapes
      const rawProjects =
        response?.data?.projects ||
        response?.data ||
        response?.projects ||
        response?.result ||
        (Array.isArray(response) ? response : []);

      if (!Array.isArray(rawProjects)) {
        console.warn('Unexpected projects response format:', response);
        return;
      }

      // Normalize projects data
      const normalizedProjects = rawProjects
        .map((p) => {
          if (!p) return null;

          const projectObj = p.project || p;
          const id = projectObj?.id || projectObj?.project_id || p?.id || p?.project_id;
          const name = projectObj?.name || projectObj?.project_name || p?.name || 'Untitled Project';
          const description = projectObj?.description || projectObj?.project_description || p?.description || '';
          const createdAt = (projectObj?.created_at || projectObj?.created_date || projectObj?.createdAt || p?.created_at || p?.created_date || '').toString().slice(0, 10) || '';

          // Extract members from project or from separate members array
          const membersRaw =
            p?.members ||
            p?.project_members ||
            projectObj?.members ||
            projectObj?.users ||
            projectObj?.project_members ||
            [];

          const members = Array.isArray(membersRaw) ? membersRaw : [];

          const createdUserId = projectObj?.created_user ?? projectObj?.createdUser ?? p?.created_user ?? p?.createdUser ?? null;
          return {
            id,
            name,
            description,
            created_user: createdUserId,
            members: members.map((m) => {
              if (!m) return null;
              if (typeof m !== 'object') m = { user_id: m };

              let email = (m.email || m.user_email || m.member_email || '').toString().trim();
              let name = (m.name || m.full_name || m.user_name || m.member_name || '').toString().trim();
              const userId = m.user_id ?? m.id ?? m.member_id;

              if ((!name || !email) && userId) {
                const found = (inviteeMembers || []).find(inv => inv.user_id == userId || inv.id == userId || inv.invite_user_id == userId)
                  || (avatarMembers || []).find(av => av.user_id == userId || av.id == userId);
                if (found) {
                  if (!name || name === 'Member') name = found.name;
                  if (!email) email = found.email;
                }
              }

              const memberName = name || (email ? email.split('@')[0] : 'Member');
              const status = (m.status || m.invite_status || m.member_status || 'accepted').toString().toLowerCase();
              const memberId = userId || email || `${memberName}-${Math.random().toString(16).slice(2)}`;

              return {
                id: memberId,
                user_id: userId,
                name: memberName,
                email,
                status,
                initials: getMemberInitials({ name: memberName, email })
              };
            }).filter(Boolean),
            createdBy: projectObj?.created_by || projectObj?.createdBy || p?.created_by || '',
            createdAt,
          };
        })
        .filter(Boolean);

      // Update state and cache
      setProjects(normalizedProjects);
      if (userIdForCache) {
        saveCachedProjects(userIdForCache, normalizedProjects);
      }

      // Call invitee members API after projects are fetched
      if (userIdForCache) {
        try {
          setLoadingInviteeMembers(true);
          const inviteeResponse = await projectsAPI.getInviteeMembers(userIdForCache);
          console.log('Invitee members fetched after projects API:', inviteeResponse);

          // Extract invitee members from response
          console.log('Full invitee response:', inviteeResponse);
          console.log('Invitee response data field:', inviteeResponse?.data);

          let inviteeData = [];
          if (inviteeResponse) {
            if (inviteeResponse.status && inviteeResponse.data) {
              console.log('Processing data field:', inviteeResponse.data);
              // Try different possible structures
              inviteeData = inviteeResponse.data.invited_people ||
                inviteeResponse.data.members ||
                inviteeResponse.data.invitees ||
                inviteeResponse.data.invited_members ||
                inviteeResponse.data.users ||
                (Array.isArray(inviteeResponse.data) ? inviteeResponse.data : []);
            } else if (Array.isArray(inviteeResponse)) {
              inviteeData = inviteeResponse;
            } else if (inviteeResponse.invited_people && Array.isArray(inviteeResponse.invited_people)) {
              inviteeData = inviteeResponse.invited_people;
            } else if (inviteeResponse.members && Array.isArray(inviteeResponse.members)) {
              inviteeData = inviteeResponse.members;
            } else if (inviteeResponse.invitees && Array.isArray(inviteeResponse.invitees)) {
              inviteeData = inviteeResponse.invitees;
            }
          }

          console.log('Extracted invitee data:', inviteeData);
          console.log('Is inviteeData an array?', Array.isArray(inviteeData));
          console.log('Invitee data length:', inviteeData?.length || 0);
          const normalizedInvitees = (Array.isArray(inviteeData) ? inviteeData : [])
            .map((m) => {
              if (!m) return null;
              if (typeof m === 'string') {
                return { email: m, name: m, status: 'invited' };
              }

              const email = m.email || m.user_email || m.invited_email || m.recipient_email || '';
              if (!email) return null; // Skip if no email

              // Extract name from email if no name provided
              const name =
                m.name ||
                m.full_name ||
                m.display_name ||
                `${m.first_name || ''} ${m.last_name || ''}`.trim() ||
                email.split('@')[0] || // Use part before @ as name
                'Member';

              // Determine status based on invite_accepted field
              const inviteAccepted = m.invite_accepted === true || m.invite_accepted === 'true';
              const status = inviteAccepted ? 'accepted' : 'invited';

              // Extract invite_user_id from various possible field names
              const inviteUserId = m.invite_user_id || m.invited_user_id || m.accepted_user_id || m.user_id;
              console.log(`Processing invitee member ${email}: invite_user_id=${inviteUserId}, status=${status}, raw_invite_user_id=${m.invite_user_id}`);

              return {
                id: inviteUserId,
                email,
                name,
                user_id: inviteUserId, // Use invite_user_id as the primary user_id for accepted invitees
                invite_user_id: inviteUserId, // Explicitly preserve invite_user_id for member lookup
                // Additional user ID fields for fallback
                invitee_id: m.invitee_id,
                member_user_id: m.member_user_id,
                initials: (name || email || 'M')
                  .split(' ')
                  .filter(Boolean)
                  .slice(0, 2)
                  .map((p) => p[0].toUpperCase())
                  .join('') || 'M',
                status: status,
                plan_id: m.plan_id,
                invite_sent_datetime: m.invite_sent_datetime,
                invite_accepted_datetime: m.invite_accepted_datetime,
                invite_accepted: m.invite_accepted,
                // Store the raw member for debugging
                raw: m,
              };
            })
            .filter(Boolean);

          setInviteeMembers(normalizedInvitees);
          console.log('Normalized invitee members after projects:', normalizedInvitees);
        } catch (inviteeError) {
          console.warn('Failed to fetch invitee members after projects:', inviteeError);
          // Don't block projects loading if this fails
        } finally {
          setLoadingInviteeMembers(false);
        }

        // Fix 3: fetch group co-members so the sidebar shows all family group members
        try {
          const gmRes = await projectsAPI.getGroupMembers();
          const rawGm = gmRes?.data?.members || gmRes?.members || [];
          const normalizedGm = (Array.isArray(rawGm) ? rawGm : [])
            .filter((m) => m && m.email)
            .map((m) => {
              const name = m.name || `${m.first_name || ''} ${m.last_name || ''}`.trim() || m.email;
              return {
                id: m.user_id || m.id,
                user_id: m.user_id || m.id,
                email: m.email,
                name,
                initials: name.split(' ').filter(Boolean).slice(0, 2).map((p) => p[0].toUpperCase()).join('') || 'M',
                status: 'accepted',
              };
            });
          setGroupMembers(normalizedGm);
        } catch (gmError) {
          console.warn('Failed to fetch group members:', gmError);
        }
      }
    } catch (error) {
      console.error('Error fetching projects:', error);
      // Don't show error toast - projects might not be available yet
      // If API fails, keep existing projects (from cache or previous fetch)
    } finally {
      setLoadingProjects(false);
    }
  };

  // Fetch single project details + members
  const fetchProjectDetails = async (projectId, overridingMembers = null) => {
    if (!projectId) return null;
    try {
      setLoadingSelectedProject(true);
      const res = await projectsAPI.getProjects({ project_id: projectId });
      const raw = res?.data || res?.project || res?.result || res || {};

      // Some APIs return { data: { project: {...}, members: [...] } } or just { project: {...} }
      const projectObj = raw?.project || raw?.data?.project || raw;
      const membersRaw =
        raw?.members ||
        raw?.project_members ||
        raw?.data?.members ||
        raw?.data?.project_members ||
        projectObj?.members ||
        projectObj?.users ||
        [];

      const membersList = Array.isArray(membersRaw) ? membersRaw : [];

      const members = membersList
        .map((m) => {
          if (!m) return null;
          if (typeof m !== 'object') m = { user_id: m };

          let email = (m.email || m.user_email || m.member_email || '').toString().trim();
          let name = (m.name || m.full_name || m.user_name || m.member_name || '').toString().trim();
          const userId = m.user_id ?? m.id ?? m.member_id;

          if ((!name || !email) && userId) {
            const currentMembersSource = overridingMembers || selectedProject?.members || [];
            const foundInExisting = currentMembersSource.find(ex => ex.user_id == userId || ex.id == userId || ex.member_id == userId);
            const foundInInvitees = (inviteeMembers || []).find(inv => inv.user_id == userId || inv.id == userId || inv.invite_user_id == userId);
            const foundInAvatars = (avatarMembers || []).find(av => av.user_id == userId || av.id == userId);
            const found = foundInExisting || foundInInvitees || foundInAvatars;
            if (found) {
              if (!name || name === 'Member') name = found.name;
              if (!email) email = found.email;
            }
          }

          name = name || (email ? email.split('@')[0] : 'Member');
          const status = (m.status || m.invite_status || m.member_status || 'accepted').toString().toLowerCase();
          const id = userId || email || `${name}-${Math.random().toString(16).slice(2)}`;
          return { id, user_id: userId, name, email, status, initials: getMemberInitials({ name, email }) };
        })
        .filter(Boolean);

      const updated = {
        id: projectObj?.id || projectObj?.project_id || projectId,
        name: projectObj?.name || projectObj?.project_name || selectedProject?.name || 'Project',
        description: projectObj?.description || projectObj?.project_description || '',
        created_user: projectObj?.created_user ?? projectObj?.createdUser ?? selectedProject?.created_user ?? null,
        chat_type: projectObj?.chat_type || selectedProject?.chat_type || 'open',
        createdBy: projectObj?.created_by || projectObj?.createdBy || '',
        createdAt: (projectObj?.created_at || projectObj?.created_date || projectObj?.createdAt || '').toString().slice(0, 10) || '',
        members,
      };

      return updated;
    } catch (error) {
      console.error('Error fetching project details:', error);
      return null;
    } finally {
      setLoadingSelectedProject(false);
    }
  };

  const handleSelectProject = async (project) => {
    if (!project?.id) return;
    setSelectedProject(project);
    // Clear current session for this project switch; we'll restore if history has one for this project
    setChatSessionId(null);
    setMessages([]);
    if (wsConnection) {
      wsConnection.close();
      setWsConnection(null);
      setIsWsConnected(false);
    }
    localStorage.removeItem('chat_session_id');
    localStorage.removeItem('chat_messages');
    saveSessionProjectId(null);

    // Project chat: MODE C - GET /api/chat/history?project_id=X
    try {
      const projectId = project.id;
      saveSessionProjectId(projectId);
      const lastSessionId = await loadProjectMessages(projectId);
      if (lastSessionId && lastSessionId > 0) {
        setTimeout(() => connectWebSocket(lastSessionId), 300);
      }
    } catch (e) {
      console.warn('Failed to load project chat history:', e);
    }

    const detailed = await fetchProjectDetails(project.id);
    if (detailed) {
      setProjects((prev) => prev.map((p) => (p.id === project.id ? { ...p, ...detailed } : p)));
      setSelectedProject((prev) => (prev?.id === project.id ? { ...prev, ...detailed } : prev));
    }

    // Call invitee members API when a project is selected
    const userId = user?.id || user?.user_id;
    if (userId) {
      try {
        setLoadingInviteeMembers(true);
        const inviteeResponse = await projectsAPI.getInviteeMembers(userId);
        console.log('Invitee members fetched after project selection:', inviteeResponse);

        // Extract invitee members from response
        // API response structure: { status: true, data: { invited_people: [...], accepted_count: 1, ... } }
        let inviteeData = [];
        if (inviteeResponse) {
          if (inviteeResponse.status && inviteeResponse.data) {
            // The API returns invited_people array
            inviteeData = inviteeResponse.data.invited_people || inviteeResponse.data.members || inviteeResponse.data.invitees || [];
          } else if (Array.isArray(inviteeResponse)) {
            inviteeData = inviteeResponse;
          } else if (inviteeResponse.invited_people && Array.isArray(inviteeResponse.invited_people)) {
            inviteeData = inviteeResponse.invited_people;
          } else if (inviteeResponse.members && Array.isArray(inviteeResponse.members)) {
            inviteeData = inviteeResponse.members;
          } else if (inviteeResponse.invitees && Array.isArray(inviteeResponse.invitees)) {
            inviteeData = inviteeResponse.invitees;
          }
        }

        // Normalize invitee members
        // Each item has: { email, invite_accepted, plan_id, invite_sent_datetime, invite_accepted_datetime }
        const normalizedInvitees = (Array.isArray(inviteeData) ? inviteeData : [])
          .map((m) => {
            if (!m) return null;
            if (typeof m === 'string') {
              return { email: m, name: m, status: 'invited' };
            }

            const email = m.email || m.user_email || m.invited_email || m.recipient_email || '';
            if (!email) return null; // Skip if no email

            // Extract name from email if no name provided
            const name =
              m.name ||
              m.full_name ||
              m.display_name ||
              `${m.first_name || ''} ${m.last_name || ''}`.trim() ||
              email.split('@')[0] || // Use part before @ as name
              'Member';

            // Determine status based on invite_accepted field
            const inviteAccepted = m.invite_accepted === true || m.invite_accepted === 'true';
            const status = inviteAccepted ? 'accepted' : 'invited';
            const inviteUserId = m.invite_user_id;
            return {
              id: m.id || m.user_id || m.invitee_id || email,
              email,
              name,
              user_id: m.user_id || m.id, // Explicitly preserve user_id for member lookup
              initials: (name || email || 'M')
                .split(' ')
                .filter(Boolean)
                .slice(0, 2)
                .map((p) => p[0].toUpperCase())
                .join('') || 'M',
              status: status,
              plan_id: m.plan_id,
              invite_sent_datetime: m.invite_sent_datetime,
              invite_accepted_datetime: m.invite_accepted_datetime,
              invite_user_id: inviteUserId,
            };
          })
          .filter(Boolean);

        setInviteeMembers(normalizedInvitees);
        console.log('Normalized invitee members after project selection:', normalizedInvitees);
      } catch (inviteeError) {
        console.warn('Failed to fetch invitee members after project selection:', inviteeError);
        // Don't block project selection if this fails
      } finally {
        setLoadingInviteeMembers(false);
      }
    }
  };

  const fetchUserAndAvatarData = async (retryCount = 0) => {
    const MAX_RETRIES = 3;
    const RETRY_DELAY = 2000; // 2 seconds

    try {
      setLoadingData(true);

      // Fetch current user
      const userResponse = await authAPI.getCurrentUser();

      // Check if response indicates an error
      if (!userResponse || userResponse.status === false || userResponse.error) {
        throw new Error(userResponse?.error || userResponse?.message || 'Failed to fetch user data');
      }

      if (userResponse.status && userResponse.data) {
        setUser(userResponse.data);
        setIsAuthenticated(true); // Signal readiness as soon as core user data is available

        // Hydrate cached projects so they show after refresh/login
        const currentUserId = userResponse.data.id || userResponse.data.user_id;
        if (currentUserId) {
          const cached = loadCachedProjects(currentUserId);
          if (cached.length > 0) {
            setProjects(cached);
          }
        }

        // Fetch user's avatars
        const userId = userResponse.data.id || userResponse.data.user_id;
        if (userId) {
          // Call invitee members API when chat page loads
          try {
            setLoadingInviteeMembers(true);
            const inviteeResponse = await projectsAPI.getInviteeMembers(userId);
            console.log('Invitee members fetched on chat page load:', inviteeResponse);

            // Extract invitee members from response
            // API response structure: { status: true, data: { invited_people: [...], accepted_count: 1, ... } }
            let inviteeData = [];
            if (inviteeResponse) {
              if (inviteeResponse.status && inviteeResponse.data) {
                // The API returns invited_people array
                inviteeData = inviteeResponse.data.invited_people || inviteeResponse.data.members || inviteeResponse.data.invitees || [];
              } else if (Array.isArray(inviteeResponse)) {
                inviteeData = inviteeResponse;
              } else if (inviteeResponse.invited_people && Array.isArray(inviteeResponse.invited_people)) {
                inviteeData = inviteeResponse.invited_people;
              } else if (inviteeResponse.members && Array.isArray(inviteeResponse.members)) {
                inviteeData = inviteeResponse.members;
              } else if (inviteeResponse.invitees && Array.isArray(inviteeResponse.invitees)) {
                inviteeData = inviteeResponse.invitees;
              }
            }

            // Normalize invitee members
            // Each item has: { email, invite_accepted, plan_id, invite_sent_datetime, invite_accepted_datetime }
            const normalizedInvitees = (Array.isArray(inviteeData) ? inviteeData : [])
              .map((m) => {
                if (!m) return null;
                if (typeof m === 'string') {
                  return { email: m, name: m, status: 'invited' };
                }

                const email = m.email || m.user_email || m.invited_email || m.recipient_email || '';
                if (!email) return null; // Skip if no email

                // Extract name from email if no name provided
                const name =
                  m.name ||
                  m.full_name ||
                  m.display_name ||
                  `${m.first_name || ''} ${m.last_name || ''}`.trim() ||
                  email.split('@')[0] || // Use part before @ as name
                  'Member';

                // Determine status based on invite_accepted field
                const inviteAccepted = m.invite_accepted === true || m.invite_accepted === 'true';
                const status = inviteAccepted ? 'accepted' : 'invited';

                return {
                  id: m.id || m.user_id || m.invitee_id || email,
                  email,
                  name,
                  user_id: m.user_id || m.id, // Explicitly preserve user_id for member lookup
                  initials: (name || email || 'M')
                    .split(' ')
                    .filter(Boolean)
                    .slice(0, 2)
                    .map((p) => p[0].toUpperCase())
                    .join('') || 'M',
                  status: status,
                  plan_id: m.plan_id,
                  invite_sent_datetime: m.invite_sent_datetime,
                  invite_accepted_datetime: m.invite_accepted_datetime,
                };
              })
              .filter(Boolean);

            setInviteeMembers(normalizedInvitees);
            console.log('Normalized invitee members:', normalizedInvitees);
          } catch (inviteeError) {
            console.warn('Failed to fetch invitee members on chat page load:', inviteeError);
            setInviteeMembers([]);
            // Don't block page load if this fails
          } finally {
            setLoadingInviteeMembers(false);
          }

          const avatarsResponse = await avatarsAPI.getAvatars(userId);

          // Extract avatars from response (handle different response structures)
          let userAvatars = [];
          if (avatarsResponse) {
            if (avatarsResponse.status && avatarsResponse.data) {
              userAvatars = avatarsResponse.data.avatars || avatarsResponse.data || [];
            } else if (Array.isArray(avatarsResponse)) {
              userAvatars = avatarsResponse;
            } else if (avatarsResponse.avatars && Array.isArray(avatarsResponse.avatars)) {
              userAvatars = avatarsResponse.avatars;
            }
          }

          // Fetch user's projects to see if they are part of any groups (even if they have no personal avatars)
          let userProjects = [];
          if (!projectsFetchedRef.current || retryCount > 0) {
            try {
              const projectsRes = await projectsAPI.getMyProjects();
              userProjects = projectsRes.data?.projects || projectsRes.data || projectsRes || [];
              if (Array.isArray(userProjects)) {
                setProjects(userProjects);
                projectsFetchedRef.current = true;
              }
            } catch (pe) {
              console.warn('Failed to fetch projects during initial check:', pe);
              // Set to true even on failure to avoid triggering redundant useEffect calls
              projectsFetchedRef.current = true;
            }
          } else {
            userProjects = projects;
          }

          // If no avatars exist, check if user was just invited or has group projects
          if ((!userAvatars || userAvatars.length === 0) && userProjects.length === 0) {
            const storedInviteParams = JSON.parse(localStorage.getItem('signup_invite_params') || '{}');
            const hasInvite = storedInviteParams.invite_id || storedInviteParams.avatar_id || searchParams.get('invite_id');

            // If they have an active invite, wait for backend link (retry only up to 10 attempts)
            if (hasInvite && retryCount < 10) {
              console.log(`Invite detected with no avatars or projects yet. Retrying... (${retryCount + 1}/10)`);
              setTimeout(() => fetchUserAndAvatarData(retryCount + 1), RETRY_DELAY);
              return;
            }

            // Allow users to land on chat even if no avatars/projects yet.
            // If they are completely empty (no plan, no avatars, no projects),
            // just mark loading finished. The UI can show its own join-a-group prompts
            // or an upgrade CTA in the sidebar.
            console.log('User has no current avatars or projects. Staying on chat component.');
            setLoadingData(false);
            setAvatarsChecked(true);
            return;
          }

          // Set avatars and find an active one for redirect if needed
          setAvatars(userAvatars);
          const activeAvatar = userAvatars.find(a => a.status === 'active') || userAvatars[0];

          // Check for subscription expiry or cancellation
          if (userResponse.data.subscription) {
            const { end_date, status } = userResponse.data.subscription;
            if (end_date) {
              const endDate = new Date(end_date);
              const today = new Date();
              today.setHours(0, 0, 0, 0);

              // If subscription is expired or explicitly marked as cancelled/canceled
              const isExpired = endDate < today;
              const isCancelled = status === 'cancelled' || status === 'canceled';

              if (isExpired || isCancelled) {
                // Only redirect non-invited users
                const storedInviteParams = JSON.parse(localStorage.getItem('signup_invite_params') || '{}');
                const hasInvite = storedInviteParams.invite_id || storedInviteParams.avatar_id;

                if (hasInvite) {
                  console.log('User has invite, ignoring expired/cancelled personal subscription check');
                } else {
                  console.log('Subscription not active (Expired:', isExpired, 'Cancelled:', isCancelled, '), redirecting to details');
                  setLoadingData(false);
                  setAvatarsChecked(true);
                  toast.error(isCancelled ? 'Your subscription has been cancelled. Please renew to access the chat.' : 'Your subscription has expired. Please renew to access the chat.', { toastId: 'subscription-check-error' });

                  const avatarIdForRedirect = activeAvatar?.id || userAvatars[0]?.id;
                  if (avatarIdForRedirect) {
                    navigate(`/avatar/${avatarIdForRedirect}`);
                  } else {
                    navigate('/chat');
                  }
                  return; // Exit early, don't set isAuthenticated
                }
              }
            }
          }

          if (activeAvatar) {
            setSelectedAvatar(activeAvatar);
          }
          setAvatarsChecked(true); // Mark that we've checked and avatars exist
          setLoadingData(false);
          localStorage.removeItem('signup_invite_params'); // Clear invite params once association is confirmed
        } else {
          // If no userId, redirect to create avatar page
          console.log('No user ID found, redirecting to create avatar page');
          setLoadingData(false);
          setAvatarsChecked(true);
          toast.info('Please create an avatar first');
          navigate('/chat');
          return;
        }
      } else {
        // If user response is invalid, mark as checked and redirect
        setLoadingData(false);
        setAvatarsChecked(true);
        navigate('/chat');
        return;
      }
    } catch (error) {
      console.error('Error fetching user/avatar data:', error);

      // Check if it's a 503 or network error and retry
      const errorStatus = error?.status || error?.response?.status || error?.statusCode;
      const isServerError = errorStatus === 503 || errorStatus === 502 || errorStatus === 504;
      const isNetworkError = !errorStatus && (error?.message?.includes('network') || error?.message?.includes('fetch'));

      if ((isServerError || isNetworkError) && retryCount < MAX_RETRIES) {
        // Retry with exponential backoff
        const delay = RETRY_DELAY * (retryCount + 1);
        console.log(`Server unavailable (${errorStatus || 'network error'}). Retrying in ${delay}ms... (Attempt ${retryCount + 1}/${MAX_RETRIES})`);

        toast.info(`Server is temporarily unavailable. Retrying... (${retryCount + 1}/${MAX_RETRIES})`, {
          toastId: 'server-retry',
          autoClose: delay
        });

        setTimeout(() => {
          fetchUserAndAvatarData(retryCount + 1);
        }, delay);
        return; // Don't set loading to false yet, we're retrying
      }

      // Max retries reached or non-retryable error
      setLoadingData(false);
      setAvatarsChecked(true);
      // Don't set isAuthenticated - we'll redirect

      if (retryCount >= MAX_RETRIES) {
        toast.error('Server is temporarily unavailable. Please try refreshing the page in a few moments.', {
          toastId: 'server-unavailable',
          autoClose: 8000
        });
        navigate('/chat');
      } else if (errorStatus === 401) {
        // Unauthorized - token might be invalid
        toast.error('Your session has expired. Please sign in again.', {
          toastId: 'session-expired',
          autoClose: 5000
        });
        localStorage.removeItem('access_token');
        navigate('/signin');
      } else {
        toast.error(`Failed to load avatar data${errorStatus ? ` (${errorStatus})` : ''}. Please try refreshing the page.`, {
          toastId: 'avatar-data-error',
          autoClose: 5000
        });
        navigate('/chat');
      }
    } finally {
      // States are set in try/catch blocks, but ensure they're set if we reach here without setting them
      // (this handles edge cases)
      if (retryCount === 0 && !avatarsChecked) {
        // If we somehow reach here without setting states,
        // mark as checked to prevent infinite loading
        setAvatarsChecked(true);
        setLoadingData(false);
      }
    }
  };

  const fetchTokenBalance = async () => {
    try {
      const response = await plansAPI.getTokenBalance();
      console.log('Token balance response:', response);
      if (response && response.data) {
        setTokenBalance(response.data);
      } else if (response && response.token_balance) {
        setTokenBalance(response);
      }
    } catch (error) {
      console.warn('Failed to fetch token balance:', error);
    }
  };

  const getAvatarInitials = (avatar) => {
    if (!avatar) return 'U';
    const firstName = avatar.first_name || '';
    const lastName = avatar.last_name || '';
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase() || 'A';
  };

  const getMessageAvatarDisplay = (message) => {
    // If message has senderType, use it for display
    if (message && message.senderType && message.senderType.toLowerCase() !== 'avatar') {
      const senderTypeStr = message.senderType.toString().trim();
      // If it looks like a name (has space), get initials
      if (senderTypeStr.includes(' ')) {
        const parts = senderTypeStr.split(' ').filter(Boolean);
        const first = parts[0]?.[0] || '';
        const last = parts.length > 1 ? parts[parts.length - 1]?.[0] : '';
        return `${first}${last}`.toUpperCase() || senderTypeStr.substring(0, 2).toUpperCase();
      }
      // Otherwise take first 2 characters
      return senderTypeStr.substring(0, 2).toUpperCase();
    }
    // Default to avatar's actual initials
    const firstName = selectedAvatar?.first_name || '';
    const lastName = selectedAvatar?.last_name || '';
    const initials = `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
    return initials || 'AV';
  };

  const getUserInitials = () => {
    if (!user) return 'U';
    const firstName = user.first_name || user.firstName || '';
    const lastName = user.last_name || user.lastName || '';
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase() || 'U';
  };

  const isAdminUser = () => {
    const adminToken = localStorage.getItem('ADMIN_ACCESS_TOKEN') || localStorage.getItem('admin_access_token');
    return !!adminToken;
  };

  const isProjectOwner = (project) => {
    if (!project) return false;
    const currentUserId = user?.id ?? user?.user_id;
    if (currentUserId != null && project.created_user != null) {
      return Number(project.created_user) === Number(currentUserId);
    }
    const meName = user ? `${user.first_name || ''} ${user.last_name || ''}`.trim() : '';
    const meEmail = user?.email || '';
    const createdBy = (project.createdBy || '').toString().trim();
    if (!createdBy) return false;
    return (
      (meName && createdBy.toLowerCase() === meName.toLowerCase()) ||
      (meEmail && createdBy.toLowerCase() === meEmail.toLowerCase()) ||
      createdBy.toLowerCase() === 'you'
    );
  };

  // Any authenticated member of a family group can add people to the group
  const canManageProjectMembers = (project) => {
    if (!project) return false;
    if (isAdminUser()) return true;
    if (isProjectOwner(project)) return true;
    const members = project.members || [];
    const meEmail = (user?.email || '').toLowerCase();
    const meId = user?.id ?? user?.user_id;
    return members.some((m) => {
      if (meEmail && (m.email || '').toLowerCase() === meEmail) return true;
      if (meId != null) {
        const mid = m.user_id ?? m.id ?? m.member_id;
        if (mid != null && Number(mid) === Number(meId)) return true;
      }
      return false;
    });
  };

  const getAddableMembersForProject = (projectOverride) => {
    const project = projectOverride ?? selectedProject;
    if (!project) return [];

    const projectMembers = project.members || [];
    const projectMemberEmails = new Set(
      projectMembers
        .map((m) => (m.email || m.user_email || m.member_email || '').toString().trim().toLowerCase())
        .filter(Boolean)
    );
    // Include both number and string forms of each id so we match candidates regardless of type
    const projectMemberIds = new Set();
    projectMembers.forEach((m) => {
      const id = m.user_id ?? m.id ?? m.member_id;
      if (id != null && id !== '') {
        projectMemberIds.add(Number(id));
        projectMemberIds.add(String(id));
      }
    });

    const isAlreadyMember = (m) => {
      const email = (m.email || m.user_email || '').toString().trim().toLowerCase();
      const uid = m.user_id ?? m.invite_user_id ?? m.id;
      if (email && projectMemberEmails.has(email)) return true;
      if (uid != null && uid !== '') {
        if (projectMemberIds.has(Number(uid)) || projectMemberIds.has(String(uid))) return true;
      }
      return false;
    };

    // Invitee members: accepted only, not already in project
    const acceptedInvitees = (inviteeMembers || []).filter((m) => {
      const isAccepted =
        m.invite_accepted === true ||
        m.invite_accepted === 'true' ||
        (m.status || '').toLowerCase() === 'accepted';
      const email = (m.email || '').toLowerCase();
      return isAccepted && email && !isAlreadyMember(m);
    });

    // Avatar members: not already in project (by email or id)
    const addableByEmail = new Map();
    acceptedInvitees.forEach((m) => {
      const email = (m.email || '').toLowerCase();
      if (email) addableByEmail.set(email, m);
    });
    (avatarMembers || []).forEach((m) => {
      if (isAlreadyMember(m)) return;
      const email = (m.email || '').toLowerCase();
      if (!email) return;
      if (!addableByEmail.has(email)) addableByEmail.set(email, m);
    });

    // Family group avatars: add avatar owners not already in the project,
    // displayed as "[Avatar Name] — [relation]" so the user knows who they're adding
    const addableById = new Map();
    addableByEmail.forEach((m) => {
      const uid = m.user_id ?? m.id;
      if (uid != null) {
        addableById.set(Number(uid), m);
      }
    });
    (myAvatarsList || []).forEach((av) => {
      const ownerUserId = av.user_id ?? av.owner_id;
      if (ownerUserId == null) return;
      // Skip if owner is already a project member
      if (projectMemberIds.has(Number(ownerUserId)) || projectMemberIds.has(String(ownerUserId))) return;
      // Skip if owner is already in the addable list
      if (addableById.has(Number(ownerUserId))) return;
      const avatarFirstName = av.first_name || '';
      const avatarLastName = av.last_name || '';
      const avatarFullName = `${avatarFirstName} ${avatarLastName}`.trim() || av.name || 'Avatar';
      const relation = av.relation || '';
      const displayName = relation ? `${avatarFullName} — ${relation}` : avatarFullName;
      const candidate = {
        id: `avatar-${av.id || ownerUserId}`,
        user_id: ownerUserId,
        name: displayName,
        email: av.email || '',
        initials: getMemberInitials({ name: avatarFullName, email: av.email || '' }),
        status: 'accepted',
        _avatarSource: true,
      };
      addableById.set(Number(ownerUserId), candidate);
    });

    // Merge avatar-sourced candidates that aren't already in addableByEmail
    addableById.forEach((candidate) => {
      if (candidate._avatarSource) {
        const email = (candidate.email || '').toLowerCase();
        if (email && !addableByEmail.has(email)) {
          addableByEmail.set(`__avatar_uid_${candidate.user_id}`, candidate);
        } else if (!email) {
          addableByEmail.set(`__avatar_uid_${candidate.user_id}`, candidate);
        }
      }
    });

    return Array.from(addableByEmail.values());
  };

  const openAddMembersModal = (project) => {
    const proj = project ?? selectedProject;
    if (!proj) return;

    // Only select if it's a different project, to avoid overwriting detailed view with shallow list
    if (project && (!selectedProject || selectedProject.id !== project.id)) {
      handleSelectProject(project);
    }

    setShowAddMembersModal(true);
    setAddMembersQuery('');
    setSelectedAddMembers([]);

    // Fetch group co-members and merge into inviteeMembers (dedup by email)
    projectsAPI.getGroupMembers().then((res) => {
      const raw = res?.data?.members || res?.members || [];
      if (!Array.isArray(raw) || raw.length === 0) return;
      const normalized = raw
        .filter((m) => m && m.email)
        .map((m) => {
          const email = (m.email || '').toLowerCase();
          const name = m.name || email.split('@')[0] || 'Member';
          const uid = m.user_id ?? null;
          return {
            id: uid,
            email,
            name,
            user_id: uid,
            invite_user_id: uid,
            initials: name.split(' ').filter(Boolean).slice(0, 2).map((p) => p[0].toUpperCase()).join('') || 'M',
            status: 'accepted',
            invite_accepted: true,
          };
        });
      setInviteeMembers((prev) => {
        const existingEmails = new Set((prev || []).map((m) => (m.email || '').toLowerCase()));
        const toAdd = normalized.filter((m) => !existingEmails.has(m.email));
        return toAdd.length > 0 ? [...(prev || []), ...toAdd] : prev;
      });
    }).catch(() => {});
  };

  // Auto-close Add members modal when no more members left to add
  useEffect(() => {
    if (showAddMembersModal) {
      const all = getAddableMembersForProject();
      if (all.length === 0 && !addingMembers) {
        setShowAddMembersModal(false);
      }
    }
  }, [showAddMembersModal, selectedProject?.members?.length, inviteeMembers, avatarMembers, addingMembers]);

  const toggleSelectedAddMember = (member) => {
    if (!member) return;
    const email = (member.email || '').toLowerCase();
    if (!email) return;
    setSelectedAddMembers((prev) => {
      const exists = prev.some((p) => (p.email || '').toLowerCase() === email);
      if (exists) return prev.filter((p) => (p.email || '').toLowerCase() !== email);
      return [...prev, member];
    });
  };

  const handleDeleteProject = async () => {
    if (!projectToDelete?.id) return;
    setDeletingProjectId(projectToDelete.id);
    setShowDeleteProjectModal(false);
    setProjectToDelete(null);
    setProjectMenuOpenId(null);
    try {
      await projectsAPI.deleteProject(projectToDelete.id);
      setProjects((prev) => prev.filter((p) => p.id !== projectToDelete.id));
      if (selectedProject?.id === projectToDelete.id) {
        setSelectedProject(null);
        setProjectMessages([]);
        saveSessionProjectId(null);
      }
      await fetchProjects();
    } catch (e) {
      console.error('Failed to delete group:', e);
      toast.error(e?.message || 'Failed to delete group.');
    } finally {
      setDeletingProjectId(null);
    }
  };

  const handleLeaveProject = async () => {
    if (!projectToLeave?.id) return;
    setLeavingProjectId(projectToLeave.id);
    setShowLeaveProjectModal(false);
    setProjectToLeave(null);
    setProjectMenuOpenId(null);
    try {
      await projectsAPI.leaveProject(projectToLeave.id);
      setProjects((prev) => prev.filter((p) => p.id !== projectToLeave.id));
      if (selectedProject?.id === projectToLeave.id) {
        setSelectedProject(null);
        setProjectMessages([]);
        saveSessionProjectId(null);
      }
      await fetchProjects();
    } catch (e) {
      console.error('Failed to leave group:', e);
      toast.error(e?.message || 'Failed to leave group.');
    } finally {
      setLeavingProjectId(null);
    }
  };

  const handleRenameProject = async (e) => {
    e?.preventDefault();
    if (!projectToRename?.id || !renameProjectName?.trim()) {
      toast.error('Please enter a group name.');
      return;
    }
    setRenamingProject(true);
    try {
      await projectsAPI.updateProject(projectToRename.id, { name: renameProjectName.trim() });
      setProjects((prev) =>
        prev.map((p) => (p.id === projectToRename.id ? { ...p, name: renameProjectName.trim() } : p))
      );
      if (selectedProject?.id === projectToRename.id) {
        setSelectedProject((prev) => (prev ? { ...prev, name: renameProjectName.trim() } : null));
      }
      setShowRenameProjectModal(false);
      setProjectToRename(null);
      setRenameProjectName('');
    } catch (err) {
      console.error('Failed to rename group:', err);
      toast.error(err?.message || 'Failed to rename group.');
    } finally {
      setRenamingProject(false);
    }
  };

  const resetAddMembersModal = () => {
    setAddMembersQuery('');
    setSelectedAddMembers([]);
    setAddingMembers(false);
  };

  const addOneMemberToProject = async (member) => {
    if (!selectedProject?.id || !member) return;
    const email = (member.email || '').trim();
    const userId =
      member.user_id ||
      member.invite_user_id ||
      (email ? findUserIdForEmail(email) : null);
    if (!userId) {
      toast.error('Unable to add this member (missing user_id). They may need to accept their invitation first.');
      return;
    }
    setAddingMembers(true);
    try {
      await projectsAPI.addMember({ project_id: selectedProject.id, user_id: userId });
      setAddMembersQuery(''); // Clear search so the added name doesn't stay in the search box
      // Optimistically add to selectedProject so suggestion box and group list update immediately
      const newMemberEntry = {
        id: userId,
        user_id: userId,
        name: member.name || member.email || 'Member',
        email: member.email || '',
        status: 'accepted',
        initials: getMemberInitials({ name: member.name, email: member.email }),
      };
      const newMembersArray = [...(selectedProject.members || []), newMemberEntry];
      setSelectedProject((prev) => (prev ? {
        ...prev,
        members: newMembersArray,
      } : prev));
      setProjects((prev) => prev.map((p) => (p.id === selectedProject.id
        ? { ...p, members: newMembersArray }
        : p)));
      await fetchProjects();
      const detailed = await fetchProjectDetails(selectedProject.id, newMembersArray);
      if (detailed?.members?.length) setSelectedProject(detailed);
    } catch (e) {
      console.error('Failed to add member:', e);
      const msg = (e?.message || e?.response?.data?.message || '').toLowerCase();
      if (msg.includes('already a member') || msg.includes('already in this project')) {
        // Don't show error — they're already in the group; list will update to exclude them
        setAddMembersQuery('');
      } else {
        toast.error(e?.message || 'Failed to add member.');
      }
    } finally {
      setAddingMembers(false);
    }
  };

  const addSelectedMembersToProject = async () => {
    if (!selectedProject?.id) return;
    if (!canManageProjectMembers(selectedProject)) return;
    if (selectedAddMembers.length === 0) {
      // toast.info('Select at least one member to add.');
      return;
    }

    setAddingMembers(true);
    let addedCount = 0;
    let skippedCount = 0;
    let failedCount = 0;
    const successfullyAdded = [];

    for (const m of selectedAddMembers) {
      const email = (m.email || '').trim();
      const userId =
        m.user_id ||
        m.invite_user_id ||
        (email ? findUserIdForEmail(email) : null);

      if (!userId) {
        skippedCount += 1;
        continue;
      }

      try {
        // Existing backend expects one member per call.
        // POST /api/projects/members { project_id, user_id }
        // eslint-disable-next-line no-await-in-loop
        await projectsAPI.addMember({ project_id: selectedProject.id, user_id: userId });
        addedCount += 1;
        successfullyAdded.push({ ...m, user_id: userId });
      } catch (e) {
        console.error('Failed to add member:', { email, userId, error: e });
        failedCount += 1;
      }
    }

    // if (skippedCount > 0) toast.info(`Skipped ${skippedCount} (missing user_id / not accepted yet).`);
    if (failedCount > 0) toast.error(`Failed to add ${failedCount} member${failedCount !== 1 ? 's' : ''}.`);

    if (successfullyAdded.length > 0) {
      const newEntries = successfullyAdded.map((m) => ({
        id: m.user_id ?? m.email,
        user_id: m.user_id,
        name: m.name || m.email || 'Member',
        email: (m.email || '').trim(),
        status: 'accepted',
        initials: getMemberInitials({ name: m.name, email: m.email }),
      }));
      const newMembersArray = [...(selectedProject.members || []), ...newEntries];
      setSelectedProject((prev) => (prev ? { ...prev, members: newMembersArray } : prev));
      setProjects((prev) => prev.map((p) => (p.id === selectedProject.id ? { ...p, members: newMembersArray } : p)));
      try {
        await fetchProjects();
        const detailed = await fetchProjectDetails(selectedProject.id, newMembersArray);
        if (detailed?.members?.length) setSelectedProject(detailed);
      } catch (projectError) {
        console.warn('Failed to refresh projects after adding members:', projectError);
      }
    } else {
      try {
        await fetchProjects();
      } catch (projectError) {
        console.warn('Failed to refresh projects after adding members:', projectError);
      }
    }

    setShowAddMembersModal(false);
    resetAddMembersModal();
    setAddingMembers(false);
  };

  const removeProjectMember = (member) => {
    if (!selectedProject || !member) return;
    if (!canManageProjectMembers(selectedProject)) return;

    // Prevent removing yourself
    const meEmail = (user?.email || '').toLowerCase();
    const memberEmail = (member.email || '').toLowerCase();
    if (meEmail && memberEmail && meEmail === memberEmail) {
      // toast.info("You can't remove yourself from the project.");
      return;
    }

    const updatedProject = {
      ...selectedProject,
      members: (selectedProject.members || []).filter((m) => (m.email || m.id) !== (member.email || member.id))
    };

    setProjects((prev) => prev.map((p) => (p.id === selectedProject.id ? updatedProject : p)));
    setSelectedProject(updatedProject);
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Save messages to localStorage whenever messages change (but not during initial load)
  useEffect(() => {
    if (!initialLoadRef.current) {
      console.log('💾 Saving messages to localStorage:', messages.length, 'messages');
      if (messages.length > 0) {
        saveMessages(messages);
      } else {
        console.log('🗑️ Clearing messages from localStorage');
        localStorage.removeItem('chat_messages');
      }
    } else {
      console.log('⏳ Skipping message save during initial load');
    }
  }, [messages]);

  // Save session ID to localStorage whenever it changes (but not during initial load)
  useEffect(() => {
    if (!initialLoadRef.current) {
      saveSessionId(chatSessionId);
    }
  }, [chatSessionId]);

  // Handle new chat session cleanup
  useEffect(() => {
    if (chatSessionKey > 0) {
      console.log('🔄 Chat session key changed, ensuring clean state');
      // Additional cleanup if needed
    }
  }, [chatSessionKey]);

  // Reset chat session when switching avatars (but keep for projects)
  useEffect(() => {
    const currentAvatarId = selectedAvatar?.id;
    const previousAvatarId = previousAvatarIdRef.current;

    console.log('🔄 Avatar changed effect triggered:', {
      currentAvatarId,
      previousAvatarId,
      selectedProject: selectedProject?.id,
      initialLoad: initialLoadRef.current,
      currentMessages: messages.length,
      isActualSwitch: previousAvatarId && previousAvatarId !== currentAvatarId
    });

    // Only clear messages if we're actually switching between different avatars
    // (not during initial load and not when first selecting an avatar)
    if (!initialLoadRef.current && !selectedProject && previousAvatarId && previousAvatarId !== currentAvatarId) {
      console.log('🔄 Actually switching avatars - clearing chat session');
      // Reset chat session when switching between avatars
      setChatSessionId(null);
      if (wsConnection) {
        wsConnection.close();
        setWsConnection(null);
        setIsWsConnected(false);
      }
      setMessages([]);
    } else if (initialLoadRef.current) {
      console.log('⏳ Skipping avatar switch during initial load');
    } else if (!previousAvatarId && currentAvatarId) {
      console.log('👤 First avatar selection - keeping restored messages');
    }

    // Refetch chat sessions when avatar changes (to filter by avatar_id)
    if (!selectedProject && currentAvatarId) {
      fetchChatSessions();
    }

    // Update the previous avatar ID for next comparison
    console.log('📝 Updated previousAvatarIdRef from', previousAvatarIdRef.current, 'to', currentAvatarId);
    previousAvatarIdRef.current = currentAvatarId;
  }, [selectedAvatar?.id]);

  // When project is selected, session clear/restore is handled in handleSelectProject (history API first, then chat).
  useEffect(() => {
    setProjectMessages([]);
  }, [selectedProject]);

  // Collapse sidebar members list when switching groups
  useEffect(() => {
    setSidebarMembersExpanded(false);
  }, [selectedProject?.id]);

  // Clear input mention state on session change
  useEffect(() => {
    setInputValue('');
  }, [chatSessionId]);

  const handleMemberClick = async (m) => {
    const myId = user?.id || user?.user_id;
    const memberId = m.user_id || m.id;

    // Check for an existing direct-chat project containing exactly these two users
    const existing = (projects || []).find((p) => {
      const members = p.members || [];
      const ids = members.map((pm) => pm.user_id ?? pm.id).filter(Boolean);
      return ids.includes(myId) && ids.includes(memberId) && ids.length === 2;
    });

    if (existing) {
      handleSelectProject(existing);
      return;
    }

    // Create a new direct-chat project named after the member
    try {
      const memberName = m.name || m.email || 'Direct Chat';
      const res = await projectsAPI.createProject({
        name: memberName,
        created_user: myId,
        chat_type: 'selected',
      });

      const apiProject = res?.data || res?.project || res;
      const apiProjectId = apiProject?.id || apiProject?.project_id || apiProject?._id;

      const meName = user
        ? `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email || 'You'
        : 'You';
      const meEmail = user?.email || '';
      const meInitials = getUserInitials();

      const newProject = {
        id: apiProjectId || `local-${Date.now()}`,
        name: apiProject?.name || memberName,
        description: '',
        members: [
          { id: myId, user_id: myId, name: meName, email: meEmail, initials: meInitials, status: 'accepted' },
          { id: memberId, user_id: memberId, name: m.name, email: m.email, initials: m.initials, status: 'accepted' },
        ],
        createdBy: meName,
        createdAt: new Date().toISOString().split('T')[0],
      };

      // Add the other member via API
      if (apiProjectId && memberId) {
        try {
          await projectsAPI.addMember({ project_id: apiProjectId, user_id: memberId });
        } catch (err) {
          console.warn('addMember failed:', err);
        }
      }

      setProjects((prev) => {
        const next = [...(prev || []), newProject];
        saveCachedProjects(myId, next);
        return next;
      });
      setSelectedProject(newProject);
      setChatSessionId(null);
      setMessages([]);
      if (wsConnection) {
        wsConnection.close();
        setWsConnection(null);
        setIsWsConnected(false);
      }
      localStorage.removeItem('chat_session_id');
      localStorage.removeItem('chat_messages');
      saveSessionProjectId(newProject.id);
      try {
        const lastSessionId = await loadProjectMessages(newProject.id);
        if (lastSessionId && lastSessionId > 0) {
          setTimeout(() => connectWebSocket(lastSessionId), 300);
        }
      } catch (err) {
        console.warn('loadProjectMessages after direct chat create:', err);
      }
    } catch (err) {
      console.error('handleMemberClick createProject failed:', err);
      toast.error('Could not open direct chat. Please try again.');
    }
  };

  const handleCreateProject = async (e) => {
    e.preventDefault();
    if (!projectName.trim()) {
      toast.error('Please enter a project name');
      return;
    }

    setCreatingProject(true);

    try {
      const createdUserId = user?.id || user?.user_id;
      if (!createdUserId) {
        toast.error('User not found. Please sign in again.');
        return;
      }

      const res = await projectsAPI.createProject({
        name: projectName.trim(),
        created_user: createdUserId,
        chat_type: projectDescription === 'just_us' ? 'just_us' : projectDescription === 'selected' ? 'selected' : 'open',
      });

      // Support multiple backend response shapes
      const apiProject = res?.data || res?.project || res;
      const apiProjectId = apiProject?.id || apiProject?.project_id || apiProject?._id;

      // Persist Just Us type in localStorage so it survives page refreshes
      if (projectDescription === 'just_us' && apiProjectId) {
        markProjectJustUs(apiProjectId);
      }

      const meName = user
        ? `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email || 'You'
        : 'You';
      const meEmail = user?.email || '';
      const meInitials = getUserInitials();

      const newProject = {
        id: apiProjectId || `local-${Date.now()}`,
        name: apiProject?.name || projectName.trim(),
        description: apiProject?.description || projectDescription,
        members: [
          {
            id: createdUserId,
            name: meName,
            email: meEmail,
            initials: meInitials,
            status: 'accepted'
          }
        ],
        createdBy: meName,
        createdAt: new Date().toISOString().split('T')[0]
      };

      setProjects((prev) => {
        const next = [...(prev || []), newProject];
        saveCachedProjects(createdUserId, next);
        return next;
      });
      setSelectedProject(newProject);
      setProjectName('');
      setProjectDescription('');
      setShowCreateProjectModal(false);

      // Switch chat context to the new group: clear single-chat state and load group messages
      setChatSessionId(null);
      setMessages([]);
      if (wsConnection) {
        wsConnection.close();
        setWsConnection(null);
        setIsWsConnected(false);
      }
      localStorage.removeItem('chat_session_id');
      localStorage.removeItem('chat_messages');
      saveSessionProjectId(newProject.id);
      try {
        const lastSessionId = await loadProjectMessages(newProject.id);
        if (lastSessionId && lastSessionId > 0) {
          setTimeout(() => connectWebSocket(lastSessionId), 300);
        }
      } catch (err) {
        console.warn('Load project messages after create:', err);
      }

      // Add members from the New Chat modal
      const membersToAdd = newChatType === 'open'
        ? (avatarMembers || []).filter(m => (m.user_id ?? m.id) !== createdUserId)
        : newChatPickedMembers;

      if (apiProjectId && membersToAdd.length > 0) {
        await Promise.all(
          membersToAdd.map(m => {
            const memberId = m.user_id ?? m.id;
            if (memberId) {
              return projectsAPI.addMember({ project_id: apiProjectId, user_id: memberId }).catch(err => {
                console.warn('addMember failed for', memberId, err);
              });
            }
          })
        );
        setNewChatPickedMembers([]);
      }

      // For just_us and selected chats, store member IDs so the first POST /api/chat
      // can seed project_session_members on the backend.
      if (projectDescription === 'just_us' || newChatType === 'selected') {
        const sessionMemberIds = membersToAdd
          .map(m => m.user_id ?? m.id)
          .filter(id => id != null && id !== createdUserId)
          .map(Number);
        setPendingSessionMemberIds(sessionMemberIds);
      } else {
        setPendingSessionMemberIds([]);
      }

      // Best-effort: fetch full details by project_id immediately (matches Swagger curl)
      const detailed = await fetchProjectDetails(newProject.id);
      if (detailed) {
        setProjects((prev) => {
          const next = (prev || []).map((p) => (p.id === newProject.id ? { ...p, ...detailed } : p));
          saveCachedProjects(createdUserId, next);
          return next;
        });
        setSelectedProject((prev) => (prev?.id === newProject.id ? { ...prev, ...detailed } : prev));
      }

      // Then refresh list (won't wipe UI even if member_id isn't supported)
      fetchProjects();
    } catch (error) {
      console.error('Create project error:', error);
      toast.error(error?.message || 'Failed to create project. Please try again.');
    } finally {
      setCreatingProject(false);
    }
  };

  // Helper function to extract and validate user_id from a member object
  const extractUserId = (member) => {
    if (!member) return null;

    // Priority: user_id field first (this is the actual user_id from the API)
    // Then try id field only if user_id is not available
    const candidateId = member.user_id || (member.id && !member.user_id ? member.id : null) || member.userId;

    if (!candidateId) return null;

    // Validate that it's not an email (contains @) and is a valid number
    if (typeof candidateId === 'string' && candidateId.includes('@')) {
      // This is an email, not a user_id - skip it
      console.warn('Skipping email string as user_id:', candidateId);
      return null;
    }

    // Ensure it's a valid integer
    const userIdNum = typeof candidateId === 'number' ? candidateId : parseInt(candidateId, 10);
    if (isNaN(userIdNum) || userIdNum <= 0) {
      console.warn('Invalid user_id value:', candidateId);
      return null;
    }

    return userIdNum;
  };

  // Helper function to find user_id for an email (accessible from anywhere in the component)
  const findUserIdForEmail = (email) => {
    let userId = null;

    console.log('🔍 Looking up user_id for email:', email);
    console.log('📋 Available invitee members:', inviteeMembers);
    console.log('👥 Available avatar members:', avatarMembers);
    console.log('📁 Selected project members:', selectedProject?.members);

    // Check invitee members FIRST (people who accepted invitations - these should be added directly)
    if (!userId && inviteeMembers && inviteeMembers.length > 0) {
      const matchingInvitee = inviteeMembers.find(m =>
        (m.email || '').toLowerCase() === email.toLowerCase()
      );
      if (matchingInvitee) {
        // Check if they have accepted (but add them regardless if they're in invitee list)
        const isAccepted = matchingInvitee.invite_accepted === true ||
          matchingInvitee.invite_accepted === 'true' ||
          (matchingInvitee.status || '').toLowerCase() === 'accepted';

        console.log('✅ Found matching invitee member:', matchingInvitee, 'Accepted:', isAccepted);
        console.log('🔑 Invitee member keys:', Object.keys(matchingInvitee));
        console.log('🔍 All invitee member fields:', matchingInvitee);

        // For invitee members, the invite_user_id should be the primary field for accepted invitees
        // This is populated by the getInviteeMembers API call
        let candidateUserId = matchingInvitee.invite_user_id;

        console.log('🔍 Initial candidateUserId from user_id fields (checking invite_user_id first):', candidateUserId);
        console.log('🔍 invite_user_id value:', matchingInvitee.invite_user_id);

        // If no user_id in dedicated fields, try id field (but validate it's not an email)
        if (!candidateUserId && matchingInvitee.id) {
          const idValue = matchingInvitee.id;
          console.log('🔍 Trying id field:', idValue, 'Type:', typeof idValue);

          // If id is a number and looks like a user_id (positive integer), use it
          if (typeof idValue === 'number' && idValue > 0 && Number.isInteger(idValue)) {
            candidateUserId = idValue;
            console.log('✅ Using numeric id as user_id:', candidateUserId);
          } else if (typeof idValue === 'string' && !idValue.includes('@')) {
            const parsedId = parseInt(idValue, 10);
            if (!isNaN(parsedId) && parsedId > 0 && Number.isInteger(parsedId)) {
              candidateUserId = parsedId;
              console.log('✅ Parsed string id as user_id:', candidateUserId);
            }
          }
        }

        // If still no user_id, check if there's a user object nested in the invitee member
        if (!candidateUserId && matchingInvitee.user) {
          const userObj = matchingInvitee.user;
          candidateUserId = userObj.id || userObj.user_id;
          console.log('🔍 Found user object, candidateUserId:', candidateUserId);
        }

        // Validate candidateUserId
        if (candidateUserId) {
          if (typeof candidateUserId === 'string' && candidateUserId.includes('@')) {
            console.warn('⚠️ Skipping email string as user_id from invitee member:', candidateUserId);
          } else {
            userId = candidateUserId;
            console.log('✅ Found user_id from invitee members (can add directly):', userId, 'Type:', typeof userId, 'Accepted:', isAccepted);
          }
        } else {
          console.warn('❌ No valid user_id found in invitee member object. Full object:', JSON.stringify(matchingInvitee, null, 2));

          // For invitee members that don't have user_id, we need to look elsewhere
          // This happens when the API response doesn't include invite_user_id for accepted members
          // Continue checking other sources below
        }
      } else {
        console.log('❌ No matching invitee member found for email:', email);
      }
    }

    // If we still don't have a userId after checking invitee members, check project members and other sources
    if (!userId) {
      // Check current project members
      if (selectedProject?.members && selectedProject.members.length > 0) {
        const projectMember = selectedProject.members.find(m =>
          (m.email || '').toLowerCase() === email.toLowerCase()
        );
        if (projectMember) {
          console.log('🔍 Found project member with matching email:', projectMember);
          if (projectMember.user_id) {
            userId = projectMember.user_id;
            console.log('✅ Found user_id from project members (direct user_id):', userId);
          } else {
            userId = extractUserId(projectMember);
            if (userId) {
              console.log('✅ Found user_id from project members (extracted):', userId);
            }
          }
        }
      }

      // Check other projects if still not found
      if (!userId && projects && projects.length > 0) {
        for (const proj of projects) {
          if (proj.id !== selectedProject?.id && proj.members && proj.members.length > 0) {
            const memberInOtherProject = proj.members.find(m =>
              (m.email || '').toLowerCase() === email.toLowerCase()
            );
            if (memberInOtherProject) {
              console.log('🔍 Found member in another project:', proj.name);
              if (memberInOtherProject.user_id) {
                userId = memberInOtherProject.user_id;
                console.log('✅ Found user_id from another project (direct user_id):', userId, 'Project:', proj.id);
                break;
              } else {
                userId = extractUserId(memberInOtherProject);
                if (userId) {
                  console.log('✅ Found user_id from another project (extracted):', userId, 'Project:', proj.id);
                  break;
                }
              }
            }
          }
        }
      }
    }

    // Check avatar members (people who are already members of avatars)
    if (!userId && avatarMembers && avatarMembers.length > 0) {
      const matchingAvatarMember = avatarMembers.find(m =>
        (m.email || '').toLowerCase() === email
      );
      if (matchingAvatarMember) {
        userId = extractUserId(matchingAvatarMember);
        if (userId) {
          console.log('Found user_id from avatar members:', userId, 'Type:', typeof userId);
        }
      }
    }

    // Check existing project members
    if (!userId && selectedProject.members && selectedProject.members.length > 0) {
      const existingMember = selectedProject.members.find(m =>
        (m.email || '').toLowerCase() === email
      );
      if (existingMember) {
        userId = extractUserId(existingMember);
        if (userId) {
          console.log('Found user_id from project members:', userId, 'Type:', typeof userId);
        }
      }
    }

    // Check other projects for this user (these also have user_id field directly)
    if (!userId && projects && projects.length > 0) {
      // First try to find by email
      let memberWithEmail = projects
        .flatMap(p => (p.members || []).map(m => ({ ...m, projectId: p.id })))
        .find(m => m.email?.toLowerCase() === email);

      if (memberWithEmail) {
        // Project members have user_id field directly: { id: 2, user_id: 174, ... }
        if (memberWithEmail.user_id) {
          userId = memberWithEmail.user_id;
          console.log('✅ Found user_id from another project (direct user_id field):', userId, 'Project:', memberWithEmail.projectId);
        } else {
          userId = extractUserId(memberWithEmail);
          if (userId) {
            console.log('✅ Found user_id from another project (extracted):', userId, 'Type:', typeof userId);
          }
        }
      }

      // If still not found by email, check if any project member has a user_id that matches
      // This handles cases where email might not be in the member object
      if (!userId) {
        for (const proj of projects) {
          if (proj.members && proj.members.length > 0) {
            for (const member of proj.members) {
              // If member has user_id, we can potentially use it if we can verify it's the right user
              // But we need email match, so skip this for now
              // The main issue is that invitee members might not have user_id
            }
          }
        }
      }
    }

    // Also check the combined member list (as displayed in UI)
    if (!userId) {
      const allMembersMap = new Map();
      if (avatarMembers && avatarMembers.length > 0) {
        avatarMembers.forEach(m => {
          const memberEmail = (m.email || '').toLowerCase();
          if (memberEmail) {
            allMembersMap.set(memberEmail, m);
          }
        });
      }
      if (inviteeMembers && inviteeMembers.length > 0) {
        inviteeMembers.forEach(m => {
          const memberEmail = (m.email || '').toLowerCase();
          if (memberEmail && !allMembersMap.has(memberEmail)) {
            allMembersMap.set(memberEmail, m);
          }
        });
      }

      const combinedMember = allMembersMap.get(email.toLowerCase());
      if (combinedMember) {
        userId = extractUserId(combinedMember);
        if (userId) {
          console.log('Found user_id from combined member list:', userId, 'Type:', typeof userId);
        }
      }
    }

    return userId;
  };

  const handleFileUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    const token = localStorage.getItem('access_token');
    if (!token) {
      toast.error("You are not authenticated.");
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }

    let currentSessionId = chatSessionId;
    let isNewSession = false;

    // Create session if it doesn't already exist
    if (!currentSessionId) {
      setIsUploading(true);
      try {
        // Use first file name as session title
        const title = files[0]?.name || 'Document Upload';
        console.log('Creating new chat session for upload:', title);
        const result = await createChatSession(title);
        if (result && result.sessionId) {
          currentSessionId = result.sessionId;
          isNewSession = true;
          // Refresh sesssions list in sidebar
          fetchChatSessions();
        } else {
          throw new Error("Failed to start session.");
        }
      } catch (err) {
        console.error('Session creation for upload failed:', err);
        toast.error("Please start a session before uploading files.");
        setIsUploading(false);
        if (fileInputRef.current) fileInputRef.current.value = "";
        return;
      }
    }

    const formData = new FormData();
    for (let i = 0; i < files.length; i++) {
      formData.append('files', files[i]);
    }

    const apiUrl = (configApiUrl || process.env.REACT_APP_API_URL || 'https://sayagainservices.sayagain.ai').replace(/\/$/, '');
    const uploadUrl = `${apiUrl}/api/chat/${currentSessionId}/upload`;

    setIsUploading(true);
    let uploadSuccess = false;
    try {
      const response = await fetch(uploadUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        const msg = data.msg || data.message || data.error || 'Upload failed';
        throw new Error(msg);
      }

      const filesProcessed = data.data?.files_processed ?? data.files_processed ?? 0;
      const skippedFiles = data.data?.skipped_files ?? data.skipped_files ?? [];
      const apiMsg = data.msg || data.message;

      if (filesProcessed > 0) {
        // toast.success(apiMsg || `Successfully uploaded ${filesProcessed} file${filesProcessed > 1 ? 's' : ''}.`);
        uploadSuccess = true;
      } else if (skippedFiles.length > 0) {
        // toast.info(apiMsg || `Some files were skipped.`);
        uploadSuccess = true;
      } else {
        // toast.success('Files uploaded successfully');
        uploadSuccess = true;
      }
    } catch (err) {
      console.error('File upload error:', err);
      toast.error(err.message || 'Failed to upload files');
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }

    if (uploadSuccess) {
      // Reload message history to see server's responses/summaries for the uploaded files
      const isGroupChat = !!(selectedProject?.id || localStorage.getItem('chat_session_project_id'));
      if (isGroupChat) {
        const projectId = selectedProject?.id || localStorage.getItem('chat_session_project_id');
        if (projectId) await loadProjectMessages(projectId, false);
      } else {
        await loadSessionMessages(currentSessionId, false);
      }

      if (isNewSession) {
        connectWebSocket(currentSessionId);
      }
    }
  };

  const handleCancelResponse = (e) => {
    if (e) e.preventDefault();
    console.log('🛑 Stopping current response...');
    const wsToClose = currentWsRef.current || wsConnection;
    if (wsToClose) {
      try {
        wsToClose.close();
        console.log('✅ WebSocket closed by user');
      } catch (err) {
        console.error('❌ Error closing WebSocket:', err);
      }
      setIsWsConnected(false);
      setWsConnection(null);
      currentWsRef.current = null;
      
      // Reconnect after a short delay to be ready for the next message
      if (chatSessionId) {
        console.log('🔄 Reconnecting WebSocket for next message...');
        setTimeout(() => connectWebSocket(chatSessionId), 1000);
      }
    }
    setIsTyping(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (!isTyping) {
        handleSendMessage(e);
      }
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (isChatInputDisabled) {
      handleTokenLimitReached();
      return;
    }
    if (!inputValue.trim()) return;

    const messageText = inputValue.trim();
    const isGroupChat = !!(selectedProject?.id || localStorage.getItem('chat_session_project_id'));
    const currentProjectId = selectedProject?.id || localStorage.getItem('chat_session_project_id');
    const isJustUs = !!(currentProjectId && isJustUsProject(currentProjectId));

    const userFullName = user ? `${user.first_name || ''} ${user.last_name || ''}`.trim() || 'User' : 'User';
    const currentUserId = user?.id || user?.user_id || null;
    setInputValue('');
    if (!isJustUs) {
      setIsTyping(true);
    }

    // Optimistic user message — shown immediately for both single and group chat.
    // For group chat the backend will echo the message back; the onmessage handler
    // replaces the optimistic entry (by tempId stored in pendingGroupOptimisticIdRef)
    // instead of appending a duplicate.
    const tempId = `optimistic-${Date.now()}`;
    const optimisticMsg = {
      id: tempId,
      type: 'user',
      content: messageText,
      time: new Date().toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }),
      senderType: userFullName,
      senderName: userFullName,
      senderUserId: currentUserId,
    };
    setMessages(prev => [...prev, optimisticMsg]);
    if (isGroupChat) {
      pendingGroupOptimisticIdRef.current = tempId;
    }

    // Remove the optimistic message if the send fails for any reason.
    const rollbackOptimistic = () => {
      setMessages(prev => prev.filter(m => m.id !== tempId));
      if (isGroupChat) pendingGroupOptimisticIdRef.current = null;
    };

    try {
      // If no chat session exists, create one first via POST /api/chat
      if (!chatSessionId) {
        creatingSessionRef.current = true;
        console.log('Creating new chat session...');
        const result = await createChatSession(messageText);
        if (result && result.sessionId) {
          console.log('🚀 Session created, connecting WebSocket...', result.sessionId);

          fetchChatSessions();
          connectWebSocket(result.sessionId);

          // User message already in state from optimistic add above — no duplicate add needed.

          // Update token balance from first-message response (POST /api/chat returns it; WebSocket only from 2nd message)
          if (result.tokenBalance && typeof result.tokenBalance === 'object') {
            setTokenBalance({
              base_tokens: result.tokenBalance.base_tokens ?? 0,
              extra_tokens: result.tokenBalance.extra_tokens ?? 0,
              used_tokens: result.tokenBalance.used_tokens ?? 0,
              remaining_tokens: result.tokenBalance.remaining_tokens ?? 0,
              total_tokens: (result.tokenBalance.base_tokens ?? 0) + (result.tokenBalance.base_tokens ?? 0),
              usage_percentage: result.tokenBalance.usage_percentage ?? 0,
              weekly_tokens_remaining: result.tokenBalance.weekly_tokens_remaining ?? -1,
              monthly_tokens_remaining: result.tokenBalance.monthly_tokens_remaining ?? -1
            });
          }

          // If we got an initial message from the API (rare, but possible), add it
          if (result.initialMessage) {
            const trimmedAI = result.initialMessage.trim();
            lastWebSocketMessageRef.current = { content: trimmedAI, timestamp: Date.now() };
            const aiMsg = {
              id: Date.now() + Math.random(),
              type: 'avatar',
              content: trimmedAI,
              time: new Date().toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }),
              senderType: result.senderType || 'Avatar'
            };
            setMessages(prev => [...prev, aiMsg]);
          }
          checkChatLimitWarning();
          setIsTyping(false);
          return; // First message already handled by POST; WebSocket is for future messages
        } else {
          console.error('Failed to create chat session');
          rollbackOptimistic();
          setIsTyping(false);
          toast.error('Failed to start chat session');
        }
        creatingSessionRef.current = false;
      } else if (isJustUs) {
        // Just Us (human-only) group chat — send via REST, no WebSocket
        const token = localStorage.getItem('access_token');
        const apiUrl = (configApiUrl || process.env.REACT_APP_API_URL || 'https://sayagainservices.sayagain.ai').replace(/\/$/, '');
        try {
          const res = await fetch(`${apiUrl}/api/chat/${chatSessionId}/message`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`,
            },
            body: JSON.stringify({ message: messageText }),
          });
          if (!res.ok) {
            const errData = await res.json().catch(() => ({}));
            console.error('❌ Just Us message send failed:', errData);
            rollbackOptimistic();
            toast.error('Failed to send message. Please try again.');
          }
          // Optimistic message is already in state — no need to add again
        } catch (err) {
          console.error('❌ Just Us message send error:', err);
          rollbackOptimistic();
          toast.error('Failed to send message. Please try again.');
        }
      } else {
        // Send message via existing WebSocket connection
        console.log('Checking WebSocket connection for sending message...');
        debugWebSocketStatus();

        if (isWsConnected && wsConnection && wsConnection.readyState === WebSocket.OPEN) {
          console.log('✅ WebSocket is connected, sending message directly');
          console.log('WebSocket readyState:', wsConnection.readyState, 'isWsConnected:', isWsConnected);
          const sent = sendWebSocketMessage(messageText);
          if (sent) {
            setTimeout(checkChatLimitWarning, 2000);
          }
          if (!sent) {
            console.error('❌ Failed to send message via WebSocket');
            rollbackOptimistic();
            setIsTyping(false);
            toast.error('Failed to send message. Please try again.');
          }
        } else {
          // Try to reconnect and send
          console.log('🔄 WebSocket not connected, attempting to reconnect...');
          console.log('Current state - isWsConnected:', isWsConnected, 'wsConnection exists:', !!wsConnection);
          if (wsConnection) {
            console.log('WebSocket readyState:', wsConnection.readyState, 'Status:', getWebSocketStatus());
          }

          if (chatSessionId) {
            connectWebSocket(chatSessionId);

            // Wait for connection with timeout
            let attempts = 0;
            const maxAttempts = 10; // 5 seconds max wait

            const checkConnection = () => {
              attempts++;
              console.log(`⏳ Connection attempt ${attempts}/${maxAttempts}, status: ${getWebSocketStatus()}`);
              console.log(`   ReadyState: ${currentWsRef.current ? currentWsRef.current.readyState : 'null'}, isWsConnected: ${isWsConnected}`);

              // Check WebSocket readyState using ref (immediate) or state variable (fallback)
              const wsToCheck = currentWsRef.current || wsConnection;
              if (wsToCheck && wsToCheck.readyState === WebSocket.OPEN) {
                console.log('✅ Reconnected successfully, sending message');
                const sent = sendWebSocketMessage(messageText);
                if (sent) {
                  setTimeout(checkChatLimitWarning, 2000);
                }
                if (!sent) {
                  console.error('❌ Failed to send message after reconnection');
                  rollbackOptimistic();
                  setIsTyping(false);
                  toast.error('Failed to send message. Please try again.');
                }
              } else if (attempts >= maxAttempts) {
                console.error('❌ Failed to reconnect WebSocket after maximum attempts');
                console.log('Final state - WebSocket:', getWebSocketStatus());
                console.log('Final state - isWsConnected:', isWsConnected);
                console.log('Final state - currentWsRef readyState:', currentWsRef.current ? currentWsRef.current.readyState : 'null');
                console.log('Final state - wsConnection readyState:', wsConnection ? wsConnection.readyState : 'null');
                rollbackOptimistic();
                setIsTyping(false);
                toast.error('Connection lost. Please try again.');
              } else {
                setTimeout(checkConnection, 500);
              }
            };

            setTimeout(checkConnection, 500);
          } else {
            console.error('❌ No chat session ID available for reconnection');
            rollbackOptimistic();
            setIsTyping(false);
            toast.error('No active chat session. Please start a new conversation.');
          }
        }
      }
    } catch (error) {
      console.error('Error sending message:', error);
      rollbackOptimistic();
      setIsTyping(false);
      creatingSessionRef.current = false;
      const msg = error?.message || '';
      const msgLower = msg.toLowerCase();
      // Check for weekly limit first (specific handler)
      if (msgLower.includes('weekly') || msgLower.includes('comfort')) {
        handleWeeklyLimitReached();
        return;
      }
      // Check for token limit OR monthly limit
      if (msgLower.includes('token limit reached') || msgLower.includes('monthly')) {
        handleTokenLimitReached();
        return;
      }
      if (msg.toLowerCase().includes('not a member of this project') || msg.toLowerCase().includes('not a member')) {
        toast.error("You're not a member of this project. Ask a group admin to add you to the group, then try again or refresh the page.");
      } else {
        toast.error(msg || 'Failed to send message. Please try again.');
      }
    }
  };


  // Sync chatAutoRenew from user subscription when user data arrives
  useEffect(() => {
    if (user?.subscription?.auto_renew !== undefined) {
      setChatAutoRenew(!!user.subscription.auto_renew);
    }
  }, [user]);

  // Close profile menu when clicking outside
  useEffect(() => {
    if (!showChatProfileMenu) return;
    const handler = (e) => {
      if (chatProfileMenuRef.current && !chatProfileMenuRef.current.contains(e.target)) {
        setShowChatProfileMenu(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showChatProfileMenu]);

  const getChatPlanDetails = () => {
    const subscription = user?.subscription;
    const planDetails = user?.plan_details || user?.plan;
    const planInterval = subscription?.plan_interval || planDetails?.plan_interval || planDetails?.interval;
    const planPrice = planDetails?.price_usd !== undefined
      ? `$${planDetails.price_usd}/${planInterval === 'yearly' || planInterval === 'year' ? 'year' : 'month'}`
      : (planInterval === 'yearly' || planInterval === 'year' ? '$149/year' : planInterval === 'monthly' ? '$19.99/month' : null);
    const planName = planDetails?.name || null;
    const currentAutoRenew = chatAutoRenew !== null ? chatAutoRenew : !!(subscription?.auto_renew);
    const hasSubscription = !!(subscription || planName);
    return { planName, planInterval, planPrice, currentAutoRenew, hasSubscription };
  };

  const handleChatToggleAutoRenew = async () => {
    const primaryAvatarId = avatars[0]?.id;
    if (!primaryAvatarId) return;
    const next = !chatAutoRenew;
    try {
      setChatAutoRenew(next);
      await avatarsAPI.updateAvatarProfile(primaryAvatarId, { auto_renew: next });
    } catch {
      setChatAutoRenew(!next);
      toast.error('Failed to update auto-renew setting');
    }
  };

  const handleChatCancelSubscription = async () => {
    setCancellingSubscription(true);
    try {
      await avatarsAPI.cancelSubscription();
      setShowCancelConfirmModal(false);
      setShowChatProfileMenu(false);
    } catch {
      toast.error('Failed to cancel subscription. Please try again.');
    } finally {
      setCancellingSubscription(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('chat_session_id');
    localStorage.removeItem('chat_session_project_id');
    localStorage.removeItem('chat_messages');
    localStorage.removeItem('signup_invite_params'); // Crucial: clear invite state on logout
    sessionStorage.clear(); // Clear cached session state
    navigate('/');
  };


  // Block only on the initial user data fetch.
  // Once we have a user object, let the layout render so the user sees the sidebars and navigation immediately.
  if (!user) {
    return <LoadingSpinner fullScreen text="Loading your account..." />;
  }

  return (
    <div className="chat-page">
      {/* Sidebar */}
      <aside className="chat-sidebar">
        <div className="sidebar-header">
          <Link to="/" className="sidebar-logo">
            <img src={process.env.PUBLIC_URL + '/Sayagain.png'} alt="SayAgain" className="sidebar-logo-img" />
          </Link>
        </div>

        {/* New Chat Button */}
        <button className="new-chat-btn" disabled={isChatInputDisabled} onClick={() => {
          if (isChatInputDisabled) {
            handleTokenLimitReached();
            return;
          }
          setNewChatType(null);
          setNewChatMemberStep(false);
          setNewChatPickedMembers([]);
          setShowNewChatModal(true);
        }}>
          <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
          </svg>
          New Chat
        </button>

        {/* Chat Search Input */}
        {/* <div className="chat-search-container" style={{ padding: '0.5rem 1rem' }}>
          <input
            type="text"
            className="chat-search-input"
            placeholder="Search chats"
            value={chatSearchQuery}
            onChange={(e) => setChatSearchQuery(e.target.value)}
            style={{
              width: '100%',
              padding: '0.5rem 0.75rem',
              background: 'var(--surface)',
              border: '1px solid var(--border-subtle)',
              borderRadius: '6px',
              color: 'var(--text)',
              fontSize: '0.85rem'
            }}
          />
        </div> */}

        {/* Single scrollable area for left panel (ChatGPT-style) */}
        <div className="chat-sidebar-scroll">
          {/* Your History Section - ChatGPT Style */}
          <div className="chat-history-section">
            <div className="chat-history-header">
              <div className="chat-history-label">Private Chats</div>
            </div>

            <div className="chat-history-scroll">
              {chatSessionsLoading && chatSessions.length === 0 ? (
                <div style={{ padding: '1rem', textAlign: 'center' }}>
                  <Spinner animation="border" size="sm" />
                  <div style={{ fontSize: '0.8rem', color: 'var(--muted)', marginTop: '0.5rem' }}>
                    Loading chats...
                  </div>
                </div>
              ) : getFilteredSessions().length > 0 ? (
                <>
                  <div className="chat-history-list" style={{ flex: 1 }}>
                    {getFilteredSessions().map((session) => {
                      // Handle both string and number comparison
                      const isActive = String(session.session_id) === String(chatSessionId) && chatSessionId !== null;
                      return (
                        <div
                          key={session.session_id}
                          className={`chat-history-item ${isActive ? 'active' : ''}`}
                          onClick={() => {
                            // Don't select session if currently editing
                            if (editingSessionId === session.session_id) return;
                            setSelectedProject(null);
                            saveSessionProjectId(null);
                            setChatSessionId(session.session_id);
                            setChatSessionKey(prev => prev + 1);
                            saveSessionId(session.session_id);
                            loadSessionMessages(session.session_id);
                            connectWebSocket(session.session_id);
                          }}
                          style={{
                            cursor: 'pointer',
                            borderLeft: isActive ? '3px solid var(--soft-teal)' : '3px solid transparent',
                            backgroundColor: isActive ? 'rgba(107, 158, 160, 0.1)' : 'transparent',
                            fontWeight: isActive ? '600' : '400',
                            transition: 'background-color 0.2s ease, border-color 0.2s ease'
                          }}
                          onMouseEnter={(e) => {
                            if (!isActive) {
                              e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.03)';
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (!isActive) {
                              e.currentTarget.style.backgroundColor = 'transparent';
                            }
                          }}
                        >

                          <div className="chat-history-content">
                            {editingSessionId === session.session_id ? (
                              <input
                                type="text"
                                value={editingSessionTitle}
                                onChange={(e) => setEditingSessionTitle(e.target.value)}
                                onClick={(e) => e.stopPropagation()}
                                onBlur={() => {
                                  if (editingSessionTitle.trim()) {
                                    updateChatSessionTitle(session.session_id, editingSessionTitle);
                                  } else {
                                    setEditingSessionId(null);
                                    setEditingSessionTitle('');
                                  }
                                }}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    if (editingSessionTitle.trim()) {
                                      updateChatSessionTitle(session.session_id, editingSessionTitle);
                                    }
                                  } else if (e.key === 'Escape') {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    setEditingSessionId(null);
                                    setEditingSessionTitle('');
                                  }
                                }}
                                autoFocus
                                disabled={updatingSessionTitle}
                                style={{
                                  fontSize: '0.9rem',
                                  fontWeight: '500',
                                  width: '100%',
                                  padding: '2px 4px',
                                  border: '1px solid var(--soft-teal)',
                                  borderRadius: '4px',
                                  background: 'var(--warm-white)',
                                  color: 'var(--text)',
                                  outline: 'none'
                                }}
                              />
                            ) : (
                              <div className="chat-history-title" style={{
                                fontSize: '0.9rem',
                                fontWeight: isActive ? '600' : '500',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                color: isActive ? 'var(--soft-teal)' : 'var(--text)'
                              }}>
                                {session.title || 'Untitled'}
                              </div>
                            )}
                          </div>
                          <div className="session-menu-wrapper" style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: '2px', flexShrink: 0 }}>
                            <button
                              type="button"
                              className="chat-list-item-add-btn"
                              title="Chat options"
                              onClick={(e) => {
                                e.stopPropagation();
                                setSessionMenuOpenId((prev) => (prev === session.session_id ? null : session.session_id));
                              }}
                              disabled={deletingSessionId === session.session_id}
                              style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '6px' }}
                            >
                              {deletingSessionId === session.session_id ? (
                                <Spinner animation="border" size="sm" style={{ width: 14, height: 14 }} />
                              ) : (
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                                  <circle cx="12" cy="5" r="1.5" />
                                  <circle cx="12" cy="12" r="1.5" />
                                  <circle cx="12" cy="19" r="1.5" />
                                </svg>
                              )}
                            </button>
                            {sessionMenuOpenId === session.session_id && (
                              <div
                                className="project-menu-dropdown"
                                style={{
                                  position: 'absolute',
                                  top: '100%',
                                  right: 0,
                                  marginTop: '4px',
                                  background: 'var(--warm-white)',
                                  border: '1px solid var(--border-subtle)',
                                  borderRadius: '8px',
                                  boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
                                  zIndex: 100,
                                  minWidth: '140px',
                                  overflow: 'hidden',
                                }}
                              >
                                <button
                                  type="button"
                                  className="project-menu-item"
                                  style={{
                                    display: 'block',
                                    width: '100%',
                                    padding: '10px 14px',
                                    textAlign: 'left',
                                    border: 'none',
                                    background: 'none',
                                    cursor: 'pointer',
                                    fontSize: '13px',
                                    color: 'var(--slate)',
                                  }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setSessionMenuOpenId(null);
                                    setEditingSessionId(session.session_id);
                                    setEditingSessionTitle(session.title || 'Untitled');
                                  }}
                                >
                                  Rename
                                </button>
                                <button
                                  type="button"
                                  className="project-menu-item project-menu-item-danger"
                                  style={{
                                    display: 'block',
                                    width: '100%',
                                    padding: '10px 14px',
                                    textAlign: 'left',
                                    border: 'none',
                                    background: 'none',
                                    cursor: 'pointer',
                                    fontSize: '13px',
                                    color: '#c53030',
                                  }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setSessionMenuOpenId(null);
                                    setSessionToDelete({ session_id: session.session_id, title: session.title || 'Untitled' });
                                    setShowDeleteChatModal(true);
                                  }}
                                >
                                  Delete
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </>
              ) : (
                <div className="chat-history-empty">
                  <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                  </svg>
                  <span>
                    {chatSearchQuery ? 'No chats match your search' : 'No chats yet. Start a new chat or open by session ID above.'}
                  </span>
                </div>
              )}
            </div>
          </div>

          <div className="chat-history-header" style={{ marginTop: '16px', marginLeft: '16px', marginRight: '16px' }}>
            <div className="chat-history-label">Group Chats</div>
          </div>
          <div className="chat-list">
            {loadingProjects ? (
              <div style={{ padding: '12px', textAlign: 'center', fontSize: '12px', color: '#666' }}>
                <Spinner animation="border" size="sm" />
              </div>
            ) : projects.length > 0 ? (
              projects.map((project) => (
                <div
                  key={project.id}
                  className={`chat-list-item ${selectedProject?.id === project.id ? 'active' : ''}`}
                  onClick={() => handleSelectProject(project)}
                >
                  <div
                    className="chat-list-icon"
                    style={{ background: 'linear-gradient(135deg, var(--soft-teal), var(--light-teal))' }}
                  >
                    <span style={{ color: 'white', fontWeight: 700 }}>
                      {(project.name || 'C').charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <div className="chat-list-info" style={{ minWidth: 0, flex: 1 }}>
                    <h4 style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {project.name}
                    </h4>
                    <p>
                      {(() => {
                        // project.members = ProjectMember rows (non-creators only)
                        // +1 always for the creator who has no ProjectMember row
                        const count = (project.members || []).length + 1;
                        return `${count} member${count !== 1 ? 's' : ''}`;
                      })()}
                    </p>
                  </div>
                  <div className="chat-list-item-actions" style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
                    {canManageProjectMembers(project) && (
                      <button
                        type="button"
                        className="chat-list-item-add-btn"
                        title="Add members"
                        onClick={(e) => {
                          e.stopPropagation();
                          openAddMembersModal(project);
                        }}
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 5v14m7-7H5" />
                        </svg>
                      </button>
                    )}
                    <div className="project-menu-wrapper" style={{ position: 'relative' }}>
                      <button
                        type="button"
                        className="chat-list-item-add-btn"
                        title="Group options"
                        onClick={(e) => {
                          e.stopPropagation();
                          setProjectMenuOpenId((prev) => (prev === project.id ? null : project.id));
                        }}
                        disabled={deletingProjectId === project.id || leavingProjectId === project.id}
                      >
                        {deletingProjectId === project.id || leavingProjectId === project.id ? (
                          <Spinner animation="border" size="sm" style={{ width: 14, height: 14 }} />
                        ) : (
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                            <circle cx="12" cy="5" r="1.5" />
                            <circle cx="12" cy="12" r="1.5" />
                            <circle cx="12" cy="19" r="1.5" />
                          </svg>
                        )}
                      </button>
                      {projectMenuOpenId === project.id && (
                        <div
                          className="project-menu-dropdown"
                          style={{
                            position: 'absolute',
                            top: '100%',
                            right: 0,
                            marginTop: '4px',
                            background: 'var(--warm-white)',
                            border: '1px solid var(--border-subtle)',
                            borderRadius: '8px',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
                            zIndex: 100,
                            minWidth: '140px',
                            overflow: 'hidden',
                          }}
                        >
                          {isProjectOwner(project) && (
                            <button
                              type="button"
                              className="project-menu-item"
                              style={{
                                display: 'block',
                                width: '100%',
                                padding: '10px 14px',
                                textAlign: 'left',
                                border: 'none',
                                background: 'none',
                                cursor: 'pointer',
                                fontSize: '13px',
                                color: 'var(--slate)',
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                setProjectMenuOpenId(null);
                                setProjectToRename(project);
                                setRenameProjectName(project.name || '');
                                setShowRenameProjectModal(true);
                              }}
                            >
                              Rename Group
                            </button>
                          )}
                          <button
                            type="button"
                            className="project-menu-item"
                            style={{
                              display: 'block',
                              width: '100%',
                              padding: '10px 14px',
                              textAlign: 'left',
                              border: 'none',
                              background: 'none',
                              cursor: 'pointer',
                              fontSize: '13px',
                              color: 'var(--slate)',
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              setProjectToLeave(project);
                              setShowLeaveProjectModal(true);
                            }}
                          >
                            Leave Group
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div style={{ padding: '12px 24px', fontSize: '12px', color: '#666' }}>
                No conversations yet
              </div>
            )}
          </div>
        </div>
        {/* End chat-sidebar-scroll */}

        {/* Members List - collapse/expand with arrow */}
        <div className="sidebar-addons" style={{ marginTop: '16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px' }}>
            <button
              type="button"
              className="members-section-toggle"
              onClick={() => setMembersSectionExpanded((prev) => !prev)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                background: 'none',
                border: 'none',
                padding: 0,
                cursor: 'pointer',
                font: 'inherit',
                color: 'inherit'
              }}
              title={membersSectionExpanded ? 'Collapse members' : 'Show members'}
            >
              <span className="sidebar-addons-label" style={{ marginBottom: 0 }}>
                Members
              </span>
              <span style={{ display: 'inline-flex', alignItems: 'center', color: 'var(--slate)' }}>
                {membersSectionExpanded ? (
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 15l7-7 7 7" />
                  </svg>
                ) : (
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                  </svg>
                )}
              </span>
            </button>
            <button
              type="button"
              className="members-add-btn"
              onClick={(e) => {
                e.stopPropagation();
                setShowAddMemberModal(true);
              }}
              title="Add family members"
              style={{
                background: 'none',
                border: 'none',
                padding: '4px',
                cursor: 'pointer',
                color: 'var(--slate, #64748b)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 5v14m7-7H5" />
              </svg>
            </button>
          </div>
          {membersSectionExpanded && (
            <div className="sidebar-addons-members" style={{ marginTop: '8px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {selectedProject ? (
                <>
                  {(() => {
                    // Merge project members + group co-members + avatarMembers (AvatarMemberRelation).
                    // avatarMembers covers i_will_pay=0 invitees who have no ProjectMember row.
                    const _currentUid = user?.id || user?.user_id;
                    const projectMembers = (Array.isArray(selectedProject.members) ? selectedProject.members : [])
                      .filter(m => String(m.user_id ?? m.id) !== String(_currentUid));
                    const seenIds = new Set(projectMembers.map((m) => m.user_id ?? m.id).filter(Boolean));
                    if (_currentUid) seenIds.add(_currentUid); // always exclude self
                    const extraGroupMembers = (groupMembers || []).filter((m) => {
                      const uid = m.user_id ?? m.id;
                      if (!uid || seenIds.has(uid)) return false;
                      seenIds.add(uid);
                      return true;
                    });
                    const extraAvatarMembers = (avatarMembers || []).filter((m) => {
                      const uid = m.user_id ?? m.id;
                      if (!uid || seenIds.has(uid)) return false;
                      seenIds.add(uid);
                      return true;
                    });
                    const members = [...projectMembers, ...extraGroupMembers, ...extraAvatarMembers];
                    if (members.length === 0) {
                      return (
                        <div style={{ padding: '12px', fontSize: '12px', color: '#666' }}>
                          No members yet
                        </div>
                      );
                    }

                    const visibleMembers = sidebarMembersExpanded ? members : members.slice(0, 1);
                    const hiddenCount = Math.max(0, members.length - visibleMembers.length);

                    const renderMemberRow = (m) => (
                      <div
                        key={m.id || m.email || m.name}
                        role="button"
                        tabIndex={0}
                        onClick={() => handleMemberClick(m)}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleMemberClick(m); } }}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: '10px',
                          padding: '10px 12px',
                          borderRadius: '10px',
                          background: 'rgba(255,255,255,0.55)',
                          border: '1px solid rgba(0,0,0,0.05)',
                          cursor: 'pointer'
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0 }}>
                          <div
                            style={{
                              width: '30px',
                              height: '30px',
                              borderRadius: '50%',
                              background: 'linear-gradient(135deg, var(--soft-teal), var(--light-teal))',
                              color: 'white',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '12px',
                              fontWeight: '700',
                              flexShrink: 0
                            }}
                          >
                            {(m.initials || (m.name || m.email || 'M').substring(0, 2)).toUpperCase()}
                          </div>
                          <div style={{ minWidth: 0 }}>
                            <div
                              style={{
                                fontSize: '13px',
                                fontWeight: 600,
                                color: 'var(--soft-black)',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                              }}
                            >
                              {m.name || m.email || 'Member'}
                            </div>
                            {m.email && (
                              <div
                                style={{
                                  fontSize: '11px',
                                  color: 'var(--slate)',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap'
                                }}
                              >
                                {m.email}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );

                    return (
                      <>
                        {visibleMembers.map(renderMemberRow)}
                        {hiddenCount > 0 && (
                          <button
                            type="button"
                            onClick={() => setSidebarMembersExpanded(true)}
                            style={{
                              width: '100%',
                              padding: '10px 12px',
                              borderRadius: '10px',
                              border: '1px dashed rgba(0,0,0,0.12)',
                              background: 'rgba(255,255,255,0.45)',
                              color: 'var(--slate)',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              gap: '10px',
                              fontSize: '12px',
                              fontWeight: 600
                            }}
                            title="Show all members"
                          >
                            <span>{`+${hiddenCount} more`}</span>
                            <span style={{ fontSize: '14px' }}>▼</span>
                          </button>
                        )}
                        {sidebarMembersExpanded && members.length > 1 && (
                          <button
                            type="button"
                            onClick={() => setSidebarMembersExpanded(false)}
                            style={{
                              width: '100%',
                              padding: '10px 12px',
                              borderRadius: '10px',
                              border: '1px solid rgba(0,0,0,0.08)',
                              background: 'rgba(255,255,255,0.6)',
                              color: 'var(--slate)',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              gap: '10px',
                              fontSize: '12px',
                              fontWeight: 600
                            }}
                            title="Collapse"
                          >
                            <span>Show less</span>
                            <span style={{ fontSize: '14px' }}>▲</span>
                          </button>
                        )}
                      </>
                    );
                  })()}
                </>
              ) : (
                <>
                  {loadingAvatarMembers ? (
                    <div style={{ padding: '12px', fontSize: '12px', color: '#666' }}>
                      Loading...
                    </div>
                  ) : (() => {
                    // Combine avatar members and invitee members, avoiding duplicates by email
                    const allMembersMap = new Map();

                    // Add avatar members first
                    avatarMembers.forEach(m => {
                      const email = (m.email || '').toLowerCase();
                      if (email) {
                        allMembersMap.set(email, m);
                      } else {
                        allMembersMap.set(m.id || m.name, m);
                      }
                    });

                    // Add invitee members (will override if same email exists, or add new)
                    inviteeMembers.forEach(m => {
                      const email = (m.email || '').toLowerCase();
                      if (email && !allMembersMap.has(email)) {
                        allMembersMap.set(email, m);
                      } else if (!email) {
                        const key = m.id || m.name;
                        if (!allMembersMap.has(key)) {
                          allMembersMap.set(key, m);
                        }
                      }
                    });

                    // Add group co-members (family group members with no avatar relation or invitee row)
                    (groupMembers || []).forEach(m => {
                      const email = (m.email || '').toLowerCase();
                      if (email && !allMembersMap.has(email)) {
                        allMembersMap.set(email, m);
                      } else if (!email) {
                        const key = m.user_id || m.id || m.name;
                        if (key && !allMembersMap.has(key)) {
                          allMembersMap.set(key, m);
                        }
                      }
                    });

                    const _selfUid = user?.id || user?.user_id;
                    const _selfEmail = (user?.email || '').toLowerCase();
                    const allMembers = Array.from(allMembersMap.values()).filter(m => {
                      if (_selfUid && String(m.user_id ?? m.id) === String(_selfUid)) return false;
                      if (_selfEmail && (m.email || '').toLowerCase() === _selfEmail) return false;
                      return true;
                    });
                    return allMembers.length > 0 ? (
                      allMembers.map((m) => (
                        <div
                          key={m.id || m.email || m.name}
                          role="button"
                          tabIndex={0}
                          onClick={() => handleMemberClick(m)}
                          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleMemberClick(m); } }}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '10px',
                            padding: '10px 12px',
                            borderRadius: '10px',
                            background: 'rgba(255,255,255,0.55)',
                            border: '1px solid rgba(0,0,0,0.05)',
                            cursor: 'pointer'
                          }}
                        >
                          <div style={{
                            width: '30px',
                            height: '30px',
                            borderRadius: '50%',
                            background: 'linear-gradient(135deg, var(--soft-teal), var(--light-teal))',
                            color: 'white',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '12px',
                            fontWeight: '700',
                            flexShrink: 0
                          }}>
                            {m.initials || 'M'}
                          </div>
                          <div style={{ minWidth: 0 }}>
                            <div style={{
                              fontSize: '13px',
                              fontWeight: 600,
                              color: 'var(--soft-black)',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}>
                              {m.name || m.email || 'Member'}
                            </div>
                            {m.email && (
                              <div style={{
                                fontSize: '11px',
                                color: 'var(--slate)',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                              }}>
                                {m.email}
                              </div>
                            )}
                          </div>
                        </div>
                      ))
                    ) : (
                      <div style={{ padding: '12px', fontSize: '12px', color: '#666' }}>
                        No accepted members yet
                      </div>
                    );
                  })()}
                </>
              )}
            </div>
          )}
        </div>

        {/* Dashboard Summary Section */}
        {dashboardSummary && (
          <div className="sidebar-addons" style={{ marginTop: '20px', padding: '15px', backgroundColor: 'rgba(0,0,0,0.05)', borderRadius: '8px' }}>
            <div className="sidebar-addons-label">Dashboard Summary</div>
            <div style={{ fontSize: '12px', color: '#666', marginTop: '10px' }}>
              {loadingPlans ? (
                <div>Loading plans...</div>
              ) : plans.length > 0 ? (
                <div>
                  <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>Plans Available: {plans.length}</div>
                  {plans.slice(0, 3).map((plan, idx) => (
                    <div key={idx} style={{ marginBottom: '5px', padding: '5px', backgroundColor: 'rgba(255,255,255,0.5)', borderRadius: '4px' }}>
                      <div style={{ fontWeight: '600' }}>{plan.name || plan.plan_name || `Plan ${idx + 1}`}</div>
                      {plan.price_usd && <div style={{ fontSize: '11px' }}>${plan.price_usd}</div>}
                    </div>
                  ))}
                  {plans.length > 3 && <div style={{ fontSize: '11px', color: '#999' }}>+{plans.length - 3} more plans</div>}
                </div>
              ) : (
                <div>No plans data available</div>
              )}
            </div>
          </div>
        )}
      </aside>

      {/* Main Chat Area */}
      <main className="chat-main">
        {/* Add-on purchase confirmation banner */}
        {addonConfirmed && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            padding: '10px 20px',
            background: 'linear-gradient(90deg, #1D9E75, #2bb890)',
            color: '#fff',
            fontSize: '14px',
            fontWeight: 500,
            animation: 'fadeIn 0.3s ease',
          }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="20 6 9 17 4 12" />
            </svg>
            Your conversations have been extended — enjoy!
          </div>
        )}
        {/* Show chat UI when a project (group) or avatar is selected */}
        {(selectedProject || selectedAvatar) ? (
          <>
            {/* Avatar Chat Header */}
            <header className="chat-header">
              <div
                className="chat-header-left"
                style={{ cursor: 'default' }}
              >
                <div className="chat-header-info">
                  <h3 style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                    {selectedProject ? (
                      <>
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {selectedProject.name || 'Group chat'}
                        </span>
                        {/* Avatar pill — teal outline, no fill */}
                        {!isJustUsProject(selectedProject.id) && selectedAvatar && (
                          <span style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            border: '1.5px solid #1D9E75',
                            borderRadius: '999px',
                            color: '#1D9E75',
                            fontSize: '12px',
                            fontWeight: 500,
                            padding: '1px 10px',
                            whiteSpace: 'nowrap',
                            flexShrink: 0,
                          }}>
                            {[selectedAvatar.first_name, selectedAvatar.last_name].filter(Boolean).join(' ')}
                          </span>
                        )}
                        {/* Human member full names — exclude the viewing user */}
                        {(() => {
                          const _uid = user?.id || user?.user_id;
                          const otherMembers = (selectedProject.members || []).filter(m => String(m.user_id ?? m.id) !== String(_uid));
                          if (otherMembers.length === 0) return null;
                          return (
                            <span style={{ fontWeight: 400, fontSize: '13px', color: 'var(--slate, #64748b)', whiteSpace: 'nowrap' }}>
                              {otherMembers.map(m => m.name || m.email || 'Member').join(', ')}
                            </span>
                          );
                        })()}
                      </>
                    ) : (
                      <>
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {selectedAvatar
                            ? `${selectedAvatar?.first_name || ''} ${selectedAvatar?.last_name || ''}`.trim() || 'Avatar'
                            : 'Chat'}
                        </span>
                        {/* Improve Avatar (lightbulb) — solo avatar chat only */}
                        <button className="icon-btn icon-btn--inline" title="Improve Avatar" onClick={() => setShowImproveModal(true)}>
                          <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" width="18" height="18">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                          </svg>
                        </button>
                      </>
                    )}
                  </h3>
                  <p>
                    {selectedProject
                      ? '● Group chat'
                      : `● Avatar Active${selectedAvatar?.confidence ? ` • ${selectedAvatar.confidence}% confidence` : ''}`}
                  </p>
                </div>
              </div>
              <div className="chat-header-actions">
                {/* User profile icon + dropdown */}
                <div className="profile-menu-wrapper" ref={chatProfileMenuRef}>
                  <button
                    className="profile-menu-trigger"
                    onClick={() => setShowChatProfileMenu(prev => !prev)}
                    title="Account"
                  >
                    <div className="profile-avatar-circle">
                      {getUserInitials()}
                    </div>
                  </button>

                  {showChatProfileMenu && (() => {
                    const { planName, planInterval, planPrice, currentAutoRenew, hasSubscription } = getChatPlanDetails();
                    return (
                      <div className="profile-menu-dropdown">
                        {/* 1. User info */}
                        <div className="profile-menu-section">
                          <div className="profile-menu-user">
                            <div className="profile-menu-avatar-circle">
                              {getUserInitials()}
                            </div>
                            <div className="profile-menu-user-info">
                              <div className="profile-menu-name">
                                {`${user?.first_name || ''} ${user?.last_name || ''}`.trim() || 'Account'}
                              </div>
                              <div className="profile-menu-email">{user?.email || ''}</div>
                            </div>
                          </div>
                        </div>

                        {/* 2. Plan & Billing link */}
                        <div className="profile-menu-section">
                          <button
                            className="profile-menu-action"
                            onClick={() => { setShowChatProfileMenu(false); setShowBillingModal(true); if (!addons || addons.length === 0 || !avatarPlans || avatarPlans.length === 0) { fetchAddons(); } }}
                            style={{ width: '100%' }}
                          >
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <rect x="1" y="4" width="22" height="16" rx="2" ry="2"></rect>
                              <line x1="1" y1="10" x2="23" y2="10"></line>
                            </svg>
                            <span>Plan &amp; Billing</span>
                          </button>
                        </div>

                        {/* 3. Avatars list */}
                        <div className="profile-menu-section">
                          <div className="profile-menu-section-title">Avatars</div>
                          <div className="profile-menu-avatars">
                            {avatars.length > 0 ? (
                              avatars.map((avatar) => (
                                <div
                                  key={avatar.id}
                                  className="profile-menu-avatar-item"
                                  onClick={() => { setShowChatProfileMenu(false); setSelectedAvatar(avatar); }}
                                  style={{ cursor: 'pointer' }}
                                >
                                  <div className="profile-menu-avatar-initials">
                                    {`${avatar.first_name?.charAt(0) || ''}${avatar.last_name?.charAt(0) || ''}`.toUpperCase()}
                                  </div>
                                  <div className="profile-menu-avatar-name">
                                    {`${avatar.first_name || ''} ${avatar.last_name || ''}`.trim()}
                                  </div>
                                  <div className="profile-menu-avatar-status">
                                    {avatar.status === 'active' ? '● Active' : ''}
                                  </div>
                                </div>
                              ))
                            ) : (
                              <div className="profile-menu-no-avatars">No avatars yet</div>
                            )}
                          </div>
                        </div>

                        {/* 4. Logout */}
                        <div className="profile-menu-actions">
                          <button onClick={handleLogout} className="profile-menu-action profile-menu-logout">
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
                              <polyline points="16 17 21 12 16 7"></polyline>
                              <line x1="21" y1="12" x2="9" y2="12"></line>
                            </svg>
                            <span>Logout</span>
                          </button>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              </div>
            </header>


            {/* Avatar Messages */}
            <div className={`chat-messages ${messages.length === 0 && !isTyping && !loadingData && !messagesLoading ? 'empty' : ''}`}>
              {loadingData || messagesLoading ? (
                <div className="chat-empty-state" style={{ justifyContent: 'center', padding: '2rem' }}>
                  <div className="loading-spinner-wrapper">
                    <Spinner animation="border" size="sm" />
                  </div>
                  <p className="empty-state-description" style={{ marginTop: '0.75rem' }}>Loading messages…</p>
                </div>
              ) : messages.length === 0 && !isTyping ? (
                <div className="chat-empty-state">
                  <div className="empty-state-icon">
                    <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                    </svg>
                  </div>
                  <h3 className="empty-state-title">Start a conversation</h3>
                  <p className="empty-state-description">
                    {selectedProject
                      ? `Start chatting in ${selectedProject.name || 'this group'}. Type a message below.`
                      : selectedAvatar
                        ? `Begin chatting with ${selectedAvatar.first_name || 'your avatar'}. Ask questions, share memories, or just say hello.`
                        : 'Begin chatting with your avatar. Ask questions, share memories, or just say hello.'}
                  </p>
                </div>
              ) : (
                <>
                  {messages.map((message) => {
                    // Get sender name for display (for group chats)
                    const senderName = message.senderName || message.senderType || null;
                    const isGroupChat = !!selectedProject;

                    // Get current user ID for comparison
                    const currentUserId = user?.id || user?.user_id || null;
                    const senderUserId = message.senderUserId || null;

                    // Check if this message is from the current logged-in user (only for group chats)
                    const isCurrentUserMessage = message.type === 'user' && (
                      !isGroupChat || (
                        senderUserId != null &&
                        currentUserId != null &&
                        String(senderUserId) === String(currentUserId)
                      )
                    );

                    // Get initials for user messages - use sender name if available
                    const getUserMessageInitials = () => {
                      if (senderName && message.type === 'user') {
                        const nameParts = senderName.trim().split(' ').filter(Boolean);
                        if (nameParts.length >= 2) {
                          return `${nameParts[0][0]}${nameParts[nameParts.length - 1][0]}`.toUpperCase();
                        } else if (nameParts.length === 1) {
                          return nameParts[0].substring(0, 2).toUpperCase();
                        }
                      }
                      return user ? getUserInitials() : 'U';
                    };

                    // Avatar messages always show on left side
                    const isAvatarMessage = message.type === 'avatar';

                    return (
                      <div
                        key={message.id}
                        className={`message ${message.type} ${isCurrentUserMessage ? 'message-own' : ''}`}
                        style={{
                          display: 'flex',
                          flexDirection: isCurrentUserMessage ? 'row-reverse' : 'row',
                          justifyContent: 'flex-start', // Start of the flow: Right for row-reverse, Left for row
                          alignItems: 'flex-start',
                          gap: '10px',
                          width: '100%',
                          marginBottom: '16px',
                        }}
                      >
                        <div className={`message-avatar ${message.type === 'user' ? 'gradient-gold' : 'gradient-sage'}`}>
                          {message.type === 'avatar'
                            ? getMessageAvatarDisplay(message)
                            : getUserMessageInitials()}
                        </div>
                        <div
                          className="message-body"
                          style={{
                            maxWidth: '85%',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: isCurrentUserMessage ? 'flex-end' : 'flex-start',
                            flex: '0 1 auto',
                            minWidth: '0'
                          }}
                        >
                          {isGroupChat && message.type === 'user' && senderName && !isCurrentUserMessage && (
                            <div className="message-sender-name" style={{
                              fontSize: '12px',
                              fontWeight: 600,
                              color: 'var(--slate)',
                              marginBottom: '4px'
                            }}>
                              {senderName}
                            </div>
                          )}
                          <div
                            className="message-content"
                            style={
                              message.type === 'user' ? {
                                // User messages
                                background: isCurrentUserMessage ? 'var(--soft-teal)' : 'rgba(255, 255, 255, 0.9)',
                                color: isCurrentUserMessage ? 'white' : 'var(--text)',
                                padding: '10px 14px',
                                borderRadius: '12px',
                                wordWrap: 'break-word',
                                textAlign: 'left',
                                border: isCurrentUserMessage ? 'none' : '1px solid rgba(0, 0, 0, 0.08)',
                                boxShadow: isCurrentUserMessage ? 'none' : '0 1px 2px rgba(0, 0, 0, 0.05)'
                              } : {
                                // Avatar messages
                                background: 'rgba(255, 255, 255, 0.9)',
                                color: 'var(--text)',
                                padding: '10px 14px',
                                borderRadius: '12px',
                                wordWrap: 'break-word',
                                textAlign: 'left',
                                border: '1px solid rgba(0, 0, 0, 0.08)',
                                boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)'
                              }
                            }
                          >
                            {(() => {
                              let content = message.content;
                              // Guard: if content is somehow a non-string object, extract its text
                              if (content !== null && content !== undefined && typeof content === 'object') {
                                content = content.message || content.content || content.text || content.detail || JSON.stringify(content);
                              }
                              if (!content) content = '';
                              // Ensure follow-up questions at the end of responses are visually separated
                              if (message.type === 'avatar') {
                                // Match the last sentence if it's a question and there's preceding text
                                const questionRegex = /([.!?]\s*)([A-Z][^.!?]*\?\s*)$/;
                                if (questionRegex.test(content)) {
                                  content = content.replace(questionRegex, '$1\n\n$2');
                                }
                              }
                              return content.split('\n').map((line, idx) => (
                                <React.Fragment key={idx}>
                                  {line}
                                  {idx < content.split('\n').length - 1 && <br />}
                                </React.Fragment>
                              ));
                            })()}
                          </div>
                          <div
                            className="message-time"
                            style={{
                              fontSize: '11px',
                              color: 'var(--muted)',
                              marginTop: '4px',
                              textAlign: isCurrentUserMessage ? 'right' : 'left'
                            }}
                          >
                            {message.time}
                          </div>
                        </div>
                      </div>
                    );
                  })}

                  {isTyping && (
                    <div className="message avatar">
                      <div className="message-avatar gradient-sage">
                        {selectedAvatar ? getAvatarInitials(selectedAvatar) : 'A'}
                      </div>
                      <div className="message-body">
                        <div className="message-content typing-indicator">
                          <Spinner animation="grow" size="sm" />
                          <Spinner animation="grow" size="sm" />
                          <Spinner animation="grow" size="sm" />
                        </div>
                      </div>
                    </div>
                  )}

                  <div ref={messagesEndRef} />
                </>
              )}
            </div>
          </>
        ) : null}

        {/* Improve modal quiet invite — shown whenever an avatar is present (private and group chats) */}
        {selectedAvatar && (
          <div className="chat-improve-invite">
            <button
              type="button"
              className="chat-improve-invite-link"
              onClick={() => setShowImproveModal(true)}
            >
              Help {selectedAvatar?.first_name || 'them'} remember
            </button>
          </div>
        )}

        {/* Show chat input when project or avatar is selected (group chat or avatar chat) */}
        {(selectedProject || selectedAvatar) && (
          <div className="chat-input-area">
            <Form onSubmit={handleSendMessage} className="chat-input-wrapper">
              <input
                type="file"
                multiple
                ref={fileInputRef}
                style={{ display: 'none' }}
                onChange={handleFileUpload}
                accept=".pdf,.txt,.md,.markdown,.log,.rtf,.sql,.xlsx,.csv,.docx,.odt,.pst,.mbox,.eml,.json,.zip"
              />
              <button
                type="button"
                className="upload-btn"
                disabled={isUploading || isChatInputDisabled}
                onClick={(e) => { e.preventDefault(); fileInputRef.current?.click(); }}
                title="Upload Files"
                style={{
                  marginRight: '8px',
                  flexShrink: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: 'transparent',
                  border: 'none',
                  color: 'var(--slate)',
                  cursor: 'pointer'
                }}
              >
                {isUploading ? <Spinner animation="border" size="sm" /> : (
                  <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" width="24" height="24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                  </svg>
                )}
              </button>
              <Form.Control
                as="textarea"
                ref={chatInputRef}
                rows={1}
                className="chat-input"
                placeholder={isChatInputDisabled ? 'Your conversations have reached their limit for now.' : 'Type a message...'}
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={handleKeyDown}
                disabled={isChatInputDisabled}
                style={{
                  resize: 'none',
                  minHeight: '52px',
                  overflowY: 'hidden'
                }}
              />
              {isTyping ? (
                <button
                  type="button"
                  className="stop-btn"
                  onClick={handleCancelResponse}
                  title="Stop generating"
                >
                  <div className="stop-icon" />
                </button>
              ) : (
                <button
                  type="submit"
                  className="send-btn"
                  disabled={!inputValue.trim() || isTyping || isChatInputDisabled}
                  title="Send message"
                >
                  <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                  </svg>
                </button>
              )}
            </Form>
          </div>
        )}
      </main>

      {/* Weekly Limit Modal - Shows when weekly_tokens_remaining hits 0 */}
      <CustomModal
        show={showWeeklyLimitModal}
        onHide={() => setShowWeeklyLimitModal(false)}
        size="md"
      >
        <div style={{ textAlign: 'center', padding: '8px 16px 24px' }}>
          <p style={{ fontSize: '16px', color: 'var(--soft-black)', lineHeight: '1.6', margin: '0 0 24px' }}>
            You've had a lot of conversations this week — we hope they've brought you some comfort. Your conversations with{' '}
            <strong>{selectedAvatar?.first_name || 'your loved one'}</strong> will continue again on{' '}
            {weeklyResetDate}, or{' '}
            <span
              onClick={() => {
                setShowWeeklyLimitModal(false);
                if (!addons || addons.length === 0) {
                  fetchAddons();
                }
                setShowTopUpConfirmModal(true);
              }}
              style={{ color: 'var(--soft-teal)', textDecoration: 'underline', cursor: 'pointer', whiteSpace: 'nowrap' }}
            >
              Keep the conversation going →
            </span>.
          </p>
          <CustomButton variant="secondary" onClick={() => setShowWeeklyLimitModal(false)}>
            I'll come back
          </CustomButton>
        </div>
      </CustomModal>

      {/* Top-Up Confirmation Modal - Warm, plain-language $5 top-up */}
      <CustomModal
        show={showTopUpConfirmModal}
        onHide={() => setShowTopUpConfirmModal(false)}
        size="md"
        iconVariant="sage"
        icon={
          <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
          </svg>
        }
      >
        <div style={{ textAlign: 'center', padding: '8px 16px 24px' }}>
          <p style={{ fontSize: '16px', color: 'var(--soft-black)', lineHeight: '1.7', margin: '0 0 8px' }}>
            Keeping the conversation going costs just <strong>$5.00</strong>.
          </p>
          <p style={{ fontSize: '15px', color: 'var(--slate)', lineHeight: '1.6', margin: '0 0 28px' }}>
            This is a one-time charge — so you can keep talking with{' '}
            <strong>{selectedAvatar?.first_name || 'your loved one'}</strong> and never affects your regular plan.
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '14px' }}>
            <CustomButton
              variant="primary"
              onClick={() => {
                setShowTopUpConfirmModal(false);
                const addon = addons && addons.length > 0 ? addons[0] : null;
                if (addon) {
                  handleAddonPayment(addon);
                } else {
                  fetchAddons().then(() => {
                    const freshAddon = addons && addons.length > 0 ? addons[0] : null;
                    if (freshAddon) handleAddonPayment(freshAddon);
                  });
                }
              }}
            >
              Continue for $5.00
            </CustomButton>
            <span
              onClick={() => setShowTopUpConfirmModal(false)}
              style={{ fontSize: '14px', color: 'var(--slate)', textDecoration: 'underline', cursor: 'pointer' }}
            >
              No thanks, I'll wait
            </span>
          </div>
        </div>
      </CustomModal>

      <CustomModal
        show={showTokenLimitModal}
        onHide={() => setShowTokenLimitModal(false)}
        title="Your conversations are paused"
        subtitle={`Your weekly conversations with ${selectedAvatar?.first_name || 'your loved one'} have reached their limit. You can add a little more for $5.00, or come back when your limit resets.`}
        size="md"
      >
        <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', marginTop: '24px' }}>
          <CustomButton variant="secondary" onClick={() => setShowTokenLimitModal(false)}>
            I'll come back
          </CustomButton>
          <CustomButton
            variant="primary"
            onClick={() => {
              setShowTokenLimitModal(false);
              if (!addons || addons.length === 0) {
                fetchAddons();
              }
              setShowTopUpConfirmModal(true);
            }}
          >
            Keep the conversation going
          </CustomButton>
        </div>
      </CustomModal>

      {/* Delete Group confirmation */}
      <CustomModal
        show={showDeleteProjectModal}
        onHide={() => { setShowDeleteProjectModal(false); setProjectToDelete(null); setProjectMenuOpenId(null); }}
        title="Delete Group"
        subtitle={projectToDelete ? `Are you sure you want to delete "${projectToDelete.name}"? This action cannot be undone.` : ''}
        size="md"
        iconVariant="rose"
        icon={
          <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
          </svg>
        }
      >
        {projectToDelete && (
          <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', marginTop: '24px' }}>
            <CustomButton variant="secondary" onClick={() => { setShowDeleteProjectModal(false); setProjectToDelete(null); setProjectMenuOpenId(null); }}>
              Cancel
            </CustomButton>
            <CustomButton variant="danger" onClick={handleDeleteProject}>
              Delete Group
            </CustomButton>
          </div>
        )}
      </CustomModal>

      {/* Leave Group confirmation */}
      <CustomModal
        show={showLeaveProjectModal}
        onHide={() => { setShowLeaveProjectModal(false); setProjectToLeave(null); setProjectMenuOpenId(null); }}
        title="Leave Group"
        subtitle={projectToLeave ? `Are you sure you want to leave "${projectToLeave.name}"? You can rejoin later if someone adds you back.` : ''}
        size="md"
        iconVariant="sage"
        icon={
          <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
          </svg>
        }
      >
        {projectToLeave && (
          <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', marginTop: '24px' }}>
            <CustomButton variant="secondary" onClick={() => { setShowLeaveProjectModal(false); setProjectToLeave(null); setProjectMenuOpenId(null); }}>
              Cancel
            </CustomButton>
            <CustomButton variant="primary" onClick={handleLeaveProject}>
              Leave Group
            </CustomButton>
          </div>
        )}
      </CustomModal>

      {/* Rename Group modal */}
      <CustomModal
        show={showRenameProjectModal}
        onHide={() => { setShowRenameProjectModal(false); setProjectToRename(null); setRenameProjectName(''); setProjectMenuOpenId(null); }}
        title="Rename Group"
        subtitle={projectToRename ? `Change the name of "${projectToRename.name}"` : ''}
        size="md"
        iconVariant="sage"
        icon={
          <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
          </svg>
        }
      >
        {projectToRename && (
          <Form onSubmit={handleRenameProject}>
            <Form.Group className="mb-4">
              <Form.Label>Group name</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter new name"
                value={renameProjectName}
                onChange={(e) => setRenameProjectName(e.target.value)}
                required
                autoFocus
              />
            </Form.Group>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', marginTop: '24px' }}>
              <CustomButton
                variant="secondary"
                type="button"
                onClick={() => { setShowRenameProjectModal(false); setProjectToRename(null); setRenameProjectName(''); setProjectMenuOpenId(null); }}
              >
                Cancel
              </CustomButton>
              <CustomButton variant="primary" type="submit" disabled={renamingProject || !renameProjectName.trim()}>
                {renamingProject ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Renaming...
                  </>
                ) : (
                  'Rename'
                )}
              </CustomButton>
            </div>
          </Form>
        )}
      </CustomModal>

      {/* Delete Chat (direct/single) confirmation */}
      <CustomModal
        show={showDeleteChatModal}
        onHide={() => { setShowDeleteChatModal(false); setSessionToDelete(null); setSessionMenuOpenId(null); }}
        title="Delete Chat"
        subtitle={sessionToDelete ? `Are you sure you want to delete "${sessionToDelete.title}"? This action cannot be undone.` : ''}
        size="md"
        iconVariant="rose"
        icon={
          <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
          </svg>
        }
      >
        {sessionToDelete && (
          <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', marginTop: '24px' }}>
            <CustomButton variant="secondary" onClick={() => { setShowDeleteChatModal(false); setSessionToDelete(null); setSessionMenuOpenId(null); }}>
              Cancel
            </CustomButton>
            <CustomButton
              variant="danger"
              onClick={() => {
                const sid = sessionToDelete.session_id;
                setShowDeleteChatModal(false);
                setSessionToDelete(null);
                setSessionMenuOpenId(null);
                if (sid) deleteChatSession(sid);
              }}
              disabled={deletingSessionId === sessionToDelete.session_id}
            >
              {deletingSessionId === sessionToDelete.session_id ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Deleting...
                </>
              ) : (
                'Delete'
              )}
            </CustomButton>
          </div>
        )}
      </CustomModal>

      {/* Invite Family Member modal */}
      <CustomModal
        show={showAddMemberModal}
        onHide={() => { setShowAddMemberModal(false); setGiftSentSuccess(false); resetInviteForm(); }}
        title={giftSentSuccess ? 'Gift Sent' : 'Invite Someone to Connect'}
        subtitle={giftSentSuccess ? '' : "Enter their details below and we'll send them an invitation to join your Family Group."}
      >
        {giftSentSuccess ? (
          <div style={{ textAlign: 'center', padding: '32px 16px 16px' }}>
            <div style={{ fontSize: '2.5rem', marginBottom: '16px' }}>🎁</div>
            <h3 style={{ fontWeight: 700, color: '#1a1a1a', marginBottom: '12px', fontSize: '1.4rem' }}>Your gift has been sent.</h3>
            <p style={{ color: '#555', fontSize: '1rem', lineHeight: 1.6, marginBottom: 0 }}>
              We've emailed them an invitation. Once they create their account, they'll appear in your chat interface.
            </p>
          </div>
        ) : (
          <div style={{ textAlign: 'left', marginTop: '16px' }}>
            {/* Person cards — one row per invitee: name fields + email side by side */}
            <Form.Group className="mb-3">
              <Form.Label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#595959', fontSize: '14px' }}>Who are you inviting?</Form.Label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {invitees.map((invitee) => (
                  <div key={invitee.id} style={{ display: 'flex', flexDirection: 'column', gap: '8px', padding: '14px', border: '1px solid #e2e8f0', borderRadius: '10px', background: '#fafafa' }}>
                    <div style={{ display: 'flex', gap: '10px' }}>
                      <Form.Control
                        type="text"
                        placeholder="First"
                        value={invitee.firstName}
                        onChange={(e) => setInvitees(prev => prev.map(i => i.id === invitee.id ? { ...i, firstName: e.target.value } : i))}
                        style={{ flex: 1, minWidth: 0, padding: '10px 12px', borderRadius: '6px', border: '1px solid #e2e8f0', fontSize: '16px' }}
                      />
                      <Form.Control
                        type="text"
                        placeholder="Last"
                        value={invitee.lastName}
                        onChange={(e) => setInvitees(prev => prev.map(i => i.id === invitee.id ? { ...i, lastName: e.target.value } : i))}
                        style={{ flex: 1, minWidth: 0, padding: '10px 12px', borderRadius: '6px', border: '1px solid #e2e8f0', fontSize: '16px' }}
                      />
                      {invitees.length > 1 && (
                        <button
                          type="button"
                          onClick={() => setInvitees(prev => prev.filter(i => i.id !== invitee.id))}
                          style={{ flexShrink: 0, width: '36px', height: '36px', alignSelf: 'center', borderRadius: '50%', border: '1px solid #e2e8f0', background: '#fff', color: '#94a3b8', fontSize: '16px', lineHeight: 1, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0 }}
                        >×</button>
                      )}
                    </div>
                    <Form.Control
                      type="email"
                      placeholder="Email"
                      value={invitee.email}
                      onChange={(e) => setInvitees(prev => prev.map(i => i.id === invitee.id ? { ...i, email: e.target.value } : i))}
                      style={{ width: '100%', padding: '10px 12px', borderRadius: '6px', border: '1px solid #e2e8f0', fontSize: '16px' }}
                    />
                  </div>
                ))}
              </div>
              <button
                type="button"
                onClick={() =>
                  setInvitees((prev) => [
                    ...prev,
                    { id: Date.now(), firstName: '', lastName: '', email: '', action: 'send_invite' },
                  ])
                }
                style={{ marginTop: '8px', background: 'none', border: 'none', color: 'var(--soft-teal)', fontSize: '13px', cursor: 'pointer', padding: '4px 0', fontWeight: '500' }}
              >
                + Add another person
              </button>
            </Form.Group>

            <Form.Group className="mb-4">
              <Form.Label style={{ display: 'block', marginBottom: '12px', fontWeight: '500', color: '#595959', fontSize: '14px' }}>Who will cover their subscription?</Form.Label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <div
                  role="button"
                  tabIndex={0}
                  onClick={() => {
                    setInviteWhoPay(true);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setInviteWhoPay(true);
                    }
                  }}
                  style={{
                    border: inviteWhoPay === true ? '2px solid #C5A866' : '1px solid #e2e8f0',
                    borderRadius: '12px',
                    padding: '16px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '16px',
                    cursor: 'pointer',
                    backgroundColor: inviteWhoPay === true ? 'rgba(197, 168, 102, 0.05)' : '#fff'
                  }}
                >
                  <div style={{ width: '20px', height: '20px', borderRadius: '50%', border: '2px solid ' + (inviteWhoPay === true ? '#C5A866' : '#cbd5e1'), display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    {inviteWhoPay === true && <div style={{ width: '10px', height: '10px', borderRadius: '50%', backgroundColor: '#C5A866' }} />}
                  </div>
                  <div style={{ textAlign: 'left' }}>
                    <div style={{ fontWeight: '500', color: '#333', fontSize: '15px' }}>I'll pay for them</div>
                  </div>
                </div>

                <div
                  role="button"
                  tabIndex={0}
                  onClick={() => { setInviteWhoPay(false); setInviteGiftPlan(null); setInvitees(prev => prev.map(i => ({ ...i, action: 'send_invite' }))); }}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setInviteWhoPay(false); setInviteGiftPlan(null); setInvitees(prev => prev.map(i => ({ ...i, action: 'send_invite' }))); } }}
                  style={{
                    border: inviteWhoPay === false ? '2px solid #C5A866' : '1px solid #e2e8f0',
                    borderRadius: '12px',
                    padding: '16px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '16px',
                    cursor: 'pointer',
                    backgroundColor: inviteWhoPay === false ? 'rgba(197, 168, 102, 0.05)' : '#fff'
                  }}
                >
                  <div style={{ width: '20px', height: '20px', borderRadius: '50%', border: '2px solid ' + (inviteWhoPay === false ? '#C5A866' : '#cbd5e1'), display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    {inviteWhoPay === false && <div style={{ width: '10px', height: '10px', borderRadius: '50%', backgroundColor: '#C5A866' }} />}
                  </div>
                  <div style={{ textAlign: 'left' }}>
                    <div style={{ fontWeight: '500', color: '#333', fontSize: '15px' }}>Let them pay</div>
                  </div>
                </div>
              </div>
            </Form.Group>

            {/* Plan selection — expands when "I'll pay for them" is selected */}
            <div style={{ maxHeight: inviteWhoPay === true ? '300px' : '0', overflow: 'hidden', transition: 'max-height 0.3s ease' }}>
              <Form.Group className="mb-4">
                <Form.Label style={{ display: 'block', marginBottom: '12px', fontWeight: '500', color: '#595959', fontSize: '16px' }}>
                  Choose a plan to gift
                </Form.Label>
                {addMemberPlanLoading ? (
                  <div style={{ fontSize: '14px', color: 'var(--slate)' }}>Loading plans…</div>
                ) : !addMemberAvatarPlanDetails ? (
                  <div style={{ fontSize: '14px', color: 'var(--slate)' }}>Plans unavailable — please try again</div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    {getAddMemberInvitePlanOptions()
                      .filter(o => o.value !== 'send_invite')
                      .map(opt => {
                        const isSelected = inviteGiftPlan === opt.interval;
                        const label = opt.interval === 'monthly' ? 'Gift 1 Month' : 'Gift 1 Year';
                        const priceStr = opt.price_usd != null ? `$${opt.price_usd.toFixed(2)}` : null;
                        return (
                          <div
                            key={opt.value}
                            role="button"
                            tabIndex={0}
                            onClick={() => { setInviteGiftPlan(opt.interval); setInvitees(prev => prev.map(i => ({ ...i, action: opt.value }))); }}
                            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setInviteGiftPlan(opt.interval); setInvitees(prev => prev.map(i => ({ ...i, action: opt.value }))); } }}
                            style={{
                              border: isSelected ? '2px solid #C5A866' : '1px solid #e2e8f0',
                              borderRadius: '12px',
                              padding: '16px',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '16px',
                              cursor: 'pointer',
                              backgroundColor: isSelected ? 'rgba(197, 168, 102, 0.05)' : '#fff'
                            }}
                          >
                            <div style={{ width: '20px', height: '20px', borderRadius: '50%', border: '2px solid ' + (isSelected ? '#C5A866' : '#cbd5e1'), display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                              {isSelected && <div style={{ width: '10px', height: '10px', borderRadius: '50%', backgroundColor: '#C5A866' }} />}
                            </div>
                            <div style={{ textAlign: 'left' }}>
                              <div style={{ fontWeight: '500', color: '#333', fontSize: '16px' }}>{label}</div>
                              {priceStr && <div style={{ fontSize: '14px', color: 'var(--slate)', marginTop: '2px' }}>{priceStr}</div>}
                            </div>
                          </div>
                        );
                      })}
                  </div>
                )}
              </Form.Group>
            </div>


            <style>
              {`
              .invite-family-submit-btn {
                background-color: #8dae9f !important;
                border-color: #8dae9f !important;
                color: white !important;
                border-radius: 8px !important;
                padding: 12px !important;
                font-size: 15px !important;
                font-weight: 500 !important;
                transition: all 0.2s ease;
              }
              .invite-family-submit-btn:hover {
                background-color: #799d8e !important;
                border-color: #799d8e !important;
              }
              .invite-family-submit-btn:not(:disabled) {
                cursor: pointer !important;
              }
            `}
            </style>

            <CustomButton
              variant="primary"
              className="w-100 invite-family-submit-btn"
              onClick={handleSendInvitation}
              disabled={
                sendingInvite ||
                addMemberPlanLoading ||
                inviteWhoPay === null ||
                !addMemberAvatarPlanDetails ||
                (inviteWhoPay === true && inviteGiftPlan === null) ||
                (inviteWhoPay === true &&
                  inviteCheckoutPreview.giftCount > 0 &&
                  !inviteCheckoutPreview.paypalGiftOk)
              }
            >
              {sendingInvite
                ? 'Sending...'
                : inviteWhoPay === true &&
                  inviteCheckoutPreview.giftCount > 0 &&
                  inviteCheckoutPreview.paypalGiftOk
                  ? 'Continue to PayPal'
                  : invitees.filter((i) => (i.email || '').trim()).length > 1
                    ? `Send ${invitees.filter((i) => (i.email || '').trim()).length} invitations`
                    : 'Send invitation'}
            </CustomButton>
          </div>
        )}
      </CustomModal>

      {/* Improve Avatar Modal */}
      <CustomModal
        show={showImproveModal}
        onHide={() => {
          setShowImproveModal(false);
          setImproveContext(null);
          setImproveConversation([]);
          setImproveAnswer('');
          setImproveDone(false);
          setImproveCoveredDomains([]);
        }}
        title={`Help ${selectedAvatar?.first_name || 'them'} Remember`}
        size="lg"
        className="improve-modal"
        closeButtonLabel="Save & Close"
      >
        <>
          {/* Self-Knowledge section — shown once API responds */}
          {improveContext && (
            <div className="improve-confidence-section">
              <div className="improve-confidence-header">
                <span className="improve-confidence-label">{improveContext.avatar_name}'s Self-Knowledge</span>
                <span className="improve-confidence-score">{improveContext.confidence_score ?? 0}%</span>
              </div>
              <div className="improve-confidence-bar">
                <div
                  className="improve-confidence-fill"
                  style={{ width: `${improveContext.confidence_score ?? 0}%` }}
                />
              </div>
              <div className="improve-stats">
                <div className="improve-stat">
                  <div className="improve-stat-value">{improveContext.stats?.documents_shared ?? 0}</div>
                  <div className="improve-stat-label">Documents shared</div>
                </div>
                <div className="improve-stat">
                  <div className="improve-stat-value">{improveContext.stats?.conversations_had ?? 0}</div>
                  <div className="improve-stat-label">Conversations had</div>
                </div>
                <div className="improve-stat">
                  <div className="improve-stat-value">{improveContext.stats?.memories_added ?? 0}</div>
                  <div className="improve-stat-label">Memories added</div>
                </div>
              </div>
            </div>
          )}

          {/* Conversation thread */}
          <div className="improve-conversation">
            {improveContextLoading && (
              <div className="improve-msg improve-msg-avatar improve-typing">
                <span /><span /><span />
              </div>
            )}
            {improveConversation.map((msg, idx) => (
              <div
                key={idx}
                className={`improve-msg ${msg.role === 'avatar' ? 'improve-msg-avatar' : msg.role === 'system' ? 'improve-msg-system' : 'improve-msg-user'}`}
              >
                {msg.text}
              </div>
            ))}
            {improveAnswerSending && (
              improveTypingTimedOut ? (
                <div className="improve-msg improve-msg-avatar improve-timeout-msg">
                  Taking a moment to remember…
                </div>
              ) : (
                <div className="improve-msg improve-msg-avatar improve-typing">
                  <span /><span /><span />
                </div>
              )
            )}
            {improveDone && (
              <div className="improve-msg improve-msg-avatar improve-done-msg">
                You've given me so much to hold onto today. Come back anytime — there's always more to remember.
              </div>
            )}
            <div ref={improveConversationEndRef} />
          </div>

          {/* Answer input */}
          {!improveDone && (
            <Form onSubmit={handleImproveAnswer} className="improve-answer-form">
              <Form.Control
                as="textarea"
                rows={3}
                className="improve-answer-input"
                placeholder="Share a memory…"
                value={improveAnswer}
                onChange={(e) => setImproveAnswer(e.target.value)}
                disabled={improveAnswerSending || improveContextLoading}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    if (improveAnswer.trim() && !improveAnswerSending) handleImproveAnswer(e);
                  }
                }}
              />
              <button
                type="submit"
                className="improve-answer-send"
                disabled={improveAnswerSending || !improveAnswer.trim()}
              >
                {improveAnswerSending ? (
                  <Spinner animation="border" size="sm" style={{ width: 18, height: 18 }} />
                ) : (
                  <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" style={{ width: 20, height: 20 }}>
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                  </svg>
                )}
              </button>
            </Form>
          )}

          {/* Add documents — available to all family group members */}
          {(
            <div className="improve-add-docs">
              <input
                ref={improveFileInputRef}
                type="file"
                id="improve-doc-upload"
                multiple
                accept=".pdf,.doc,.docx,.txt,.jpg,.jpeg,.png"
                style={{ display: 'none' }}
                onChange={handleImproveDocUpload}
                disabled={improveDocsUploading}
              />
              <button
                type="button"
                className="improve-add-docs-btn"
                onClick={() => { if (!improveDocsUploading) improveFileInputRef.current?.click(); }}
                disabled={improveDocsUploading}
              >
                {improveDocsUploading ? (
                  <><Spinner animation="border" size="sm" style={{ width: 14, height: 14, marginRight: 6 }} />Uploading…</>
                ) : (
                  <>
                    <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" style={{ width: 16, height: 16, marginRight: 6 }}>
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                    </svg>
                    Add more documents or memories
                  </>
                )}
              </button>
              {improveDocsUploading && (
                <p className="improve-docs-hint">Processing — this may take a moment.</p>
              )}
            </div>
          )}
        </>
      </CustomModal>

      {/* Plan & Billing Modal */}
      {showBillingModal && (() => {
        const { planName, planInterval, planPrice, currentAutoRenew, hasSubscription } = getChatPlanDetails();
        const userPlanId = user?.subscription?.plan_id;
        const userInterval = (user?.subscription?.plan_interval || getChatPlanDetails().planInterval || '').toLowerCase();
        const renewalDate = user?.subscription?.end_date
          ? new Date(user.subscription.end_date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
          : null;
        return (
          <CustomModal
            show={showBillingModal}
            onHide={() => !isChatInputDisabled && setShowBillingModal(false)}
            title="Plan & Billing"
            size="lg"
            hideCloseButton={isChatInputDisabled}
            backdrop={isChatInputDisabled ? 'static' : true}
          >
            <div style={{ padding: '4px 0 20px 0', maxHeight: '70vh', overflowY: 'auto', overflowX: 'hidden' }}>

              {/* Current plan details */}
              <div style={{ marginBottom: '20px', padding: '0 15px' }}>
                <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--slate)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '10px' }}>Current Plan</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {(planName || !hasSubscription) && (
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ color: 'var(--slate)' }}>Plan</span>
                      <span style={{ fontWeight: 500 }}>{planName || 'Free Trial'}</span>
                    </div>
                  )}
                  {planInterval && (
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ color: 'var(--slate)' }}>Billing</span>
                      <span style={{ fontWeight: 500, textTransform: 'capitalize' }}>{planInterval}</span>
                    </div>
                  )}
                  {planPrice && (
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ color: 'var(--slate)' }}>Price</span>
                      <span style={{ fontWeight: 500 }}>{planPrice}</span>
                    </div>
                  )}
                  {renewalDate && (
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ color: 'var(--slate)' }}>{currentAutoRenew ? 'Renews' : 'Expires'}</span>
                      <span style={{ fontWeight: 500 }}>{renewalDate}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Auto-renew toggle */}
              {hasSubscription && (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 15px', borderTop: '1px solid var(--border-subtle)', marginBottom: '4px' }}>
                  <span style={{ color: 'var(--charcoal)', fontWeight: 500 }}>Auto-renew</span>
                  <Form.Check
                    type="switch"
                    id="chat-billing-modal-auto-renew"
                    checked={currentAutoRenew}
                    onChange={handleChatToggleAutoRenew}
                    className="profile-menu-autorenew-switch"
                  />
                </div>
              )}

              <hr style={{ borderColor: 'var(--border-subtle)', margin: '20px 0' }} />

              {/* Profile Subscriptions */}
              <div style={{ marginBottom: '30px', padding: '0 15px' }}>
                <h4 style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '15px', color: 'var(--soft-black)', textAlign: 'center' }}>Profile Subscriptions</h4>
                <div className="addons-list" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '15px' }}>
                  {avatarPlans && avatarPlans.length > 0 ? (
                    avatarPlans.map(type => {
                      const plansList = type.plans || [];
                      if (plansList.length === 0) return null;
                      return plansList.map(plan => {
                        const isYearly = Number(plan.yearly_price_usd) > 0;
                        const isMonthly = Number(plan.monthly_price_usd) > 0;
                        if (!isYearly && !isMonthly) return null;
                        const billingPeriod = isYearly ? 'yearly' : 'monthly';
                        if (billingPeriod === 'monthly' && userInterval === 'yearly') return null;
                        const priceText = isYearly ? `$${plan.yearly_price_usd}/year` : `$${plan.monthly_price_usd}/month`;
                        const isCurrentPlan = userPlanId
                          ? plan.id === userPlanId
                          : (!!userInterval && billingPeriod === userInterval);
                        return (
                          <div key={`avatar-plan-${plan.id}-${billingPeriod}`} style={{ display: 'flex' }}>
                            <div style={{
                              background: isCurrentPlan ? 'rgba(107,158,160,0.04)' : 'white',
                              border: isCurrentPlan ? '2px solid var(--soft-teal)' : '1px solid var(--border-subtle)',
                              borderRadius: '16px',
                              padding: '24px',
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              textAlign: 'center',
                              boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                              flex: 1,
                            }}>
                              {isCurrentPlan && (
                                <div style={{ fontSize: '11px', fontWeight: 700, color: 'var(--soft-teal)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '8px' }}>
                                  Your current plan
                                </div>
                              )}
                              <div style={{ marginBottom: '16px' }}>
                                <h5 style={{ margin: '0 0 5px 0', fontSize: '16px', fontWeight: '600', color: 'var(--soft-black)' }}>
                                  {isYearly ? 'Annual' : 'Monthly'}
                                </h5>
                                <div style={{ marginTop: '5px', fontSize: '14px', fontWeight: 'bold', color: 'var(--soft-teal)' }}>
                                  {priceText}
                                </div>
                                {isYearly && (
                                  <div style={{ marginTop: '4px', fontSize: '12px', color: 'var(--soft-teal)' }}>
                                    Save 37% vs monthly
                                  </div>
                                )}
                              </div>
                              <button
                                disabled={isCurrentPlan}
                                onClick={() => {
                                  if (!isCurrentPlan) {
                                    setShowBillingModal(false);
                                    handleRenewalPayment(plan, billingPeriod);
                                  }
                                }}
                                style={{
                                  background: isCurrentPlan ? '#e2e8f0' : 'var(--soft-teal)',
                                  color: isCurrentPlan ? 'var(--slate)' : 'white',
                                  border: 'none',
                                  padding: '10px 32px',
                                  borderRadius: '24px',
                                  fontSize: '15px',
                                  fontWeight: '600',
                                  cursor: isCurrentPlan ? 'default' : 'pointer',
                                  whiteSpace: 'nowrap',
                                  marginTop: '8px',
                                }}
                              >
                                {isCurrentPlan ? 'Current Plan' : 'Select'}
                              </button>
                            </div>
                          </div>
                        );
                      });
                    })
                  ) : (
                    <div style={{ textAlign: 'center', padding: '10px', color: 'var(--slate)' }}>
                      Loading avatar subscriptions...
                    </div>
                  )}
                </div>
              </div>

              <hr style={{ borderColor: 'var(--border-subtle)', margin: '20px 0' }} />

              {/* Add-on Plans */}
              <div style={{ padding: '0 15px' }}>
                <h4 style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '15px', color: 'var(--soft-black)', textAlign: 'center' }}>Add-on Plans</h4>
                {loadingAddons ? (
                  <div style={{ textAlign: 'center', padding: '20px' }}>
                    <Spinner animation="border" size="sm" />
                    <p style={{ marginTop: '10px', color: 'var(--slate)' }}>Loading available addons...</p>
                  </div>
                ) : addons.length > 0 ? (
                  <div className="addons-list" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '15px' }}>
                    {addons.map((addon) => (
                      <div key={addon.id} style={{
                        background: 'white',
                        border: '1px solid var(--border-subtle)',
                        borderRadius: '16px',
                        padding: '24px',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        textAlign: 'center',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                        transition: 'transform 0.2s ease'
                      }}>
                        <div style={{ marginBottom: '16px' }}>
                          <h5 style={{ margin: '0 0 5px 0', fontSize: '16px', fontWeight: '600', color: 'var(--soft-black)' }}>
                            {addon.name}
                          </h5>
                          <p style={{ margin: 0, fontSize: '13px', color: 'var(--slate)' }}>
                            {addon.description === 'Continuing conversation beyond plan limit.'
                              ? 'Continue your conversations when your monthly allocation runs out.'
                              : addon.description || 'Enhance your experience'}
                          </p>
                          <div style={{ marginTop: '5px', fontSize: '14px', fontWeight: 'bold', color: 'var(--soft-teal)' }}>
                            ${addon.price_usd}
                            {addon.tokens && (
                              <span style={{ fontSize: '12px', fontWeight: 'normal', color: 'var(--slate)', marginLeft: '8px' }}>
                                • 100 additional prompts.
                              </span>
                            )}
                          </div>
                        </div>
                        <button
                          onClick={() => handleAddonPayment(addon)}
                          style={{
                            background: 'var(--soft-teal)',
                            color: 'white',
                            border: 'none',
                            padding: '10px 32px',
                            borderRadius: '24px',
                            fontSize: '15px',
                            fontWeight: '600',
                            cursor: 'pointer',
                            whiteSpace: 'nowrap',
                            marginTop: '8px'
                          }}
                        >
                          Add
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={{ textAlign: 'center', padding: '20px', color: 'var(--slate)' }}>
                    No addons available at the moment.
                  </div>
                )}
              </div>

              <hr style={{ borderColor: 'var(--border-subtle)', margin: '20px 0' }} />

              {/* Update Payment Method & Cancel Subscription */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', padding: '0 15px' }}>
                <CustomButton
                  variant="secondary"
                  style={{ width: '100%' }}
                  onClick={() => window.open('https://www.paypal.com/myaccount/autopay', '_blank', 'noopener,noreferrer')}
                >
                  Update Payment Method
                </CustomButton>
                <div style={{ border: '1px solid var(--border-subtle)', borderRadius: '10px', padding: '14px' }}>
                  <p style={{ fontSize: '12px', color: 'var(--slate)', textAlign: 'center', margin: 0 }}>
                    You will be redirected to PayPal to update your payment details.
                  </p>
                </div>
                {hasSubscription && (
                  <button
                    onClick={() => { setShowBillingModal(false); setShowCancelConfirmModal(true); }}
                    disabled={cancellingSubscription}
                    style={{ background: 'none', border: 'none', padding: 0, color: '#000', cursor: 'pointer', fontSize: '14px', textDecoration: 'underline', display: 'block', margin: '0 auto' }}
                  >
                    Cancel Subscription
                  </button>
                )}
              </div>
            </div>
          </CustomModal>
        );
      })()}

      {/* Cancel Subscription Confirmation Modal */}
      <CustomModal
        show={showCancelConfirmModal}
        onHide={() => setShowCancelConfirmModal(false)}
        title="Cancel Subscription"
        subtitle="Are you sure you want to cancel your subscription?"
        iconVariant="danger"
        size="md"
        icon={
          <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
        }
      >
        <div style={{ textAlign: 'center', padding: '10px 0' }}>
          <p style={{ color: 'var(--slate)', marginBottom: '25px' }}>
            This will stop future billing but you will keep access until the end of the current period.
          </p>
          <div style={{ display: 'flex', gap: '15px', justifyContent: 'center' }}>
            <CustomButton variant="secondary" onClick={() => setShowCancelConfirmModal(false)} style={{ minWidth: '120px' }}>Keep Plan</CustomButton>
            <CustomButton variant="danger" onClick={handleChatCancelSubscription} loading={cancellingSubscription} style={{ minWidth: '120px' }}>Proceed</CustomButton>
          </div>
        </div>
      </CustomModal>


      {/* Payment Success Modal */}
      <CustomModal
        show={showPaymentSuccessModal}
        onHide={() => setShowPaymentSuccessModal(false)}
        title="Subscription Activated"
        subtitle="Your payment has been successfully processed"
        iconVariant="sage"
        size="lg"
        icon={
          <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        }
      >
        <div style={{ padding: '10px 0' }}>
          <div style={{ background: 'var(--seashell)', padding: '24px', borderRadius: '16px', border: '1px solid var(--border-subtle)', marginBottom: '24px' }}>
            <p style={{ color: 'var(--slate)', marginBottom: '20px' }}>
              Your payment has been verified by backend capture.
            </p>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
              <div>
                <div style={{ fontSize: '12px', color: 'var(--slate)', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Order ID</div>
                <div style={{ fontSize: '15px', color: 'var(--soft-black)', marginTop: '4px' }}>{paymentSuccessData?.order_id || 'N/A'}</div>
              </div>
              <div>
                <div style={{ fontSize: '12px', color: 'var(--slate)', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Plan Interval</div>
                <div style={{ fontSize: '15px', color: 'var(--soft-black)', marginTop: '4px', textTransform: 'capitalize' }}>{paymentSuccessData?.plan_interval || 'N/A'}</div>
              </div>
              <div>
                <div style={{ fontSize: '12px', color: 'var(--slate)', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Auto Renew</div>
                <div style={{ fontSize: '15px', color: 'var(--soft-black)', marginTop: '4px', textTransform: 'capitalize' }}>{paymentSuccessData?.auto_renew ? 'enable' : 'disable'}</div>
              </div>
              {paymentSuccessData?.user_name && (
                <div>
                  <div style={{ fontSize: '12px', color: 'var(--slate)', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.05em' }}>User Name</div>
                  <div style={{ fontSize: '15px', color: 'var(--soft-black)', marginTop: '4px' }}>{paymentSuccessData.user_name}</div>
                </div>
              )}
              {paymentSuccessData?.payment_amount_usd && (
                <div>
                  <div style={{ fontSize: '12px', color: 'var(--slate)', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Payment Amount</div>
                  <div style={{ fontSize: '15px', color: 'var(--soft-black)', marginTop: '4px' }}>${paymentSuccessData.payment_amount_usd} USD</div>
                </div>
              )}
            </div>
          </div>

          <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
            <CustomButton
              variant="secondary"
              onClick={() => setShowPaymentSuccessModal(false)}
            >
              Back to Chat
            </CustomButton>
          </div>
        </div>
      </CustomModal>

      {/* New Chat Type Modal */}
      <CustomModal
        show={showNewChatModal}
        onHide={() => { setShowNewChatModal(false); setNewChatMemberStep(false); setNewChatType(null); setNewChatPickedMembers([]); }}
        title={newChatMemberStep ? 'Choose members' : 'Start a new chat'}
        size="md"
      >
        {!newChatMemberStep ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {(() => {
              const avatarFullName = [selectedAvatar?.first_name, selectedAvatar?.last_name].filter(Boolean).join(' ') || 'the avatar';
              return [
              {
                key: 'private',
                title: `Just you and ${avatarFullName}`,
                subtitle: 'Private — only visible to you',
              },
              {
                key: 'open',
                title: 'Anyone in our Family Group',
                subtitle: 'Open — all members can see and join',
              },
              {
                key: 'selected',
                title: `Selected members with ${avatarFullName}`,
                subtitle: 'Choose specific people at creation, locked after',
              },
              {
                key: 'family',
                title: 'Just us — family members only',
                subtitle: `${avatarFullName} not included`,
              },
            ];})().map((opt) => (
              <button
                key={opt.key}
                type="button"
                onClick={() => {
                  setNewChatType(opt.key);
                  if (opt.key === 'selected' || opt.key === 'family') {
                    setNewChatMemberStep(true);
                  } else if (opt.key === 'private') {
                    // Private chat — start new individual session
                    setShowNewChatModal(false);
                    creatingSessionRef.current = false;
                    setSelectedProject(null);
                    setChatSessionId(null);
                    if (wsConnection) { wsConnection.close(); setWsConnection(null); setIsWsConnected(false); }
                    setMessages([]);
                    setIsTyping(false);
                    setInputValue('');
                    setChatSessionKey(prev => prev + 1);
                    lastWebSocketMessageRef.current = { content: '', timestamp: 0 };
                    localStorage.removeItem('chat_session_id');
                    localStorage.removeItem('chat_session_project_id');
                    localStorage.removeItem('chat_messages');
                    fetchChatSessions();
                  } else if (opt.key === 'open') {
                    // Open group — create project with default name, all members auto-added by backend
                    setShowNewChatModal(false);
                    setProjectName('Family Group Chat');
                    setProjectDescription('avatar_chat');
                    setShowCreateProjectModal(true);
                  }
                }}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  padding: '14px 16px',
                  borderRadius: '12px',
                  border: '1px solid var(--border-subtle, #e5e7eb)',
                  background: 'rgba(255,255,255,0.7)',
                  cursor: 'pointer',
                  textAlign: 'left',
                  transition: 'background 0.15s',
                }}
                onMouseEnter={e => e.currentTarget.style.background = 'rgba(107,158,160,0.1)'}
                onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.7)'}
              >
                <div>
                  <div style={{ fontWeight: 600, fontSize: '14px', color: 'var(--soft-black, #1a1a1a)' }}>{opt.title}</div>
                  <div style={{ fontSize: '12px', color: 'var(--slate, #64748b)', marginTop: '2px' }}>{opt.subtitle}</div>
                </div>
              </button>
            ))}
          </div>
        ) : (
          // Member picker step (for 'selected' and 'family')
          <div>
            <p style={{ fontSize: '13px', color: 'var(--slate)', marginBottom: '12px' }}>
              Select who to include in this chat:
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '20px' }}>
              {(groupMembers || []).filter(m => m.user_id !== (user?.id || user?.user_id)).map(m => {
                const picked = newChatPickedMembers.some(p => p.user_id === m.user_id);
                return (
                  <label
                    key={m.user_id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                      padding: '10px 12px',
                      borderRadius: '10px',
                      border: picked ? '1px solid var(--soft-teal)' : '1px solid var(--border-subtle, #e5e7eb)',
                      background: picked ? 'rgba(107,158,160,0.1)' : 'rgba(255,255,255,0.7)',
                      cursor: 'pointer',
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={picked}
                      onChange={() => {
                        setNewChatPickedMembers(prev =>
                          picked ? prev.filter(p => p.user_id !== m.user_id) : [...prev, m]
                        );
                      }}
                      style={{ accentColor: 'var(--soft-teal)' }}
                    />
                    <div style={{ fontSize: '13px', fontWeight: 600 }}>
                      {m.name}{m.email ? <span style={{ fontWeight: 400, color: 'var(--slate)', marginLeft: '6px' }}>{m.email}</span> : null}
                    </div>
                  </label>
                );
              })}
              {(groupMembers || []).filter(m => m.user_id !== (user?.id || user?.user_id)).length === 0 && (
                <div style={{ fontSize: '13px', color: '#666', padding: '8px 0' }}>No other members found.</div>
              )}
            </div>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <CustomButton variant="secondary" onClick={() => setNewChatMemberStep(false)}>Back</CustomButton>
              <CustomButton
                variant="primary"
                disabled={newChatPickedMembers.length === 0}
                onClick={() => {
                  setShowNewChatModal(false);
                  setNewChatMemberStep(false);
                  // Pre-populate project name and open create project modal
                  setProjectName(newChatType === 'family' ? 'Family Chat' : 'Group Chat');
                  setProjectDescription(newChatType === 'family' ? 'just_us' : 'selected');
                  setShowCreateProjectModal(true);
                  // TODO: after project creation, auto-add newChatPickedMembers
                }}
              >
                Start Chat
              </CustomButton>
            </div>
          </div>
        )}
      </CustomModal>

      {/* Create Group Modal */}
      <CustomModal
        show={showCreateProjectModal}
        onHide={() => {
          setShowCreateProjectModal(false);
          setProjectName('');
          setProjectDescription('');
        }}
        title="Start a Group Conversation"
        size="md"
      >
        <Form onSubmit={handleCreateProject}>
          <Form.Group className="mb-3">
            <Form.Control
              type="text"
              placeholder="Group name"
              value={projectName}
              onChange={(e) => setProjectName(e.target.value)}
              required
            />
          </Form.Group>
          <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '24px' }}>
            <CustomButton
              variant="secondary"
              onClick={() => {
                setShowCreateProjectModal(false);
                setProjectName('');
                setProjectDescription('');
              }}
            >
              Cancel
            </CustomButton>
            <CustomButton
              type="submit"
              variant="primary"
              disabled={creatingProject || !projectName.trim()}
            >
              {creatingProject ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Creating...
                </>
              ) : (
                'Start Conversation'
              )}
            </CustomButton>
          </div>
        </Form>
      </CustomModal>

      {/* Add Members (multi-select) Modal */}
      <CustomModal
        show={showAddMembersModal}
        onHide={() => {
          setShowAddMembersModal(false);
          resetAddMembersModal();
        }}
        title="Add members"
        subtitle="Search and click a member to add them to the group"
        size="md"
      >
        {selectedProject ? (
          <div>
            {/* In this chat */}
            {(() => {
              const _selfUid = user?.id || user?.user_id;
              const _selfEmail = (user?.email || '').toLowerCase();
              const seenUids = new Set();
              const currentMembers = [];
              const addToCurrentMembers = (m) => {
                const uid = m.user_id ?? m.id;
                const email = (m.email || '').toLowerCase();
                const key = uid != null ? String(uid) : email;
                if (!key || seenUids.has(key)) return;
                seenUids.add(key);
                const isSelf = (uid != null && String(uid) === String(_selfUid)) || (email && email === _selfEmail);
                currentMembers.push({ ...m, displayName: isSelf ? 'You' : (m.name || m.email || 'Member') });
              };
              (selectedProject.members || []).forEach(addToCurrentMembers);
              if (currentMembers.length === 0) return null;
              return (
                <div style={{ marginBottom: '16px' }}>
                  <div style={{ fontSize: '11px', fontWeight: 700, color: 'var(--slate)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '8px' }}>
                    In this chat
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    {currentMembers.map((m) => {
                      const uid = m.user_id ?? m.id;
                      const initials = m.initials || (m.displayName || 'M').substring(0, 2).toUpperCase();
                      return (
                        <div key={uid || m.email} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '6px 8px', borderRadius: '8px', background: 'rgba(0,0,0,0.03)' }}>
                          <span style={{ width: '26px', height: '26px', borderRadius: '50%', background: 'linear-gradient(135deg, var(--soft-teal), var(--light-teal))', color: 'white', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px', fontWeight: 700, flexShrink: 0 }}>
                            {initials}
                          </span>
                          <span style={{ fontSize: '13px', fontWeight: m.displayName === 'You' ? 700 : 500, color: 'var(--soft-black)' }}>{m.displayName}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })()}

            <Form.Group className="mb-3">
              <Form.Label style={{ fontSize: '12px', fontWeight: 600, color: 'var(--slate)' }}>
                Add someone
              </Form.Label>
              <Form.Control
                type="text"
                placeholder="Type 1–2 letters (name or email)…"
                value={addMembersQuery}
                onChange={(e) => setAddMembersQuery(e.target.value)}
              />
            </Form.Group>

            {selectedAddMembers.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '12px' }}>
                {selectedAddMembers.map((m) => {
                  const key = m.id || m.email || m.name;
                  const label = m.name || m.email || 'Member';
                  return (
                    <button
                      key={key}
                      type="button"
                      onClick={() => toggleSelectedAddMember(m)}
                      title="Remove"
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '6px 10px',
                        borderRadius: '999px',
                        border: '1px solid rgba(0,0,0,0.08)',
                        background: 'rgba(107, 158, 160, 0.10)',
                        color: 'var(--soft-black)',
                        cursor: 'pointer',
                        fontSize: '12px',
                        maxWidth: '100%'
                      }}
                    >
                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</span>
                      <span style={{ color: 'var(--slate)' }}>×</span>
                    </button>
                  );
                })}
              </div>
            )}

            {(() => {
              // Exclude anyone already in the chat (project members + avatarMembers)
              const _selfUid = user?.id || user?.user_id;
              const existingUids = new Set();
              const existingEmails = new Set();
              if (_selfUid) existingUids.add(String(_selfUid));
              (selectedProject.members || []).forEach(m => {
                const uid = m.user_id ?? m.id; if (uid != null) existingUids.add(String(uid));
                const email = (m.email || '').toLowerCase(); if (email) existingEmails.add(email);
              });
              // avatarMembers removed — only exclude actual project members, not all family group members
              const all = getAddableMembersForProject().filter(m => {
                const uid = m.user_id ?? m.id;
                const email = (m.email || '').toLowerCase();
                if (uid != null && existingUids.has(String(uid))) return false;
                if (email && existingEmails.has(email)) return false;
                return true;
              });
              const q = (addMembersQuery || '').trim().toLowerCase();
              const filtered = !q
                ? all
                : all.filter((m) => {
                  const name = (m.name || '').toLowerCase();
                  const email = (m.email || '').toLowerCase();
                  return name.includes(q) || email.includes(q);
                });

              return (
                <div style={{ border: '1px solid rgba(0,0,0,0.08)', borderRadius: '12px', overflow: 'hidden' }}>
                  <div style={{ maxHeight: '280px', overflowY: 'auto', background: 'rgba(255,255,255,0.8)' }}>
                    {filtered.length === 0 ? (
                      <div style={{ padding: '12px', fontSize: '12px', color: '#666' }}>
                        {q ? 'No matches. Try a different name/email.' : 'No available members to add.'}
                      </div>
                    ) : (
                      filtered.map((m) => {
                        const initials = m.initials || (m.name || m.email || 'M').substring(0, 2).toUpperCase();
                        return (
                          <button
                            key={m.id || m.email || m.name}
                            type="button"
                            onClick={() => addOneMemberToProject(m)}
                            disabled={addingMembers}
                            style={{
                              width: '100%',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              gap: '10px',
                              padding: '10px 12px',
                              border: 'none',
                              background: 'transparent',
                              cursor: addingMembers ? 'wait' : 'pointer',
                              textAlign: 'left'
                            }}
                            title="Click to add this member"
                          >
                            <span style={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0 }}>
                              <span
                                style={{
                                  width: '28px',
                                  height: '28px',
                                  borderRadius: '50%',
                                  background: 'linear-gradient(135deg, var(--soft-teal), var(--light-teal))',
                                  color: 'white',
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  fontSize: '11px',
                                  fontWeight: 700,
                                  flexShrink: 0
                                }}
                              >
                                {initials}
                              </span>
                              <span style={{ minWidth: 0, display: 'flex', flexDirection: 'row', alignItems: 'baseline', gap: '6px', overflow: 'hidden' }}>
                                <span
                                  style={{
                                    fontSize: '13px',
                                    fontWeight: 600,
                                    color: 'var(--soft-black)',
                                    whiteSpace: 'nowrap',
                                    flexShrink: 0
                                  }}
                                >
                                  {m.name || m.email || 'Member'}
                                </span>
                                {m.name && m.email && (
                                  <span style={{ fontSize: '12px', color: 'var(--slate)', flexShrink: 0 }}>—</span>
                                )}
                                {m.name && m.email && (
                                  <span
                                    style={{
                                      fontSize: '12px',
                                      color: 'var(--slate)',
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap'
                                    }}
                                  >
                                    {m.email}
                                  </span>
                                )}
                              </span>
                            </span>
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', flexShrink: 0, fontSize: '12px', fontWeight: 600, color: 'var(--soft-teal)' }}>
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M12 5v14m7-7H5" />
                              </svg>
                              Add
                            </span>
                          </button>
                        );
                      })
                    )}
                  </div>
                </div>
              );
            })()}

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '18px' }}>
              <CustomButton
                variant="secondary"
                onClick={() => {
                  setShowAddMembersModal(false);
                  resetAddMembersModal();
                }}
                disabled={addingMembers}
              >
                Cancel
              </CustomButton>
              <CustomButton
                variant="primary"
                onClick={addSelectedMembersToProject}
                disabled={addingMembers || selectedAddMembers.length === 0}
              >
                {addingMembers ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Adding...
                  </>
                ) : (
                  `Add selected (${selectedAddMembers.length})`
                )}
              </CustomButton>
            </div>
          </div>
        ) : (
          <div style={{ padding: '12px', fontSize: '12px', color: '#666' }}>Select a group first.</div>
        )}
      </CustomModal>

      {/* Invite Members Modal */}
      <CustomModal
        show={showInviteModal}
        onHide={() => {
          setShowInviteModal(false);
          setInviteEmails('');
          setSelectedInviteUserId(null);
        }}
        title="Add Member"
        subtitle="Add a team member to collaborate on this project"
        size="md"
      >
        <Form>
          <Form.Group className="mb-3">
            <div style={{ position: 'relative' }}>
              <Form.Control
                as="textarea"
                rows={3}
                name="project_invite_email"
                // Chrome sometimes ignores "off"; this value suppresses most autofill prompts.
                autoComplete="new-password"
                autoCorrect="off"
                autoCapitalize="none"
                placeholder="Enter email addresses (one per line, or separated by commas)"
                value={inviteEmails}
                onChange={(e) => {
                  const newValue = e.target.value;
                  const previousValue = inviteEmails;
                  setInviteEmails(newValue);

                  // Only clear selectedInviteUserId if user is typing something substantially different
                  // This preserves the selected user ID when suggestions are clicked
                  if (selectedInviteUserId && newValue.trim() &&
                    Math.abs(newValue.length - previousValue.length) > 2 &&
                    !newValue.toLowerCase().includes(previousValue.toLowerCase().split('@')[0])) {
                    setSelectedInviteUserId(null);
                  }

                  setSuppressInviteSuggestions(false); // show suggestions again when user types
                }}
                required
              />

              {/* Suggestion message when no matches found */}
              {(() => {
                const q = (inviteEmails || '').trim();
                if (!q || q.length < 2) return null; // Don't show until user types at least 2 characters
                if (suppressInviteSuggestions) return null;

                // Check if it looks like an email
                const isEmail = q.includes('@');

                // Check if it matches any existing members (from invitee members, project members, or other projects)
                let hasMatches = false;
                const qLower = q.toLowerCase();

                // Check invitee members
                if (inviteeMembers && inviteeMembers.length > 0) {
                  hasMatches = inviteeMembers.some((m) => {
                    const name = (m.name || '').toLowerCase();
                    const email = (m.email || '').toLowerCase();
                    return name.includes(qLower) || email.includes(qLower);
                  });
                }

                // Check project members
                if (!hasMatches && selectedProject?.members && selectedProject.members.length > 0) {
                  hasMatches = selectedProject.members.some((m) => {
                    const name = (m.name || '').toLowerCase();
                    const email = (m.email || '').toLowerCase();
                    return name.includes(qLower) || email.includes(qLower);
                  });
                }

                // Check other projects
                if (!hasMatches && projects && projects.length > 0) {
                  hasMatches = projects.some(proj =>
                    proj.members && proj.members.some((m) => {
                      const name = (m.name || '').toLowerCase();
                      const email = (m.email || '').toLowerCase();
                      return name.includes(qLower) || email.includes(qLower);
                    })
                  );
                }

                // Show suggestion if it's not an email and has no matches
                if (!isEmail && !hasMatches) {
                  return (
                    <div style={{
                      marginTop: '8px',
                      fontSize: '13px',
                      color: 'var(--slate)',
                      padding: '8px 0'
                    }}>
                      No matches found - Try using their email instead
                    </div>
                  );
                }

                return null;
              })()}

              {/* Typeahead suggestions from invitee members and current project's member list */}
              {(() => {
                if (suppressInviteSuggestions) return null;
                const q = (inviteEmails || '').trim().toLowerCase();
                if (!q) return null;

                // Combine invitee members and project members for suggestions
                const allAvailableMembers = [];

                // Add invitee members (people who accepted invitations) - these should be shown
                if (inviteeMembers && inviteeMembers.length > 0) {
                  inviteeMembers.forEach(m => {
                    if (m.email && !allAvailableMembers.find(existing => existing.email?.toLowerCase() === m.email?.toLowerCase())) {
                      allAvailableMembers.push(m);
                    }
                  });
                }

                // Add project members (excluding those already in the selected project)
                if (selectedProject?.members && selectedProject.members.length > 0) {
                  selectedProject.members.forEach(m => {
                    if (m.email && !allAvailableMembers.find(existing => existing.email?.toLowerCase() === m.email?.toLowerCase())) {
                      allAvailableMembers.push(m);
                    }
                  });
                }

                // Add members from other projects
                if (projects && projects.length > 0) {
                  projects.forEach(proj => {
                    if (proj.id !== selectedProject?.id && proj.members && proj.members.length > 0) {
                      proj.members.forEach(m => {
                        if (m.email && !allAvailableMembers.find(existing => existing.email?.toLowerCase() === m.email?.toLowerCase())) {
                          allAvailableMembers.push(m);
                        }
                      });
                    }
                  });
                }

                if (allAvailableMembers.length === 0) return null;

                const matches = allAvailableMembers
                  .filter((m) => m?.email)
                  .filter((m) => {
                    const name = (m.name || '').toLowerCase();
                    const email = (m.email || '').toLowerCase();
                    return name.includes(q) || email.includes(q);
                  })
                  .slice(0, 6);

                console.log('Available members for suggestions:', allAvailableMembers.slice(0, 3).map(m => ({
                  email: m.email,
                  invite_user_id: m.invite_user_id,
                  user_id: m.user_id,
                  status: m.status
                })));

                if (matches.length === 0) return null;

                return (
                  <div style={{
                    position: 'absolute',
                    left: 0,
                    right: 0,
                    top: '100%',
                    marginTop: '6px',
                    background: 'rgba(255,255,255,0.96)',
                    border: '1px solid rgba(0,0,0,0.08)',
                    borderRadius: '12px',
                    boxShadow: '0 12px 30px rgba(0,0,0,0.12)',
                    padding: '6px',
                    zIndex: 20
                  }}>
                    {matches.map((m) => (
                      <button
                        key={m.id || m.email}
                        type="button"
                        onClick={() => {
                          console.log('Suggestion clicked:', {
                            email: m.email,
                            invite_user_id: m.invite_user_id,
                            user_id: m.user_id,
                            fullMember: m
                          });

                          // Set the email and user ID for manual add member flow
                          setInviteEmails(m.email);
                          // For invitee members, use invite_user_id as the primary identifier for adding to projects
                          // This comes from the getInviteeMembers API response
                          const memberUserId = m.invite_user_id;
                          console.log('Suggestion clicked - invite_user_id:', m.invite_user_id, 'user_id:', m.user_id, 'final:', memberUserId);
                          if (memberUserId) {
                            setSelectedInviteUserId(memberUserId);
                            console.log('Set selectedInviteUserId to:', memberUserId);
                          } else {
                            console.warn('No invite_user_id found for invitee member:', m);
                            setSelectedInviteUserId(null); // Clear it so findUserIdForEmail will search other sources
                          }
                          setSuppressInviteSuggestions(true); // hide after selection
                        }}
                        style={{
                          width: '100%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: '10px',
                          padding: '10px',
                          borderRadius: '10px',
                          border: 'none',
                          background: 'transparent',
                          cursor: 'pointer',
                          textAlign: 'left'
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(107, 158, 160, 0.10)'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                        title={m.email}
                      >
                        <span style={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0 }}>
                          <span style={{
                            width: '26px',
                            height: '26px',
                            borderRadius: '50%',
                            background: 'linear-gradient(135deg, var(--soft-teal), var(--light-teal))',
                            color: 'white',
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '11px',
                            fontWeight: 700,
                            flexShrink: 0
                          }}>
                            {m.initials || (m.name || m.email || 'M').substring(0, 2).toUpperCase()}
                          </span>
                          <span style={{ minWidth: 0, display: 'flex', flexDirection: 'column' }}>
                            <span style={{
                              fontSize: '13px',
                              fontWeight: 600,
                              color: 'var(--soft-black)',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}>
                              {m.name || m.email}
                            </span>
                            <span style={{
                              fontSize: '12px',
                              color: 'var(--slate)',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}>
                              {m.email}
                            </span>
                          </span>
                        </span>
                        <span style={{
                          fontSize: '11px',
                          padding: '4px 8px',
                          borderRadius: '999px',
                          background: (m.status || '').toLowerCase() === 'invited' ? 'rgba(0,0,0,0.06)' : 'rgba(107, 158, 160, 0.15)',
                          color: (m.status || '').toLowerCase() === 'invited' ? 'var(--slate)' : 'var(--soft-teal)',
                          fontWeight: 600,
                          flexShrink: 0
                        }}>
                          {((m.status || 'accepted') === 'invited') ? 'Invited' : 'Accepted'}
                        </span>
                      </button>
                    ))}
                  </div>
                );
              })()}
            </div>
            {/* helper text removed */}
          </Form.Group>

          <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '24px' }}>
            <CustomButton
              variant="outline"
              onClick={async () => {
                const email = inviteEmails.trim();
                if (!email) {
                  toast.error('Please enter an email address');
                  return;
                }

                // Find user_id using the current email value
                // Try selectedInviteUserId first, then fall back to finding by email
                console.log('Add Member Debug:', {
                  email,
                  selectedInviteUserId,
                  inviteEmails,
                  availableInviteeMembers: inviteeMembers?.slice(0, 3).map(m => ({
                    email: m.email,
                    invite_user_id: m.invite_user_id,
                    user_id: m.user_id,
                    status: m.status,
                    invite_accepted: m.invite_accepted,
                    raw_invite_user_id: m.raw?.invite_user_id // Show raw API field
                  }))
                });

                // First check if this person has actually accepted an invitation
                const inviteeMember = inviteeMembers?.find(m => (m.email || '').toLowerCase() === email.toLowerCase());
                if (inviteeMember && inviteeMember.status !== 'accepted') {
                  toast.error('This person has been invited but has not yet accepted the invitation. Please ask them to check their email and accept the invitation first.');
                  return;
                }

                let userId = selectedInviteUserId;
                console.log('userId:', userId);
                if (!userId) {
                  userId = findUserIdForEmail(email);
                  console.log('Using findUserIdForEmail result:', userId);
                } else {
                  console.log('Using selectedInviteUserId:', userId);
                }

                if (!userId) {
                  toast.error('Unable to add member. This user may not have completed their account setup yet. Please ask them to sign in and accept their invitation first.');
                  console.error('Failed to find user_id for email:', email, 'Available invitee members:', inviteeMembers?.slice(0, 3));
                  console.error('This might indicate the invitee has not completed account setup or the API is not returning user_id for accepted invitees');
                  return;
                }

                try {
                  setSendingInvites(true);
                  const response = await projectsAPI.addMember({
                    project_id: selectedProject.id,
                    user_id: userId
                  });

                  console.log('POST /api/projects/members response:', response);

                  if (response.status || response.id) {

                    // Refresh projects after adding member
                    try {
                      await fetchProjects();
                    } catch (projectError) {
                      console.warn('Failed to refresh projects after adding member:', projectError);
                    }

                    // Clear input and close modal
                    setInviteEmails('');
                    setSelectedInviteUserId(null);
                    setShowInviteModal(false);
                  } else {
                    const errorMessage = response.error || response.message || 'Failed to add member';
                    toast.error(errorMessage);
                  }
                } catch (error) {
                  console.error('Error adding member:', error);
                  const errorMessage = error.message || error.msg || 'Failed to add member';
                  toast.error(errorMessage);
                } finally {
                  setSendingInvites(false);
                }
              }}
              disabled={sendingInvites || !inviteEmails.trim()}
            >
              {sendingInvites ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Adding...
                </>
              ) : (
                'Add Member'
              )}
            </CustomButton>
            <CustomButton
              variant="secondary"
              onClick={() => {
                setShowInviteModal(false);
                setInviteEmails('');
                setSelectedInviteUserId(null);
              }}
            >
              Cancel
            </CustomButton>

          </div>
        </Form>
      </CustomModal>

      {/* Plan Expired / Renewal Modal */}
      <CustomModal
        show={showPlanExpiredModal}
        onHide={() => setShowPlanExpiredModal(false)}
        title="Subscription Expired"
        subtitle="Your plan has expired. Please select a plan to renew."
        size="lg"
        iconVariant="rose"
        icon={
          <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
        }
      >
        <div style={{ padding: '20px 0' }}>
          {loadingPlans ? (
            <div style={{ textAlign: 'center', padding: '20px' }}>
              <Spinner animation="border" size="sm" />
              <p style={{ marginTop: '10px', color: 'var(--slate)' }}>Loading plans...</p>
            </div>
          ) : plans.length > 0 ? (
            <div className="addons-list" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '20px' }}>
              {plans
                .filter(p => !p.type || p.type !== 'addon') // Filter out addons if they are mixed
                .map((plan) => {
                  const isCurrent = plan.id === expiredPlanId;
                  return (
                    <div key={plan.id} style={{
                      background: isCurrent ? 'var(--seashell)' : 'white',
                      border: isCurrent ? '2px solid var(--soft-teal)' : '1px solid var(--border-subtle)',
                      borderRadius: '16px',
                      padding: '24px',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      textAlign: 'center',
                      boxShadow: isCurrent ? '0 8px 24px rgba(107, 158, 160, 0.15)' : '0 4px 12px rgba(0,0,0,0.05)',
                      transition: 'transform 0.2s ease',
                      position: 'relative'
                    }}>
                      {isCurrent && (
                        <div style={{
                          position: 'absolute',
                          top: '-12px',
                          background: 'var(--soft-teal)',
                          color: 'white',
                          padding: '4px 12px',
                          borderRadius: '20px',
                          fontSize: '12px',
                          fontWeight: 'bold',
                          letterSpacing: '0.5px'
                        }}>
                          PREVIOUS PLAN
                        </div>
                      )}

                      <h5 style={{ margin: '10px 0 5px 0', fontSize: '18px', fontWeight: '700', color: 'var(--soft-black)' }}>
                        {plan.name}
                      </h5>
                      <p style={{ margin: 0, fontSize: '14px', color: 'var(--slate)', minHeight: '40px' }}>
                        {plan.description || 'Access to premium features'}
                      </p>

                      <div style={{ margin: '16px 0', fontSize: '24px', fontWeight: 'bold', color: 'var(--soft-teal)' }}>
                        ${plan.price_usd || plan.price || 0}
                        <span style={{ fontSize: '14px', fontWeight: 'normal', color: 'var(--slate)' }}>
                          / {plan.plan_duration_days === 30 ? 'mo' : plan.plan_duration_days === 365 ? 'yr' : `${plan.plan_duration_days} days`}
                        </span>
                      </div>

                      <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 20px 0', textAlign: 'left', width: '100%' }}>
                        {plan.features && Array.isArray(plan.features) ? plan.features.map((f, i) => (
                          <li key={i} style={{ fontSize: '13px', color: 'var(--slate)', marginBottom: '8px', display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--soft-teal)" strokeWidth="2" style={{ flexShrink: 0, marginTop: '2px' }}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                            </svg>
                            {f}
                          </li>
                        )) : (
                          <>
                            <li style={{ fontSize: '13px', color: 'var(--slate)', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--soft-teal)" strokeWidth="2">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                              </svg>
                              {plan.max_avatars || 1} Avatar(s)
                            </li>
                            <li style={{ fontSize: '13px', color: 'var(--slate)', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--soft-teal)" strokeWidth="2">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                              </svg>
                              {plan.max_storage_gb || 1} GB Storage
                            </li>
                          </>
                        )}
                      </ul>

                      <button
                        onClick={() => {
                          setShowPlanExpiredModal(false);
                          setShowRenewalSelectionModal(true);
                          if (avatarPlans.length === 0) fetchAvatarPlans();
                        }}
                        style={{
                          background: isCurrent ? 'var(--soft-teal)' : 'var(--slate)',
                          color: 'white',
                          border: 'none',
                          padding: '12px 32px',
                          borderRadius: '24px',
                          fontSize: '15px',
                          fontWeight: '600',
                          cursor: 'pointer',
                          width: '100%',
                          marginTop: 'auto'
                        }}
                      >
                        {isCurrent ? 'Renew Now' : 'Select Plan'}
                      </button>
                    </div>
                  );
                })}
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: '20px', color: 'var(--slate)' }}>
              No plans available at the moment.
            </div>
          )}
        </div>
      </CustomModal>

      {/* Renewal Selection Modal (Choose Your Plan) */}
      <CustomModal
        show={showRenewalSelectionModal}
        onHide={() => setShowRenewalSelectionModal(false)}
        title="Choose Your Plan"
        subtitle="Select a plan to renew your subscription"
      >
        {(() => {
          // Identify relevant avatar type from expiredPlanId or default to "for a loved one" (since manual renewal usually implies paid)
          // We look for a plan ID that matches expiredPlanId
          let selectedAvatarType = null;
          if (expiredPlanId) {
            selectedAvatarType = avatarPlans.find(type => type.plans && type.plans.some(p => p.id === expiredPlanId));
          }
          // Fallback to "for a loved one" if not found (as that is the paid one)
          if (!selectedAvatarType) {
            selectedAvatarType = avatarPlans.find(a => normalizeAvatarTypeName(a.name) === 'for loved once');
          }

          // Safe fallback if still null or no plans
          if (!selectedAvatarType) {
            // Try to find ANY type with plans just in case
            selectedAvatarType = avatarPlans.find(type => type.plans && type.plans.length > 0);
          }

          const availablePlans = selectedAvatarType?.plans || [];
          const monthlyPlan = availablePlans.find(p => Number(p.monthly_price_usd) > 0);
          const yearlyPlan = availablePlans.find(p => Number(p.yearly_price_usd) > 0);

          // Determine current selected plan based on toggle
          const currentPlan = renewalBillingPeriod === 'yearly' ? yearlyPlan : monthlyPlan;
          const planPrice = currentPlan
            ? (renewalBillingPeriod === 'yearly'
              ? `$${currentPlan.yearly_price_usd}/year`
              : `$${currentPlan.monthly_price_usd}/month`)
            : '';

          return (
            <>
              <div className="billing-toggle" style={{ display: 'flex', gap: '8px', justifyContent: 'center', marginBottom: '24px' }}>
                {monthlyPlan && (
                  <button
                    className={`billing-option ${renewalBillingPeriod === 'monthly' ? 'active' : ''}`}
                    onClick={() => setRenewalBillingPeriod('monthly')}
                  >
                    Monthly
                  </button>
                )}
                {yearlyPlan && (
                  <button
                    className={`billing-option ${renewalBillingPeriod === 'yearly' ? 'active' : ''}`}
                    onClick={() => setRenewalBillingPeriod('yearly')}
                  >
                    Yearly
                    {yearlyPlan && monthlyPlan && (
                      <span className="billing-save">Save {Math.round((1 - (yearlyPlan.yearly_price_usd / (monthlyPlan.monthly_price_usd * 12))) * 100)}%</span>
                    )}
                  </button>
                )}
              </div>

              <div className="plan-selection-single">
                {currentPlan ? (
                  <div className="plan-selection-item selected">
                    <div className="plan-selection-header">
                      <div className="plan-selection-name">{selectedAvatarType?.name || 'Avatar Plan'}</div>
                      <div className="plan-selection-price">
                        {planPrice}
                      </div>
                    </div>
                    <div className="plan-selection-details">
                      {(() => {
                        const tokenLimit = currentPlan.token_limit ?? currentPlan.tokens_limit ?? currentPlan.max_tokens ?? '450';
                        const storageLimit = currentPlan.max_storage_gb ?? currentPlan.storage_limit ?? currentPlan.storage_limit_gb ?? currentPlan.storage ?? '5';
                        const familyMembers = currentPlan.max_family_members ?? currentPlan.family_members ?? currentPlan.family_member_limit ?? currentPlan.members_limit ?? '1';
                        // Use plan_days directly from API response
                        const durationDays = currentPlan.plan_days ?? currentPlan.days ?? currentPlan.free_trial_days ?? '30';
                        return `Duration: ${durationDays} days • Tokens: ${tokenLimit} • Storage: ${storageLimit}GB • Family members: ${familyMembers}`;
                      })()}
                    </div>
                  </div>
                ) : (
                  <div style={{ textAlign: 'center', padding: '20px', color: 'var(--slate)' }}>
                    No plan available for this duration.
                  </div>
                )}
              </div>

              <div className="modal-actions">
                <CustomButton variant="secondary" onClick={() => setShowRenewalSelectionModal(false)}>
                  Cancel
                </CustomButton>
                <CustomButton
                  variant="primary"
                  onClick={() => handleRenewalPayment(currentPlan, renewalBillingPeriod)}
                  disabled={processingRenewal || !currentPlan}
                >
                  {`Continue with ${planPrice || 'selected plan'}`}
                </CustomButton>
              </div>
            </>
          );
        })()}
      </CustomModal>



    </div>
  );
};

export default AvatarChatPage;

